import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { FC, useEffect, useMemo, useRef, useState } from "react";

import {
  AsyncWorkflowType,
  AppRole,
  CANONICAL_HIERARCHY_RUN_METRIC_KEYS,
  CanonicalBootstrapModelIdValues,
  TaxonomyApprovalJobStatus,
  TaxonomyRunStatus,
  TagLayerValues,
  getCanonicalHierarchyRunMetricCount,
  getTaxonomyRunMetricCount,
  hasTaxonomyRunCompletedPageScans,
  type IAsyncWorkflowWarning,
  type IApprovedCanonicalTagDecision,
  type ICanonicalBootstrapAdminState,
  type ICanonicalBootstrapValidationBlocker,
  type ICanonicalBootstrapValidationBlockerSource,
  type ICanonicalDuplicateGroup,
  type ICanonicalDuplicateSource,
  type ICanonicalTag,
  type ICanonicalHierarchyProposal,
  type ICanonicalTagProposal,
  type ITaxonomyApprovalJob,
  type ITaxonomyCleanupJob,
  type ITaxonomySyncRun,
} from "@monorepo/core";

import {
  ADMIN_SIDEBAR_DESKTOP_WIDTH,
  default as AdminSidebar,
} from "../components/admin-sidebar.component";
import {
  DiagnosticsAccordion,
  RecoverySection,
  WarningSummary,
  WorkflowHeader,
  WorkflowStageCard,
  type AsyncWorkflowAction,
  type AsyncWorkflowStageDefinition,
  type AsyncWorkflowStageVisualState,
} from "../components/async-workflow-shell.component";
import AppHeader from "../components/app-header.component";
import useAuth from "../hooks/auth.hook";
import {
  useApprovedCanonicalBootstrapTags,
  useApprovedCanonicalBootstrapTagParentOptions,
  useAsyncWorkflowWarnings,
  useCanonicalBootstrapAdminActions,
  useCanonicalBootstrapAdminState,
  useCanonicalHierarchyDuplicateGroups,
  useCanonicalProposalDuplicateGroups,
  usePendingCanonicalBootstrapProposals,
  usePendingCanonicalHierarchyProposals,
} from "../hooks/taxonomy-admin.hook";
import { displayFontFamily } from "../theme/mtgTheme";
import {
  buildApprovedTagDraft,
  buildAwaitingApprovalReviewExportFiles,
  buildHierarchyAwaitingApprovalReviewExportFiles,
  createMarkdownZipBlob,
  createProposalDraft,
  isCanonicalProposalDraftApprovable,
  type CanonicalProposalDraft,
} from "./canonical-bootstrap-review.utils";
import { trpc } from "../utils/trpc";

const SummaryMetricCard = ({
  label,
  value,
  helper,
}: {
  label: string;
  value: string;
  helper: string;
}) => (
  <Card sx={{ borderRadius: 3, height: "100%" }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 1, fontFamily: displayFontFamily }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {helper}
      </Typography>
    </CardContent>
  </Card>
);

const ReviewResultSkeletons = ({ count = 3 }: { count?: number }) => (
  <Stack spacing={2}>
    {Array.from({ length: count }).map((_, index) => (
      <Card
        key={`review-skeleton-${index}`}
        variant="outlined"
        sx={{ borderRadius: 3 }}
      >
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Skeleton variant="text" width="42%" height={40} />
              <Skeleton variant="text" width="68%" />
              <Skeleton variant="text" width="88%" />
            </Box>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                },
              }}
            >
              <Skeleton variant="rounded" height={76} />
              <Skeleton variant="rounded" height={76} />
              <Skeleton variant="rounded" height={76} />
              <Skeleton variant="rounded" height={76} />
            </Box>
            <Skeleton variant="rounded" height={96} />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Skeleton variant="rounded" width={160} height={36} />
              <Skeleton variant="rounded" width={160} height={36} />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    ))}
  </Stack>
);

const DuplicateGroupCard = ({
  group,
  isBusy,
  isApplying,
  onApply,
  onRemoveSource,
  onFocusSource,
}: {
  group: ICanonicalDuplicateGroup;
  isBusy: boolean;
  isApplying: boolean;
  onApply: () => void;
  onRemoveSource: (source: ICanonicalDuplicateSource) => void;
  onFocusSource?: (source: ICanonicalDuplicateSource) => void;
}) => (
  <Card variant="outlined" sx={{ borderRadius: 3 }}>
    <CardContent>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontFamily: displayFontFamily }}>
              Likely Duplicate Concept
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {group.reasonSummary}
            </Typography>
          </Box>
          <Chip
            color="warning"
            variant="outlined"
            label={`${Math.round(group.recommendation.confidence * 100)}% confidence`}
          />
        </Stack>

        <Alert severity="warning">{group.recommendation.summary}</Alert>

        <Stack spacing={1.5}>
          {group.sources.map((source) => {
            const isRecommendedKeep =
              source.sourceKey === group.recommendation.keepSourceKey;

            return (
              <Box
                key={source.sourceKey}
                sx={{
                  border: "1px solid",
                  borderColor: isRecommendedKeep ? "success.main" : "divider",
                  borderRadius: 2,
                  p: 1.5,
                }}
              >
                <Stack spacing={1}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <Chip
                        size="small"
                        color={isRecommendedKeep ? "success" : "default"}
                        label={
                          isRecommendedKeep ? "Recommended Keep" : "Duplicate"
                        }
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={getDuplicateSourceTypeLabel(source)}
                      />
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      {isPendingDuplicateSource(source) ? (
                        <Button
                          size="small"
                          variant="text"
                          color="inherit"
                          onClick={() => onRemoveSource(source)}
                          disabled={isBusy}
                        >
                          Remove
                        </Button>
                      ) : null}
                      {onFocusSource ? (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => onFocusSource(source)}
                        >
                          Open Source
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>
                  <Typography variant="subtitle2">
                    {getDuplicateSourceDisplayLabel(source)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {source.tag.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {source.tag.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Support score {source.supportScore}
                    {source.sourceRawTagKeys.length > 0
                      ? ` · Raw tags: ${source.sourceRawTagKeys.join(", ")}`
                      : ""}
                    {source.childProposalIds?.length
                      ? ` · Child proposals: ${source.childProposalIds.join(", ")}`
                      : ""}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Stack>

        <Box>
          <Button
            variant="contained"
            color="warning"
            onClick={onApply}
            disabled={isBusy}
            startIcon={
              isApplying ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Approve Recommended Fix
          </Button>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

type HierarchyApprovalDraft = {
  approvedParentSlugs: string[];
  approvedSynthesizedParentParentSlugs: string[];
};

type ApprovedCanonicalTagRepairDraft = {
  layer: ICanonicalTag["layer"];
  slug: string;
  name: string;
  description: string;
  synonyms: string;
  parentSlugs: string[];
};

type SynthesizedHierarchyParentRepairDraft = {
  slug: string;
  name: string;
  description: string;
  synonyms: string;
  parentSlugs: string[];
};

type SnapshotParentFilterOption = {
  value: string;
  layer: ICanonicalTag["layer"];
  slug: string;
  name: string;
  description: string;
  childCount: number;
};

type ValidationBlockerRepairOptions = {
  guidance: string;
  canEditApprovedTag: boolean;
  canEditParents: boolean;
  canRejectApprovedProposal: boolean;
  canEditSynthesizedParent: boolean;
  canRejectApprovedHierarchy: boolean;
};

type AttemptedValidationContext =
  | {
      kind: "pending-proposal";
      proposalId: string;
      approvedTagDraft: ReturnType<typeof buildApprovedTagDraft>;
    }
  | {
      kind: "pending-hierarchy";
      hierarchyProposalId: string;
      hierarchyDraft: HierarchyApprovalDraft;
    }
  | null;

const PARENT_LINK_VALIDATION_ISSUE_CODES = new Set<
  ICanonicalBootstrapValidationBlocker["issue"]["code"]
>([
  "DUPLICATE_PARENT_SLUG",
  "MISSING_SAME_LAYER_PARENT",
  "TOO_MANY_DIRECT_PARENTS",
  "CYCLE_DETECTED",
  "MAX_DEPTH_EXCEEDED",
]);

const isDuplicateValidationIssue = (
  blocker: ICanonicalBootstrapValidationBlocker,
) => blocker.issue.code === "DUPLICATE_CANONICAL_TAG";

const getValidationBlockerSourceKey = (
  source: ICanonicalBootstrapValidationBlockerSource,
) =>
  `${source.sourceType}:${source.proposalId ?? "no-proposal"}:${source.hierarchyProposalId ?? "no-hierarchy"}:${source.tag.layer}:${source.tag.slug}`;

const sameValidationBlockerSource = (
  left: ICanonicalBootstrapValidationBlockerSource,
  right: ICanonicalBootstrapValidationBlockerSource,
) =>
  left.sourceType === right.sourceType &&
  left.proposalId === right.proposalId &&
  left.hierarchyProposalId === right.hierarchyProposalId &&
  left.tag.layer === right.tag.layer &&
  left.tag.slug === right.tag.slug;

const doesProposalDraftMatchProposal = ({
  proposal,
  draft,
}: {
  proposal: ICanonicalTagProposal;
  draft: CanonicalProposalDraft;
}) => {
  const originalDraft = createProposalDraft(proposal);

  return (
    draft.layer === originalDraft.layer &&
    draft.slug === originalDraft.slug &&
    draft.name === originalDraft.name &&
    draft.description === originalDraft.description &&
    draft.synonyms === originalDraft.synonyms
  );
};

const doesHierarchyApprovalDraftMatchProposal = ({
  proposal,
  draft,
}: {
  proposal: ICanonicalHierarchyProposal;
  draft: HierarchyApprovalDraft;
}) => {
  const originalDraft = createHierarchyApprovalDraft(proposal);

  return (
    draft.approvedParentSlugs.join("|") ===
      originalDraft.approvedParentSlugs.join("|") &&
    draft.approvedSynthesizedParentParentSlugs.join("|") ===
      originalDraft.approvedSynthesizedParentParentSlugs.join("|")
  );
};

const getDuplicateSourceDisplayLabel = (source: ICanonicalDuplicateSource) =>
  `${source.tag.layer}:${source.tag.slug}`;

const getDuplicateSourceTypeLabel = (source: ICanonicalDuplicateSource) => {
  switch (source.sourceType) {
    case "APPROVED_CANONICAL_TAG":
      return "Approved tag";
    case "PENDING_CANONICAL_PROPOSAL":
      return "Pending proposal";
    case "APPROVED_SYNTHESIZED_HIERARCHY_PARENT":
      return "Approved synthesized parent";
    case "PENDING_SYNTHESIZED_HIERARCHY_PARENT":
      return "Pending synthesized parent";
  }
};

const isPendingDuplicateSource = (source: ICanonicalDuplicateSource) =>
  source.sourceType === "PENDING_CANONICAL_PROPOSAL" ||
  source.sourceType === "PENDING_SYNTHESIZED_HIERARCHY_PARENT";

const isApprovedDuplicateSource = (source: ICanonicalDuplicateSource) =>
  source.sourceType === "APPROVED_CANONICAL_TAG" ||
  source.sourceType === "APPROVED_SYNTHESIZED_HIERARCHY_PARENT";

const buildVisibleDuplicateRecommendationSummary = ({
  analysisType,
  actionType,
  keepSource,
  rejectSources,
}: {
  analysisType: ICanonicalDuplicateGroup["analysisType"];
  actionType: ICanonicalDuplicateGroup["recommendation"]["actionType"];
  keepSource: ICanonicalDuplicateSource;
  rejectSources: readonly ICanonicalDuplicateSource[];
}) => {
  const keptLabel = getDuplicateSourceDisplayLabel(keepSource);
  const rejectedLabels = rejectSources
    .map((source) => getDuplicateSourceDisplayLabel(source))
    .join(", ");
  if (actionType === "KEEP_APPROVED_AND_REJECT_PENDING_DUPLICATES") {
    return `Keep ${keptLabel} and reject pending duplicate ${
      analysisType === "CANONICAL_PROPOSALS"
        ? "proposal(s)"
        : "recommendation(s)"
    }: ${rejectedLabels}.`;
  }

  return `Approve ${keptLabel} and reject duplicate pending ${
    analysisType === "CANONICAL_PROPOSALS" ? "proposal(s)" : "recommendation(s)"
  }: ${rejectedLabels}.`;
};

const buildVisibleDuplicateGroup = ({
  group,
  removedSourceKeys,
}: {
  group: ICanonicalDuplicateGroup;
  removedSourceKeys: ReadonlySet<string>;
}): ICanonicalDuplicateGroup | undefined => {
  const visibleSources = group.sources.filter(
    (source) => !removedSourceKeys.has(source.sourceKey),
  );
  if (visibleSources.length < 2) {
    return undefined;
  }

  const pendingSources = visibleSources.filter(isPendingDuplicateSource);
  const approvedSources = visibleSources.filter(isApprovedDuplicateSource);
  if (pendingSources.length === 0 || approvedSources.length > 1) {
    return undefined;
  }

  const keepSource =
    approvedSources.length === 1
      ? approvedSources[0]
      : pendingSources.slice().sort((left, right) => {
          const scoreDelta = right.supportScore - left.supportScore;
          if (scoreDelta !== 0) {
            return scoreDelta;
          }
          return left.sourceKey.localeCompare(right.sourceKey);
        })[0];
  if (!keepSource) {
    return undefined;
  }

  const rejectSources = pendingSources.filter(
    (source) => source.sourceKey !== keepSource.sourceKey,
  );
  if (rejectSources.length === 0) {
    return undefined;
  }

  const actionType: ICanonicalDuplicateGroup["recommendation"]["actionType"] =
    approvedSources.length === 1
      ? "KEEP_APPROVED_AND_REJECT_PENDING_DUPLICATES"
      : "APPROVE_PENDING_AND_REJECT_PENDING_DUPLICATES";

  return {
    ...group,
    sources: visibleSources,
    recommendation: {
      ...group.recommendation,
      actionType,
      keepSourceKey: keepSource.sourceKey,
      rejectSourceKeys: rejectSources.map((source) => source.sourceKey),
      summary: buildVisibleDuplicateRecommendationSummary({
        analysisType: group.analysisType,
        actionType,
        keepSource,
        rejectSources,
      }),
    },
  };
};

const getValidationBlockerRepairOptions = ({
  blocker,
  source,
}: {
  blocker: ICanonicalBootstrapValidationBlocker;
  source: ICanonicalBootstrapValidationBlockerSource;
}): ValidationBlockerRepairOptions => {
  const hasProposalSource = Boolean(source.proposalId);
  const hasHierarchySource = Boolean(source.hierarchyProposalId);
  const isParentLinkIssue = PARENT_LINK_VALIDATION_ISSUE_CODES.has(
    blocker.issue.code,
  );

  if (source.sourceType === "APPROVED_SYNTHESIZED_HIERARCHY_PARENT") {
    return {
      guidance: isDuplicateValidationIssue(blocker)
        ? "Choose which duplicate definition to keep. Keeping this synthesized parent will reject the conflicting duplicate source(s)."
        : isParentLinkIssue
          ? "This blocker comes from the synthesized parent's hierarchy links. Edit the synthesized parent or reject the approved hierarchy recommendation."
          : "This blocker comes from a synthesized parent created by hierarchy approval. Edit the synthesized parent or reject the approved hierarchy recommendation.",
      canEditApprovedTag: false,
      canEditParents: false,
      canRejectApprovedProposal: false,
      canEditSynthesizedParent: hasHierarchySource,
      canRejectApprovedHierarchy:
        hasHierarchySource && !isDuplicateValidationIssue(blocker),
    };
  }

  if (blocker.issue.code === "DUPLICATE_CANONICAL_TAG") {
    return {
      guidance:
        "Choose one duplicate definition to keep, or edit one definition so both tags can coexist without colliding.",
      canEditApprovedTag: hasProposalSource,
      canEditParents: false,
      canRejectApprovedProposal: false,
      canEditSynthesizedParent: false,
      canRejectApprovedHierarchy: false,
    };
  }

  if (isParentLinkIssue) {
    return {
      guidance: hasHierarchySource
        ? "This blocker comes from applied parent links. Edit the parents directly or reject the approved hierarchy recommendation."
        : "This blocker comes from invalid parent links on the approved tag. Edit the parent links to resolve it.",
      canEditApprovedTag: false,
      canEditParents: hasProposalSource,
      canRejectApprovedProposal: false,
      canEditSynthesizedParent: false,
      canRejectApprovedHierarchy: hasHierarchySource,
    };
  }

  return {
    guidance:
      "Repair the approved tag definition that produced this invalid canonical output.",
    canEditApprovedTag: hasProposalSource,
    canEditParents: false,
    canRejectApprovedProposal: hasProposalSource,
    canEditSynthesizedParent: false,
    canRejectApprovedHierarchy: false,
  };
};

const createHierarchyApprovalDraft = (
  proposal: ICanonicalHierarchyProposal,
): HierarchyApprovalDraft => ({
  approvedParentSlugs: proposal.recommendedParentSlugs.slice(),
  approvedSynthesizedParentParentSlugs:
    proposal.synthesizedParent?.parentSlugs.slice() ?? [],
});

const TableSectionSkeleton = ({
  rowCount = 5,
  columnCount = 5,
}: {
  rowCount?: number;
  columnCount?: number;
}) => (
  <Stack spacing={1.5}>
    {Array.from({ length: rowCount }).map((_, rowIndex) => (
      <Box
        key={`table-skeleton-${rowIndex}`}
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        }}
      >
        {Array.from({ length: columnCount }).map((__, columnIndex) => (
          <Skeleton
            key={`table-skeleton-cell-${rowIndex}-${columnIndex}`}
            variant="rounded"
            height={32}
          />
        ))}
      </Box>
    ))}
  </Stack>
);

const formatTimestamp = (value?: string) =>
  value
    ? new Date(value).toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

const buildProgressValue = (completed: number, queued: number) =>
  queued > 0 ? Math.min(100, Math.round((completed / queued) * 100)) : 0;

const hasMetricValue = (
  metrics: Record<string, number> | undefined,
  metricKey: string,
) => Object.prototype.hasOwnProperty.call(metrics ?? {}, metricKey);

const formatCleanupJobSummary = (job: ITaxonomyCleanupJob) =>
  `Cleaned ${job.deletedRunCount ?? 0} run(s), ${job.deletedCandidateCount ?? 0} source tag candidate row(s), ${job.deletedEvidenceCount ?? 0} evidence row(s), ${job.deletedHierarchyRunCount ?? 0} hierarchy run(s), ${job.deletedHierarchyProposalCount ?? 0} hierarchy recommendation(s), and ${job.deletedHierarchyDecisionCount ?? 0} hierarchy decision(s).`;

const buildCanonicalTagLookupKey = ({
  layer,
  slug,
}: Pick<ICanonicalTag, "layer" | "slug">) => `${layer}:${slug}`;

const parseCanonicalTagLookupKey = (
  value: string,
): Pick<ICanonicalTag, "layer" | "slug"> | undefined => {
  const separatorIndex = value.indexOf(":");
  if (separatorIndex <= 0 || separatorIndex >= value.length - 1) {
    return undefined;
  }

  const layer = value.slice(0, separatorIndex);
  const slug = value.slice(separatorIndex + 1);
  if (!TagLayerValues.includes(layer as ICanonicalTag["layer"])) {
    return undefined;
  }

  return {
    layer: layer as ICanonicalTag["layer"],
    slug,
  };
};

const ALL_SNAPSHOT_LAYERS = "__ALL_SNAPSHOT_LAYERS__";
const ALL_SNAPSHOT_PARENTS = "__ALL_SNAPSHOT_PARENTS__";

type BootstrapMode = "FULL" | "LIMITED";
type CanonicalBootstrapModelId =
  (typeof CanonicalBootstrapModelIdValues)[number];
type StageId =
  | "configure"
  | "scan"
  | "detail"
  | "generate-proposals"
  | "review-proposals"
  | "generate-hierarchy"
  | "review-hierarchy"
  | "publish";

const STAGE_SEQUENCE: readonly StageId[] = [
  "configure",
  "scan",
  "detail",
  "generate-proposals",
  "review-proposals",
  "generate-hierarchy",
  "review-hierarchy",
  "publish",
];

const STAGE_CARD_ID: Record<StageId, string> = {
  configure: "workflow-stage-configure",
  scan: "workflow-stage-scan",
  detail: "workflow-stage-detail",
  "generate-proposals": "workflow-stage-generate-proposals",
  "review-proposals": "workflow-stage-review-proposals",
  "generate-hierarchy": "workflow-stage-generate-hierarchy",
  "review-hierarchy": "workflow-stage-review-hierarchy",
  publish: "workflow-stage-publish",
};

const ACTIVE_WORKSPACE_STATUSES = new Set<string>([
  TaxonomyRunStatus.PENDING,
  TaxonomyRunStatus.SCANNING,
  TaxonomyRunStatus.DETAILING,
  TaxonomyRunStatus.PROPOSING,
  TaxonomyRunStatus.GENERATING_HIERARCHY,
  TaxonomyRunStatus.PUBLISHING,
]);

const DEFAULT_BOOTSTRAP_MODEL_BY_MODE: Record<
  BootstrapMode,
  CanonicalBootstrapModelId
> = {
  FULL: "gpt-5.4",
  LIMITED: "gpt-4.1-nano",
};

const BOOTSTRAP_MODEL_LABELS: Record<CanonicalBootstrapModelId, string> = {
  "gpt-4.1-nano": "GPT-4.1 Nano",
  "gpt-4o-mini": "GPT-4o Mini",
  "gpt-5.4-nano": "GPT-5.4 Nano",
  "gpt-5.4-mini": "GPT-5.4 Mini",
  "gpt-5.4": "GPT-5.4",
};

const PROPOSAL_STALLED_AFTER_MS = 30 * 60_000;
const HIERARCHY_STALLED_AFTER_MS = 30 * 60_000;
const APPROVAL_JOB_STALLED_AFTER_MS = 30 * 60_000;
const PUBLISH_STALLED_AFTER_MS = 15 * 60_000;

const getRunMetric = getTaxonomyRunMetricCount;

const isProposalKickoffPreparing = (run: ITaxonomySyncRun | undefined) =>
  run?.runStatus === TaxonomyRunStatus.PROPOSING &&
  getRunMetric(run, "proposalQueuedCount") === 0 &&
  getRunMetric(run, "proposalCompletedCount") === 0 &&
  getRunMetric(run, "proposalCreatedCount") === 0 &&
  getRunMetric(run, "proposalNoTagCount") === 0;

const isProposalGenerationStalled = (
  run: ITaxonomySyncRun | undefined,
  nowMs: number = Date.now(),
) => {
  if (run?.runStatus !== TaxonomyRunStatus.PROPOSING) {
    return false;
  }

  const queuedCount = getRunMetric(run, "proposalQueuedCount");
  const completedCount = getRunMetric(run, "proposalCompletedCount");
  if (!isProposalKickoffPreparing(run) && queuedCount <= completedCount) {
    return false;
  }

  const updatedAtMs = Date.parse(run.updatedAt);
  if (!Number.isFinite(updatedAtMs)) {
    return false;
  }

  return nowMs - updatedAtMs >= PROPOSAL_STALLED_AFTER_MS;
};

const isHierarchyGenerationStalled = ({
  run,
  hierarchyRun,
  nowMs = Date.now(),
}: {
  run: ITaxonomySyncRun | undefined;
  hierarchyRun:
    | ICanonicalBootstrapAdminState["hierarchyRuns"][number]
    | undefined;
  nowMs?: number;
}) => {
  if (
    run?.runStatus !== TaxonomyRunStatus.GENERATING_HIERARCHY ||
    hierarchyRun?.runStatus !== "GENERATING"
  ) {
    return false;
  }

  const queuedCount = getCanonicalHierarchyRunMetricCount(
    hierarchyRun.metrics,
    CANONICAL_HIERARCHY_RUN_METRIC_KEYS.batchQueuedCount,
  );
  const completedCount = getCanonicalHierarchyRunMetricCount(
    hierarchyRun.metrics,
    CANONICAL_HIERARCHY_RUN_METRIC_KEYS.batchCompletedCount,
  );
  if (queuedCount <= completedCount) {
    return false;
  }

  const updatedAtMs = Math.max(
    Date.parse(run.updatedAt),
    Date.parse(hierarchyRun.updatedAt),
  );
  if (!Number.isFinite(updatedAtMs)) {
    return false;
  }

  return nowMs - updatedAtMs >= HIERARCHY_STALLED_AFTER_MS;
};

const isApprovalJobStalled = (
  job: ITaxonomyApprovalJob | undefined,
  nowMs: number = Date.now(),
) => {
  if (
    !job ||
    (job.jobStatus !== TaxonomyApprovalJobStatus.QUEUED &&
      job.jobStatus !== TaxonomyApprovalJobStatus.RUNNING) ||
    job.completedCount >= job.queuedCount
  ) {
    return false;
  }

  const updatedAtMs = Date.parse(job.updatedAt);
  if (!Number.isFinite(updatedAtMs)) {
    return false;
  }

  return nowMs - updatedAtMs >= APPROVAL_JOB_STALLED_AFTER_MS;
};

const isPublishStalled = (
  run: ITaxonomySyncRun | undefined,
  nowMs: number = Date.now(),
) => {
  if (run?.runStatus !== TaxonomyRunStatus.PUBLISHING) {
    return false;
  }

  const updatedAtMs = Date.parse(run.updatedAt);
  if (!Number.isFinite(updatedAtMs)) {
    return false;
  }

  return nowMs - updatedAtMs >= PUBLISH_STALLED_AFTER_MS;
};

const getRunPageScanSummary = (run: ITaxonomySyncRun | undefined) =>
  `${getRunMetric(run, "pageScanCompletedCount")}/${getRunMetric(run, "pageScanQueuedCount")}`;

const getRunDetailSummary = (run: ITaxonomySyncRun | undefined) =>
  `${getRunMetric(run, "detailCompletedCount")}/${getRunMetric(run, "detailQueuedCount")}`;

const getRunProposalSummary = (run: ITaxonomySyncRun | undefined) =>
  `${getRunMetric(run, "proposalCompletedCount")}/${getRunMetric(run, "proposalQueuedCount")}`;

const getRunProposalDisplaySummary = (run: ITaxonomySyncRun | undefined) =>
  isProposalKickoffPreparing(run) ? "Preparing" : getRunProposalSummary(run);

const getRunCoordinatorSummary = (run: ITaxonomySyncRun | undefined) =>
  getRunMetric(run, "coordinatorSchedulingCompleted") > 0
    ? "Complete"
    : "Pending";

const getAdditionalRunMetrics = (run: ITaxonomySyncRun) =>
  Object.entries(run.metrics).filter(
    ([metricKey]) =>
      ![
        "coordinatorSchedulingCompleted",
        "pageScanQueuedCount",
        "pageScanCompletedCount",
        "detailQueuedCount",
        "detailCompletedCount",
        "proposalQueuedCount",
        "proposalCompletedCount",
        "proposalCreatedCount",
        "proposalNoTagCount",
        "proposalCount",
        "decisionCount",
        "currentCandidateCount",
        "currentEvidenceCount",
        "hierarchyProposalCount",
        "hierarchyDecisionCount",
      ].includes(metricKey),
  );

const CanonicalBootstrapAdminPage: FC = () => {
  const { isAdmin, setViewRole, viewRole } = useAuth();
  const trpcUtils = trpc.useUtils();
  const adminStateQuery = useCanonicalBootstrapAdminState();
  const {
    cleanupWorkspaceMutation,
    startBootstrapMutation,
    stopBootstrapMutation,
    generateCanonicalProposalsMutation,
    generateCanonicalHierarchyMutation,
    approveCanonicalProposalMutation,
    approveCanonicalProposalDuplicateFixMutation,
    approveAllCanonicalProposalDuplicateFixesMutation,
    approveRemainingCanonicalProposalsMutation,
    approveCanonicalHierarchyProposalMutation,
    approveCanonicalHierarchyDuplicateFixMutation,
    approveAllCanonicalHierarchyDuplicateFixesMutation,
    approveRemainingCanonicalHierarchyProposalsMutation,
    rejectCanonicalHierarchyProposalMutation,
    rejectCanonicalProposalMutation,
    updateCanonicalTagHierarchyMutation,
    updateApprovedSynthesizedHierarchyParentMutation,
    publishCanonicalSnapshotMutation,
    invalidate: invalidateCanonicalBootstrapAdminState,
  } = useCanonicalBootstrapAdminActions();

  const [bootstrapMode, setBootstrapMode] = useState<BootstrapMode>("FULL");
  const [pageLimit, setPageLimit] = useState("3");
  const [proposalModelId, setProposalModelId] =
    useState<CanonicalBootstrapModelId>(DEFAULT_BOOTSTRAP_MODEL_BY_MODE.FULL);
  const [captureLlmDebugArtifacts, setCaptureLlmDebugArtifacts] =
    useState(false);
  const [proposalSearch, setProposalSearch] = useState("");
  const [isProposalListLoading, setIsProposalListLoading] = useState(false);
  const [proposalReviewPageIndex, setProposalReviewPageIndex] = useState(0);
  const [proposalReviewCursors, setProposalReviewCursors] = useState<
    (string | undefined)[]
  >([undefined]);
  const [proposalReviewRowsPerPage, setProposalReviewRowsPerPage] =
    useState(10);
  const [warningPageIndex, setWarningPageIndex] = useState(0);
  const [warningCursors, setWarningCursors] = useState<(string | undefined)[]>([
    undefined,
  ]);
  const [warningRowsPerPage, setWarningRowsPerPage] = useState(10);
  const [isWarningListLoading, setIsWarningListLoading] = useState(false);
  const [proposalDrafts, setProposalDrafts] = useState<
    Record<string, CanonicalProposalDraft>
  >({});
  const [hierarchySearch, setHierarchySearch] = useState("");
  const [isHierarchyListLoading, setIsHierarchyListLoading] = useState(false);
  const [hierarchyReviewPageIndex, setHierarchyReviewPageIndex] = useState(0);
  const [hierarchyReviewCursors, setHierarchyReviewCursors] = useState<
    (string | undefined)[]
  >([undefined]);
  const [hierarchyReviewRowsPerPage, setHierarchyReviewRowsPerPage] =
    useState(10);
  const [hierarchyRejectionNotes, setHierarchyRejectionNotes] = useState<
    Record<string, string>
  >({});
  const [hierarchyApprovalDrafts, setHierarchyApprovalDrafts] = useState<
    Record<string, HierarchyApprovalDraft>
  >({});
  const [approvedTagSearch, setApprovedTagSearch] = useState("");
  const [isApprovedTagListLoading, setIsApprovedTagListLoading] =
    useState(false);
  const [appliedHierarchyLayerFilter, setAppliedHierarchyLayerFilter] =
    useState<ICanonicalTag["layer"] | typeof ALL_SNAPSHOT_LAYERS>(
      ALL_SNAPSHOT_LAYERS,
    );
  const [appliedHierarchyParentFilter, setAppliedHierarchyParentFilter] =
    useState(ALL_SNAPSHOT_PARENTS);
  const [approvedTagPageIndex, setApprovedTagPageIndex] = useState(0);
  const [approvedTagCursors, setApprovedTagCursors] = useState<
    (string | undefined)[]
  >([undefined]);
  const [approvedTagRowsPerPage, setApprovedTagRowsPerPage] = useState(10);
  const [snapshotLayerFilter, setSnapshotLayerFilter] = useState<
    ICanonicalTag["layer"] | typeof ALL_SNAPSHOT_LAYERS
  >(ALL_SNAPSHOT_LAYERS);
  const [snapshotParentFilter, setSnapshotParentFilter] =
    useState(ALL_SNAPSHOT_PARENTS);
  const [reviewActionMessage, setReviewActionMessage] = useState<
    string | undefined
  >(undefined);
  const [attemptedValidationBlockers, setAttemptedValidationBlockers] =
    useState<ICanonicalBootstrapValidationBlocker[]>([]);
  const [attemptedValidationSummary, setAttemptedValidationSummary] = useState<
    string | undefined
  >(undefined);
  const [attemptedValidationContext, setAttemptedValidationContext] =
    useState<AttemptedValidationContext>(null);
  const [cleanupCompletionMessage, setCleanupCompletionMessage] = useState<
    string | undefined
  >(undefined);
  const [expandedStageIds, setExpandedStageIds] = useState<StageId[]>([
    "configure",
  ]);
  const [warningsExpanded, setWarningsExpanded] = useState(false);
  const [diagnosticsExpanded, setDiagnosticsExpanded] = useState(false);
  const [isCleanupDialogOpen, setIsCleanupDialogOpen] = useState(false);
  const [approvedTagRepairSource, setApprovedTagRepairSource] =
    useState<ICanonicalBootstrapValidationBlockerSource | null>(null);
  const [approvedTagRepairDraft, setApprovedTagRepairDraft] =
    useState<ApprovedCanonicalTagRepairDraft | null>(null);
  const [
    approvedTagHierarchyRepairSource,
    setApprovedTagHierarchyRepairSource,
  ] = useState<ICanonicalBootstrapValidationBlockerSource | null>(null);
  const [approvedTagHierarchyRepairDraft, setApprovedTagHierarchyRepairDraft] =
    useState<string[]>([]);
  const [synthesizedParentRepairSource, setSynthesizedParentRepairSource] =
    useState<ICanonicalBootstrapValidationBlockerSource | null>(null);
  const [synthesizedParentRepairDraft, setSynthesizedParentRepairDraft] =
    useState<SynthesizedHierarchyParentRepairDraft | null>(null);
  const [isRefreshingWorkflowState, setIsRefreshingWorkflowState] =
    useState(false);
  const [isExportingProposalReview, setIsExportingProposalReview] =
    useState(false);
  const [isExportingHierarchyReview, setIsExportingHierarchyReview] =
    useState(false);
  const [
    isApprovingRemainingProposalQueue,
    setIsApprovingRemainingProposalQueue,
  ] = useState(false);
  const [
    isApprovingRemainingHierarchyQueue,
    setIsApprovingRemainingHierarchyQueue,
  ] = useState(false);
  const [activeProposalApprovalId, setActiveProposalApprovalId] = useState<
    string | null
  >(null);
  const [activeProposalRejectionId, setActiveProposalRejectionId] = useState<
    string | null
  >(null);
  const [activeHierarchyApprovalId, setActiveHierarchyApprovalId] = useState<
    string | null
  >(null);
  const [activeHierarchyRejectionId, setActiveHierarchyRejectionId] = useState<
    string | null
  >(null);
  const [activeCleanupJobId, setActiveCleanupJobId] = useState<string | null>(
    null,
  );
  const [activeProposalDuplicateGroupId, setActiveProposalDuplicateGroupId] =
    useState<string | null>(null);
  const [activeHierarchyDuplicateGroupId, setActiveHierarchyDuplicateGroupId] =
    useState<string | null>(null);
  const [
    removedProposalDuplicateSourceKeys,
    setRemovedProposalDuplicateSourceKeys,
  ] = useState<string[]>([]);
  const [
    removedHierarchyDuplicateSourceKeys,
    setRemovedHierarchyDuplicateSourceKeys,
  ] = useState<string[]>([]);
  const [activeValidationRepairActionKey, setActiveValidationRepairActionKey] =
    useState<string | null>(null);
  const previousCleanupJobRef = useRef<
    Pick<ITaxonomyCleanupJob, "jobId" | "jobStatus"> | undefined
  >(undefined);
  const staleCleanupResetRunIdRef = useRef<string | undefined>(undefined);

  const currentProposalReviewCursor =
    proposalReviewCursors[proposalReviewPageIndex];
  const currentHierarchyReviewCursor =
    hierarchyReviewCursors[hierarchyReviewPageIndex];
  const currentWarningCursor = warningCursors[warningPageIndex];
  const currentApprovedTagCursor = approvedTagCursors[approvedTagPageIndex];
  const proposalSearchQuery = proposalSearch.trim() || undefined;
  const hierarchySearchQuery = hierarchySearch.trim() || undefined;
  const approvedTagSearchQuery = approvedTagSearch.trim() || undefined;
  const approvedTagLayerFilter =
    appliedHierarchyLayerFilter === ALL_SNAPSHOT_LAYERS
      ? undefined
      : appliedHierarchyLayerFilter;
  const selectedAppliedHierarchyParentKey =
    appliedHierarchyParentFilter === ALL_SNAPSHOT_PARENTS
      ? undefined
      : parseCanonicalTagLookupKey(appliedHierarchyParentFilter);
  const approvedTagParentFilter = selectedAppliedHierarchyParentKey?.slug;
  const effectiveApprovedTagLayerFilter =
    approvedTagLayerFilter ?? selectedAppliedHierarchyParentKey?.layer;

  const pendingProposalsQuery = usePendingCanonicalBootstrapProposals({
    cursor: currentProposalReviewCursor,
    pageSize: proposalReviewRowsPerPage,
    search: proposalSearchQuery,
  });
  const proposalDuplicateGroupsQuery = useCanonicalProposalDuplicateGroups({
    runId: adminStateQuery.data?.runs?.[0]?.runId,
  });
  const pendingHierarchyProposalsQuery = usePendingCanonicalHierarchyProposals({
    cursor: currentHierarchyReviewCursor,
    pageSize: hierarchyReviewRowsPerPage,
    search: hierarchySearchQuery,
  });
  const hierarchyDuplicateGroupsQuery = useCanonicalHierarchyDuplicateGroups({
    runId: adminStateQuery.data?.runs?.[0]?.runId,
  });
  const approvedTagsQuery = useApprovedCanonicalBootstrapTags({
    cursor: currentApprovedTagCursor,
    pageSize: approvedTagRowsPerPage,
    search: approvedTagSearchQuery,
    layer: effectiveApprovedTagLayerFilter,
    parentSlug: approvedTagParentFilter,
  });
  const approvedTagParentOptionsQuery =
    useApprovedCanonicalBootstrapTagParentOptions({
      layer: approvedTagLayerFilter,
    });

  useEffect(() => {
    if (isAdmin && viewRole !== AppRole.Admin) {
      setViewRole(AppRole.Admin);
    }
  }, [isAdmin, setViewRole, viewRole]);

  useEffect(() => {
    setProposalModelId(DEFAULT_BOOTSTRAP_MODEL_BY_MODE[bootstrapMode]);
  }, [bootstrapMode]);

  const adminState = adminStateQuery.data;
  const runs = adminState?.runs ?? [];
  const hierarchyRuns = adminState?.hierarchyRuns ?? [];
  const approvalJobs = adminState?.approvalJobs ?? [];
  const cleanupJobs = adminState?.cleanupJobs ?? [];
  const validationBlockers: ICanonicalBootstrapValidationBlocker[] =
    adminState?.validationBlockers ?? [];
  const currentSnapshot = adminState?.currentSnapshot;
  const workspaceRun = runs[0];
  const latestHierarchyRun = hierarchyRuns[0];
  const latestHierarchyTargetQueuedCount = getCanonicalHierarchyRunMetricCount(
    latestHierarchyRun?.metrics,
    CANONICAL_HIERARCHY_RUN_METRIC_KEYS.targetQueuedCount,
  );
  const latestHierarchyTargetCompletedCount =
    getCanonicalHierarchyRunMetricCount(
      latestHierarchyRun?.metrics,
      CANONICAL_HIERARCHY_RUN_METRIC_KEYS.targetCompletedCount,
    );
  const latestHierarchyBatchQueuedCount = getCanonicalHierarchyRunMetricCount(
    latestHierarchyRun?.metrics,
    CANONICAL_HIERARCHY_RUN_METRIC_KEYS.batchQueuedCount,
  );
  const latestHierarchyBatchCompletedCount =
    getCanonicalHierarchyRunMetricCount(
      latestHierarchyRun?.metrics,
      CANONICAL_HIERARCHY_RUN_METRIC_KEYS.batchCompletedCount,
    );
  const latestHierarchyRecommendationCount =
    getCanonicalHierarchyRunMetricCount(
      latestHierarchyRun?.metrics,
      CANONICAL_HIERARCHY_RUN_METRIC_KEYS.recommendationCount,
    );
  const latestHierarchySynthesizedParentCount =
    getCanonicalHierarchyRunMetricCount(
      latestHierarchyRun?.metrics,
      CANONICAL_HIERARCHY_RUN_METRIC_KEYS.synthesizedParentCount,
    );
  const hasCurrentHierarchyProgressMetrics =
    latestHierarchyRun != null &&
    hasMetricValue(
      latestHierarchyRun.metrics,
      CANONICAL_HIERARCHY_RUN_METRIC_KEYS.targetQueuedCount,
    ) &&
    hasMetricValue(
      latestHierarchyRun.metrics,
      CANONICAL_HIERARCHY_RUN_METRIC_KEYS.batchQueuedCount,
    );
  const latestHierarchyTargetProgressValue = buildProgressValue(
    latestHierarchyTargetCompletedCount,
    latestHierarchyTargetQueuedCount,
  );
  const workspaceRequiresCleanup = runs.length > 0;
  const latestProposalApprovalJob = approvalJobs.find(
    (job) => job.jobType === "CANONICAL_PROPOSALS",
  );
  const latestHierarchyApprovalJob = approvalJobs.find(
    (job) => job.jobType === "CANONICAL_HIERARCHY",
  );
  const latestCleanupJob = cleanupJobs[0];
  const hasStaleCleanupStateForCurrentRun = Boolean(
    workspaceRun?.runId &&
      latestCleanupJob &&
      !latestCleanupJob.targetRunIds.includes(workspaceRun.runId),
  );
  const visibleCleanupJob = hasStaleCleanupStateForCurrentRun
    ? undefined
    : latestCleanupJob;
  const visibleCleanupMutationData =
    cleanupWorkspaceMutation.data &&
    (!workspaceRun?.runId ||
      cleanupWorkspaceMutation.data.targetRunIds.includes(workspaceRun.runId))
      ? cleanupWorkspaceMutation.data
      : undefined;

  const isApprovalJobActive = (job: ITaxonomyApprovalJob | undefined) =>
    job?.jobStatus === TaxonomyApprovalJobStatus.QUEUED ||
    job?.jobStatus === TaxonomyApprovalJobStatus.RUNNING;
  const isCleanupJobActive = (job: ITaxonomyCleanupJob | undefined) =>
    job?.jobStatus === TaxonomyApprovalJobStatus.QUEUED ||
    job?.jobStatus === TaxonomyApprovalJobStatus.RUNNING;

  const hasActiveProposalApprovalJob = isApprovalJobActive(
    latestProposalApprovalJob,
  );
  const hasActiveHierarchyApprovalJob = isApprovalJobActive(
    latestHierarchyApprovalJob,
  );
  const hasStalledProposalApprovalJob = isApprovalJobStalled(
    latestProposalApprovalJob,
  );
  const hasStalledHierarchyApprovalJob = isApprovalJobStalled(
    latestHierarchyApprovalJob,
  );
  const hasActiveApprovalJob =
    hasActiveProposalApprovalJob || hasActiveHierarchyApprovalJob;
  const hasActiveCleanupJob = isCleanupJobActive(latestCleanupJob);
  const isNewRunBlocked = workspaceRequiresCleanup || hasActiveCleanupJob;

  const warningsQuery = useAsyncWorkflowWarnings({
    workflowType: AsyncWorkflowType.TAXONOMY_SYNC_RUN,
    workflowId: workspaceRun?.runId ?? "__disabled__",
    cursor: currentWarningCursor,
    pageSize: warningRowsPerPage,
    enabled: Boolean(workspaceRun?.runId),
  });

  const pendingProposalPage = pendingProposalsQuery.data;
  const pendingProposals: ICanonicalTagProposal[] =
    pendingProposalPage?.items ?? [];
  const rawProposalDuplicateGroups: ICanonicalDuplicateGroup[] =
    proposalDuplicateGroupsQuery.data?.items ?? [];
  const hasNextPendingProposalPage = Boolean(pendingProposalPage?.nextCursor);
  const pendingHierarchyProposalPage = pendingHierarchyProposalsQuery.data;
  const pendingHierarchyProposals: ICanonicalHierarchyProposal[] =
    pendingHierarchyProposalPage?.items ?? [];
  const rawHierarchyDuplicateGroups: ICanonicalDuplicateGroup[] =
    hierarchyDuplicateGroupsQuery.data?.items ?? [];
  const hasNextPendingHierarchyProposalPage = Boolean(
    pendingHierarchyProposalPage?.nextCursor,
  );
  const approvedTagPage = approvedTagsQuery.data;
  const approvedTags: IApprovedCanonicalTagDecision[] =
    approvedTagPage?.items ?? [];
  const approvedTagParentOptions = approvedTagParentOptionsQuery.data ?? [];
  const selectedAppliedHierarchyParent = approvedTagParentOptions.find(
    (option) =>
      buildCanonicalTagLookupKey(option) === appliedHierarchyParentFilter,
  );
  const hasNextApprovedTagPage = Boolean(approvedTagPage?.nextCursor);
  const warningPage = warningsQuery.data;
  const workflowWarnings: IAsyncWorkflowWarning[] = warningPage?.items ?? [];
  const hasNextWarningPage = Boolean(warningPage?.nextCursor);
  const removedProposalDuplicateSourceKeySet = useMemo(
    () => new Set(removedProposalDuplicateSourceKeys),
    [removedProposalDuplicateSourceKeys],
  );
  const removedHierarchyDuplicateSourceKeySet = useMemo(
    () => new Set(removedHierarchyDuplicateSourceKeys),
    [removedHierarchyDuplicateSourceKeys],
  );
  const proposalDuplicateGroups = useMemo(
    () =>
      rawProposalDuplicateGroups
        .map((group) =>
          buildVisibleDuplicateGroup({
            group,
            removedSourceKeys: removedProposalDuplicateSourceKeySet,
          }),
        )
        .filter((group): group is ICanonicalDuplicateGroup => group != null),
    [rawProposalDuplicateGroups, removedProposalDuplicateSourceKeySet],
  );
  const hierarchyDuplicateGroups = useMemo(
    () =>
      rawHierarchyDuplicateGroups
        .map((group) =>
          buildVisibleDuplicateGroup({
            group,
            removedSourceKeys: removedHierarchyDuplicateSourceKeySet,
          }),
        )
        .filter((group): group is ICanonicalDuplicateGroup => group != null),
    [rawHierarchyDuplicateGroups, removedHierarchyDuplicateSourceKeySet],
  );
  const isProposalReviewContentLoading =
    isProposalListLoading && pendingProposalsQuery.isFetching;
  const isHierarchyReviewContentLoading =
    isHierarchyListLoading && pendingHierarchyProposalsQuery.isFetching;
  const isWarningContentLoading =
    isWarningListLoading && warningsQuery.isFetching;
  const isApprovedTagContentLoading =
    isApprovedTagListLoading && approvedTagsQuery.isFetching;
  const canonicalTagDescriptionByKey = useMemo(() => {
    const descriptions = new Map<string, string>();

    approvedTags.forEach((decision) => {
      descriptions.set(
        buildCanonicalTagLookupKey(decision.approvedTag),
        decision.approvedTag.description,
      );
    });

    pendingHierarchyProposals.forEach((proposal) => {
      descriptions.set(
        buildCanonicalTagLookupKey(proposal.targetTag),
        proposal.targetTag.description,
      );

      if (proposal.synthesizedParent) {
        descriptions.set(
          buildCanonicalTagLookupKey(proposal.synthesizedParent),
          proposal.synthesizedParent.description,
        );
      }
    });

    currentSnapshot?.tags.forEach((tag) => {
      const lookupKey = buildCanonicalTagLookupKey(tag);
      if (!descriptions.has(lookupKey)) {
        descriptions.set(lookupKey, tag.description);
      }
    });

    return descriptions;
  }, [approvedTags, currentSnapshot?.tags, pendingHierarchyProposals]);
  const getHierarchyParentDescription = ({
    layer,
    slug,
  }: {
    layer: ICanonicalTag["layer"];
    slug: string;
  }) =>
    canonicalTagDescriptionByKey.get(
      buildCanonicalTagLookupKey({ layer, slug }),
    ) ??
    "No description is available for this parent tag in the current page context.";
  const allSnapshotParentOptions = useMemo<SnapshotParentFilterOption[]>(() => {
    const snapshotTags = currentSnapshot?.tags ?? [];
    const childCountByParentKey = new Map<string, number>();

    snapshotTags.forEach((tag) => {
      tag.parentSlugs.forEach((parentSlug) => {
        const parentKey = buildCanonicalTagLookupKey({
          layer: tag.layer,
          slug: parentSlug,
        });
        childCountByParentKey.set(
          parentKey,
          (childCountByParentKey.get(parentKey) ?? 0) + 1,
        );
      });
    });

    return snapshotTags
      .filter((tag) =>
        childCountByParentKey.has(buildCanonicalTagLookupKey(tag)),
      )
      .map((tag) => ({
        value: buildCanonicalTagLookupKey(tag),
        layer: tag.layer,
        slug: tag.slug,
        name: tag.name,
        description: tag.description,
        childCount:
          childCountByParentKey.get(buildCanonicalTagLookupKey(tag)) ?? 0,
      }))
      .sort((left, right) => {
        const layerDelta = left.layer.localeCompare(right.layer);
        if (layerDelta !== 0) {
          return layerDelta;
        }

        return left.slug.localeCompare(right.slug);
      });
  }, [currentSnapshot?.tags]);
  const snapshotParentOptions = useMemo(
    () =>
      allSnapshotParentOptions.filter(
        (option) =>
          snapshotLayerFilter === ALL_SNAPSHOT_LAYERS ||
          option.layer === snapshotLayerFilter,
      ),
    [allSnapshotParentOptions, snapshotLayerFilter],
  );
  const selectedSnapshotParent = useMemo(
    () =>
      allSnapshotParentOptions.find(
        (option) => option.value === snapshotParentFilter,
      ),
    [allSnapshotParentOptions, snapshotParentFilter],
  );
  const filteredSnapshotTags = useMemo(() => {
    const snapshotTags = currentSnapshot?.tags ?? [];

    return snapshotTags
      .filter((tag) => {
        if (
          snapshotLayerFilter !== ALL_SNAPSHOT_LAYERS &&
          tag.layer !== snapshotLayerFilter
        ) {
          return false;
        }

        if (!selectedSnapshotParent) {
          return true;
        }

        return (
          tag.layer === selectedSnapshotParent.layer &&
          tag.parentSlugs.includes(selectedSnapshotParent.slug)
        );
      })
      .slice()
      .sort((left, right) => {
        const layerDelta = left.layer.localeCompare(right.layer);
        if (layerDelta !== 0) {
          return layerDelta;
        }

        return left.slug.localeCompare(right.slug);
      });
  }, [currentSnapshot?.tags, selectedSnapshotParent, snapshotLayerFilter]);

  const workspacePendingProposalCount = Math.max(
    0,
    getRunMetric(workspaceRun, "proposalCount") -
      getRunMetric(workspaceRun, "decisionCount"),
  );
  const workspacePendingHierarchyProposalCount = Math.max(
    0,
    getRunMetric(workspaceRun, "hierarchyProposalCount") -
      getRunMetric(workspaceRun, "hierarchyDecisionCount"),
  );
  const workspaceWarningCount = workspaceRun?.warningCount ?? 0;
  const hasPendingProposals = workspacePendingProposalCount > 0;
  const hasPendingHierarchyProposals =
    workspacePendingHierarchyProposalCount > 0;
  const isProposalDuplicateAnalysisLoading =
    Boolean(workspaceRun?.runId) &&
    proposalDuplicateGroupsQuery.isLoading &&
    proposalDuplicateGroupsQuery.data == null;
  const isHierarchyDuplicateAnalysisLoading =
    Boolean(workspaceRun?.runId) &&
    hierarchyDuplicateGroupsQuery.isLoading &&
    hierarchyDuplicateGroupsQuery.data == null;
  const hasProposalDuplicateGroups = proposalDuplicateGroups.length > 0;
  const hasHierarchyDuplicateGroups = hierarchyDuplicateGroups.length > 0;
  const hasWorkflowWarnings = workspaceWarningCount > 0;
  const hasValidationBlockers = validationBlockers.length > 0;
  const hasAttemptedValidationBlockers =
    attemptedValidationBlockers.length > 0 && !hasValidationBlockers;
  const visibleValidationBlockers = hasValidationBlockers
    ? validationBlockers
    : attemptedValidationBlockers;
  const displayedBootstrapMode: BootstrapMode = workspaceRun?.pageLimit
    ? "LIMITED"
    : "FULL";
  const displayedProposalModelId =
    workspaceRun?.proposalModelId ?? proposalModelId;
  const displayedCaptureLlmDebugArtifacts =
    workspaceRun?.captureLlmDebugArtifacts ?? captureLlmDebugArtifacts;
  const displayedPageLimit =
    workspaceRun?.pageLimit != null
      ? String(workspaceRun.pageLimit)
      : pageLimit;
  const isWorkspacePolling =
    (workspaceRun != null &&
      ACTIVE_WORKSPACE_STATUSES.has(workspaceRun.runStatus)) ||
    hasActiveApprovalJob ||
    hasActiveCleanupJob;

  const mutationErrors = Array.from(
    new Set(
      [
        cleanupWorkspaceMutation.error?.message,
        startBootstrapMutation.error?.message,
        stopBootstrapMutation.error?.message,
        generateCanonicalProposalsMutation.error?.message,
        generateCanonicalHierarchyMutation.error?.message,
        approveCanonicalProposalMutation.error?.message,
        approveCanonicalProposalDuplicateFixMutation.error?.message,
        approveAllCanonicalProposalDuplicateFixesMutation.error?.message,
        approveRemainingCanonicalProposalsMutation.error?.message,
        approveCanonicalHierarchyProposalMutation.error?.message,
        approveCanonicalHierarchyDuplicateFixMutation.error?.message,
        approveAllCanonicalHierarchyDuplicateFixesMutation.error?.message,
        approveRemainingCanonicalHierarchyProposalsMutation.error?.message,
        rejectCanonicalHierarchyProposalMutation.error?.message,
        rejectCanonicalProposalMutation.error?.message,
        publishCanonicalSnapshotMutation.error?.message,
      ].filter((message): message is string => Boolean(message)),
    ),
  );
  const isValidationRepairMutationLoading =
    activeValidationRepairActionKey != null ||
    approveCanonicalProposalMutation.isLoading ||
    rejectCanonicalProposalMutation.isLoading ||
    rejectCanonicalHierarchyProposalMutation.isLoading ||
    updateCanonicalTagHierarchyMutation.isLoading ||
    updateApprovedSynthesizedHierarchyParentMutation.isLoading;

  const canCleanupWorkspace =
    workspaceRun != null &&
    !isWorkspacePolling &&
    !hasActiveApprovalJob &&
    !hasActiveCleanupJob &&
    !cleanupWorkspaceMutation.isLoading &&
    !startBootstrapMutation.isLoading &&
    !stopBootstrapMutation.isLoading &&
    !generateCanonicalProposalsMutation.isLoading &&
    !generateCanonicalHierarchyMutation.isLoading &&
    !publishCanonicalSnapshotMutation.isLoading;
  const canStopBootstrap =
    workspaceRun != null &&
    (ACTIVE_WORKSPACE_STATUSES.has(workspaceRun.runStatus) ||
      (workspaceRun.runStatus === TaxonomyRunStatus.AWAITING_REVIEW &&
        hasActiveApprovalJob)) &&
    !cleanupWorkspaceMutation.isLoading &&
    !startBootstrapMutation.isLoading &&
    !stopBootstrapMutation.isLoading &&
    !generateCanonicalProposalsMutation.isLoading &&
    !generateCanonicalHierarchyMutation.isLoading &&
    !publishCanonicalSnapshotMutation.isLoading;
  const hasProposalGenerationStarted =
    workspaceRun?.runStatus === TaxonomyRunStatus.PROPOSING ||
    getRunMetric(workspaceRun, "proposalQueuedCount") > 0 ||
    getRunMetric(workspaceRun, "proposalCreatedCount") > 0 ||
    getRunMetric(workspaceRun, "proposalNoTagCount") > 0;
  const hasStalledProposalGeneration =
    isProposalGenerationStalled(workspaceRun);
  const hasStalledHierarchyGeneration = isHierarchyGenerationStalled({
    run: workspaceRun,
    hierarchyRun: latestHierarchyRun,
  });
  const hasStalledPublishing = isPublishStalled(workspaceRun);
  const canGenerateProposals =
    ((workspaceRun?.runStatus === TaxonomyRunStatus.AWAITING_REVIEW &&
      !hasProposalGenerationStarted) ||
      hasStalledProposalGeneration) &&
    !generateCanonicalProposalsMutation.isLoading;
  const canGenerateHierarchy =
    ((workspaceRun?.runStatus === TaxonomyRunStatus.AWAITING_REVIEW &&
      !hasPendingProposals &&
      !hasPendingHierarchyProposals &&
      !hasActiveApprovalJob &&
      !isApprovingRemainingProposalQueue &&
      !isApprovingRemainingHierarchyQueue &&
      !publishCanonicalSnapshotMutation.isLoading) ||
      hasStalledHierarchyGeneration) &&
    !generateCanonicalHierarchyMutation.isLoading;
  const invalidPendingProposalCount = pendingProposals.filter((proposal) => {
    const draft =
      proposalDrafts[proposal.proposalId] ?? createProposalDraft(proposal);
    return !isCanonicalProposalDraftApprovable(draft);
  }).length;
  const blockedPendingProposalCount = pendingProposals.filter(
    (proposal) => (proposal.approvalValidationIssues?.length ?? 0) > 0,
  ).length;
  const blockedPendingHierarchyCount = pendingHierarchyProposals.filter(
    (proposal) => (proposal.approvalValidationIssues?.length ?? 0) > 0,
  ).length;
  const proposalDuplicateGroupByProposalId = useMemo(() => {
    const nextMap = new Map<string, ICanonicalDuplicateGroup>();
    proposalDuplicateGroups.forEach((group) => {
      group.sources.forEach((source) => {
        if (source.proposalId) {
          nextMap.set(source.proposalId, group);
        }
      });
    });
    return nextMap;
  }, [proposalDuplicateGroups]);
  const hierarchyDuplicateGroupByProposalId = useMemo(() => {
    const nextMap = new Map<string, ICanonicalDuplicateGroup>();
    hierarchyDuplicateGroups.forEach((group) => {
      group.sources.forEach((source) => {
        if (source.hierarchyProposalId) {
          nextMap.set(source.hierarchyProposalId, group);
        }
      });
    });
    return nextMap;
  }, [hierarchyDuplicateGroups]);
  const currentPageProposalDuplicateCount = pendingProposals.filter(
    (proposal) => proposalDuplicateGroupByProposalId.has(proposal.proposalId),
  ).length;
  const currentPageHierarchyDuplicateCount = pendingHierarchyProposals.filter(
    (proposal) =>
      hierarchyDuplicateGroupByProposalId.has(proposal.hierarchyProposalId),
  ).length;
  const canApproveRemaining =
    hasPendingProposals &&
    !proposalDuplicateGroupsQuery.error &&
    !isProposalDuplicateAnalysisLoading &&
    !hasProposalDuplicateGroups &&
    (!hasActiveProposalApprovalJob || hasStalledProposalApprovalJob) &&
    !isApprovingRemainingProposalQueue &&
    !approveCanonicalProposalMutation.isLoading &&
    !approveRemainingCanonicalProposalsMutation.isLoading &&
    !rejectCanonicalProposalMutation.isLoading;
  const hierarchyRerunRecommended = Boolean(
    latestHierarchyRun?.shouldRunAgain &&
      latestHierarchyRun?.recommendedAction === "RUN_AGAIN",
  );
  const hierarchyRerunBlockedByPendingReview =
    hierarchyRerunRecommended && hasPendingHierarchyProposals;
  const canApproveRemainingHierarchy =
    hasPendingHierarchyProposals &&
    !hierarchyDuplicateGroupsQuery.error &&
    !isHierarchyDuplicateAnalysisLoading &&
    !hasHierarchyDuplicateGroups &&
    (!hasActiveHierarchyApprovalJob || hasStalledHierarchyApprovalJob) &&
    !isApprovingRemainingHierarchyQueue &&
    !approveCanonicalHierarchyProposalMutation.isLoading &&
    !approveRemainingCanonicalHierarchyProposalsMutation.isLoading &&
    !rejectCanonicalHierarchyProposalMutation.isLoading;
  const baseCanPublishSnapshot =
    (workspaceRun?.runStatus === TaxonomyRunStatus.AWAITING_REVIEW ||
      workspaceRun?.runStatus === TaxonomyRunStatus.PUBLISH_FAILED ||
      hasStalledPublishing) &&
    !hasActiveApprovalJob &&
    !isApprovingRemainingProposalQueue &&
    !isApprovingRemainingHierarchyQueue &&
    !publishCanonicalSnapshotMutation.isLoading &&
    !hasPendingProposals &&
    !hasPendingHierarchyProposals &&
    !hasValidationBlockers;
  const canPublishSnapshot =
    baseCanPublishSnapshot &&
    hierarchyRuns.length > 0 &&
    latestHierarchyRun?.runStatus === "COMPLETED";
  const workspaceFailureReason =
    workspaceRun?.runStatus === TaxonomyRunStatus.FAILED ||
    workspaceRun?.runStatus === TaxonomyRunStatus.PUBLISH_FAILED
      ? workspaceRun.failureReason ??
        "The canonical bootstrap workflow failed without a recorded failure reason."
      : undefined;

  useEffect(() => {
    setProposalReviewPageIndex(0);
    setProposalReviewCursors([undefined]);
  }, [proposalSearchQuery, proposalReviewRowsPerPage, workspaceRun?.runId]);

  useEffect(() => {
    if (isProposalListLoading && !pendingProposalsQuery.isFetching) {
      setIsProposalListLoading(false);
    }
  }, [isProposalListLoading, pendingProposalsQuery.isFetching]);

  useEffect(() => {
    setWarningPageIndex(0);
    setWarningCursors([undefined]);
  }, [warningRowsPerPage, workspaceRun?.runId]);

  useEffect(() => {
    if (isWarningListLoading && !warningsQuery.isFetching) {
      setIsWarningListLoading(false);
    }
  }, [isWarningListLoading, warningsQuery.isFetching]);

  useEffect(() => {
    setHierarchyReviewPageIndex(0);
    setHierarchyReviewCursors([undefined]);
  }, [hierarchyReviewRowsPerPage, hierarchySearchQuery, workspaceRun?.runId]);

  useEffect(() => {
    if (isHierarchyListLoading && !pendingHierarchyProposalsQuery.isFetching) {
      setIsHierarchyListLoading(false);
    }
  }, [isHierarchyListLoading, pendingHierarchyProposalsQuery.isFetching]);

  useEffect(() => {
    setApprovedTagPageIndex(0);
    setApprovedTagCursors([undefined]);
  }, [
    approvedTagRowsPerPage,
    approvedTagSearchQuery,
    approvedTagLayerFilter,
    approvedTagParentFilter,
    workspaceRun?.runId,
  ]);

  useEffect(() => {
    if (isApprovedTagListLoading && !approvedTagsQuery.isFetching) {
      setIsApprovedTagListLoading(false);
    }
  }, [approvedTagsQuery.isFetching, isApprovedTagListLoading]);

  useEffect(() => {
    if (
      appliedHierarchyParentFilter !== ALL_SNAPSHOT_PARENTS &&
      !approvedTagParentOptions.some(
        (option) =>
          buildCanonicalTagLookupKey(option) === appliedHierarchyParentFilter,
      )
    ) {
      setAppliedHierarchyParentFilter(ALL_SNAPSHOT_PARENTS);
    }
  }, [appliedHierarchyParentFilter, approvedTagParentOptions]);

  useEffect(() => {
    if (
      snapshotParentFilter !== ALL_SNAPSHOT_PARENTS &&
      !snapshotParentOptions.some(
        (option) => option.value === snapshotParentFilter,
      )
    ) {
      setSnapshotParentFilter(ALL_SNAPSHOT_PARENTS);
    }
  }, [snapshotParentFilter, snapshotParentOptions]);

  useEffect(() => {
    if (!isWorkspacePolling) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void adminStateQuery.refetch();
      void pendingProposalsQuery.refetch();
      void proposalDuplicateGroupsQuery.refetch();
      void pendingHierarchyProposalsQuery.refetch();
      void hierarchyDuplicateGroupsQuery.refetch();
      void warningsQuery.refetch();
      void approvedTagsQuery.refetch();
    }, 3000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    adminStateQuery,
    approvedTagsQuery,
    hierarchyDuplicateGroupsQuery,
    isWorkspacePolling,
    pendingHierarchyProposalsQuery,
    pendingProposalsQuery,
    proposalDuplicateGroupsQuery,
    warningsQuery,
    workspaceRun?.runId,
  ]);

  useEffect(() => {
    const previousCleanupJob = previousCleanupJobRef.current;
    previousCleanupJobRef.current = latestCleanupJob
      ? {
          jobId: latestCleanupJob.jobId,
          jobStatus: latestCleanupJob.jobStatus,
        }
      : undefined;

    if (
      !latestCleanupJob ||
      latestCleanupJob.jobStatus !== TaxonomyApprovalJobStatus.COMPLETED
    ) {
      if (
        activeCleanupJobId != null &&
        latestCleanupJob?.jobId === activeCleanupJobId &&
        latestCleanupJob?.jobStatus === TaxonomyApprovalJobStatus.FAILED
      ) {
        setActiveCleanupJobId(null);
      }
      return;
    }

    const cleanupJustCompletedForTrackedJob =
      activeCleanupJobId != null &&
      latestCleanupJob.jobId === activeCleanupJobId;
    const cleanupJustCompletedAfterActivePolling =
      previousCleanupJob?.jobId === latestCleanupJob.jobId &&
      (previousCleanupJob.jobStatus === TaxonomyApprovalJobStatus.QUEUED ||
        previousCleanupJob.jobStatus === TaxonomyApprovalJobStatus.RUNNING);

    if (
      !cleanupJustCompletedForTrackedJob &&
      !cleanupJustCompletedAfterActivePolling
    ) {
      return;
    }

    cleanupWorkspaceMutation.reset();
    setActiveCleanupJobId(null);
    setCleanupCompletionMessage(
      latestCleanupJob.deletedRunCount === 0
        ? "Canonical bootstrap workspace was already empty."
        : formatCleanupJobSummary(latestCleanupJob),
    );
    setBootstrapMode("FULL");
    setPageLimit("3");
    setProposalModelId(DEFAULT_BOOTSTRAP_MODEL_BY_MODE.FULL);
    setCaptureLlmDebugArtifacts(false);
    setProposalSearch("");
    setIsProposalListLoading(false);
    setProposalReviewPageIndex(0);
    setProposalReviewCursors([undefined]);
    setProposalReviewRowsPerPage(10);
    setProposalDrafts({});
    setHierarchySearch("");
    setIsHierarchyListLoading(false);
    setHierarchyReviewPageIndex(0);
    setHierarchyReviewCursors([undefined]);
    setHierarchyReviewRowsPerPage(10);
    setHierarchyRejectionNotes({});
    setHierarchyApprovalDrafts({});
    setApprovedTagSearch("");
    setIsApprovedTagListLoading(false);
    setAppliedHierarchyLayerFilter(ALL_SNAPSHOT_LAYERS);
    setAppliedHierarchyParentFilter(ALL_SNAPSHOT_PARENTS);
    setApprovedTagPageIndex(0);
    setApprovedTagCursors([undefined]);
    setApprovedTagRowsPerPage(10);
    setSnapshotLayerFilter(ALL_SNAPSHOT_LAYERS);
    setSnapshotParentFilter(ALL_SNAPSHOT_PARENTS);
    setWarningPageIndex(0);
    setWarningCursors([undefined]);
    setWarningRowsPerPage(10);
    setIsWarningListLoading(false);
    setReviewActionMessage(undefined);
    setExpandedStageIds(["configure"]);
    setWarningsExpanded(false);
    setDiagnosticsExpanded(false);
    setIsCleanupDialogOpen(false);
    setIsRefreshingWorkflowState(false);
    setIsExportingProposalReview(false);
    setIsExportingHierarchyReview(false);
    setIsApprovingRemainingProposalQueue(false);
    setIsApprovingRemainingHierarchyQueue(false);
    setActiveProposalDuplicateGroupId(null);
    setRemovedProposalDuplicateSourceKeys([]);
    setActiveHierarchyDuplicateGroupId(null);
    setRemovedHierarchyDuplicateSourceKeys([]);
    setActiveProposalApprovalId(null);
    setActiveProposalRejectionId(null);
    setActiveHierarchyApprovalId(null);
    setActiveHierarchyRejectionId(null);
    clearAttemptedValidationBlockers();
    setApprovedTagRepairSource(null);
    setApprovedTagRepairDraft(null);
    setApprovedTagHierarchyRepairSource(null);
    setApprovedTagHierarchyRepairDraft([]);
    setSynthesizedParentRepairSource(null);
    setSynthesizedParentRepairDraft(null);

    void invalidateCanonicalBootstrapAdminState();
  }, [
    activeCleanupJobId,
    cleanupWorkspaceMutation,
    invalidateCanonicalBootstrapAdminState,
    latestCleanupJob,
  ]);

  useEffect(() => {
    if (!pendingProposalPage) {
      return;
    }

    setProposalDrafts((currentDrafts) => {
      let hasChanges = false;
      const nextDrafts = { ...currentDrafts };

      pendingProposalPage.items.forEach((proposal) => {
        if (!nextDrafts[proposal.proposalId]) {
          nextDrafts[proposal.proposalId] = createProposalDraft(proposal);
          hasChanges = true;
        }
      });

      return hasChanges ? nextDrafts : currentDrafts;
    });
  }, [pendingProposalPage]);

  useEffect(() => {
    if (!pendingHierarchyProposalPage) {
      return;
    }

    setHierarchyApprovalDrafts((currentDrafts) => {
      let hasChanges = false;
      const nextDrafts = { ...currentDrafts };

      pendingHierarchyProposalPage.items.forEach((proposal) => {
        if (!nextDrafts[proposal.hierarchyProposalId]) {
          nextDrafts[proposal.hierarchyProposalId] =
            createHierarchyApprovalDraft(proposal);
          hasChanges = true;
        }
      });

      return hasChanges ? nextDrafts : currentDrafts;
    });
  }, [pendingHierarchyProposalPage]);

  useEffect(() => {
    const workspaceRunId = workspaceRun?.runId;
    if (!hasStaleCleanupStateForCurrentRun || !workspaceRunId) {
      staleCleanupResetRunIdRef.current = undefined;
      return;
    }

    if (staleCleanupResetRunIdRef.current === workspaceRunId) {
      return;
    }

    staleCleanupResetRunIdRef.current = workspaceRunId;
    cleanupWorkspaceMutation.reset();
    setCleanupCompletionMessage(undefined);
    setActiveCleanupJobId(null);
    setActiveProposalDuplicateGroupId(null);
    setRemovedProposalDuplicateSourceKeys([]);
    setActiveHierarchyDuplicateGroupId(null);
    setRemovedHierarchyDuplicateSourceKeys([]);
    setIsCleanupDialogOpen(false);
    previousCleanupJobRef.current = undefined;
  }, [
    cleanupWorkspaceMutation,
    hasStaleCleanupStateForCurrentRun,
    workspaceRun?.runId,
  ]);

  useEffect(() => {
    setProposalDrafts({});
    setIsProposalListLoading(false);
    setActiveProposalDuplicateGroupId(null);
    setRemovedProposalDuplicateSourceKeys([]);
  }, [workspaceRun?.runId]);

  useEffect(() => {
    setHierarchyRejectionNotes({});
    setHierarchyApprovalDrafts({});
    setIsHierarchyListLoading(false);
    setActiveHierarchyDuplicateGroupId(null);
    setRemovedHierarchyDuplicateSourceKeys([]);
  }, [workspaceRun?.runId]);

  useEffect(() => {
    setIsWarningListLoading(false);
    setIsApprovedTagListLoading(false);
  }, [workspaceRun?.runId]);

  const setProposalDraftField = (
    proposalId: string,
    field: keyof CanonicalProposalDraft,
    value: string,
  ) => {
    setProposalDrafts((currentDrafts) => ({
      ...currentDrafts,
      [proposalId]: {
        ...(currentDrafts[proposalId] ?? {
          layer: "FUNCTIONAL",
          slug: "",
          name: "",
          description: "",
          synonyms: "",
          rejectionNotes: "",
        }),
        [field]: value,
      },
    }));
  };

  const setHierarchyRejectionNote = (
    hierarchyProposalId: string,
    value: string,
  ) => {
    setHierarchyRejectionNotes((currentNotes) => ({
      ...currentNotes,
      [hierarchyProposalId]: value,
    }));
  };

  const setHierarchyApprovalDraft = (
    hierarchyProposalId: string,
    nextDraft: HierarchyApprovalDraft,
  ) => {
    setHierarchyApprovalDrafts((currentDrafts) => ({
      ...currentDrafts,
      [hierarchyProposalId]: nextDraft,
    }));
  };

  const latestMutationMessage = visibleCleanupMutationData
    ? visibleCleanupMutationData.jobStatus ===
      TaxonomyApprovalJobStatus.COMPLETED
      ? visibleCleanupMutationData.deletedRunCount === 0
        ? "Canonical bootstrap workspace was already empty."
        : formatCleanupJobSummary(visibleCleanupMutationData)
      : `Queued cleanup job ${visibleCleanupMutationData.jobId} for ${visibleCleanupMutationData.targetRunIds.length} run(s).`
    : startBootstrapMutation.data
      ? `Started canonical bootstrap run ${startBootstrapMutation.data.runId}.`
      : stopBootstrapMutation.data
        ? `Stopped canonical bootstrap run ${stopBootstrapMutation.data.runId}.`
        : generateCanonicalProposalsMutation.data
          ? `Started or resumed proposal generation for run ${generateCanonicalProposalsMutation.data.runId}.`
          : generateCanonicalHierarchyMutation.data
            ? `Started hierarchy generation ${generateCanonicalHierarchyMutation.data.hierarchyRunId}.`
            : approveRemainingCanonicalProposalsMutation.data
              ? `Queued proposal approval job ${approveRemainingCanonicalProposalsMutation.data.jobId} for ${approveRemainingCanonicalProposalsMutation.data.queuedCount} proposal(s).`
              : approveRemainingCanonicalHierarchyProposalsMutation.data
                ? `Queued hierarchy approval job ${approveRemainingCanonicalHierarchyProposalsMutation.data.jobId} for ${approveRemainingCanonicalHierarchyProposalsMutation.data.queuedCount} recommendation(s).`
                : publishCanonicalSnapshotMutation.data
                  ? `Started canonical snapshot publish for run ${publishCanonicalSnapshotMutation.data.runId}.`
                  : undefined;
  const cleanupStatusBanner =
    visibleCleanupJob?.jobStatus === TaxonomyApprovalJobStatus.QUEUED
      ? {
          severity: "info" as const,
          message: `Cleanup job ${visibleCleanupJob.jobId} is queued for ${visibleCleanupJob.targetRunIds.length} run(s).`,
        }
      : visibleCleanupJob?.jobStatus === TaxonomyApprovalJobStatus.RUNNING
        ? {
            severity: "info" as const,
            message: `Cleanup job ${visibleCleanupJob.jobId} is running for ${visibleCleanupJob.targetRunIds.length} run(s).`,
          }
        : visibleCleanupJob?.jobStatus === TaxonomyApprovalJobStatus.FAILED
          ? {
              severity: "error" as const,
              message:
                visibleCleanupJob.failureReason ??
                `Cleanup job ${visibleCleanupJob.jobId} failed.`,
            }
          : undefined;

  const feedbackBanner = latestMutationMessage
    ? { severity: "success" as const, message: latestMutationMessage }
    : cleanupStatusBanner
      ? cleanupStatusBanner
      : cleanupCompletionMessage
        ? { severity: "success" as const, message: cleanupCompletionMessage }
        : reviewActionMessage
          ? {
              severity: reviewActionMessage.includes("missing required")
                ? ("warning" as const)
                : reviewActionMessage.includes("validation")
                  ? ("warning" as const)
                  : reviewActionMessage.startsWith("Exported")
                    ? ("success" as const)
                    : ("info" as const),
              message: reviewActionMessage,
            }
          : undefined;

  const topWarningCodes = Array.from(
    new Set(workflowWarnings.map((warning) => warning.code)),
  ).slice(0, 3);
  const detailWarningCount = workflowWarnings.filter(
    (warning) => warning.stage === "DETAILING",
  ).length;

  const focusElement = (
    targetId: string,
    options?: {
      stageId?: StageId;
      openWarnings?: boolean;
      openDiagnostics?: boolean;
    },
  ) => {
    if (options?.stageId) {
      setExpandedStageIds((currentIds) =>
        currentIds.includes(options.stageId as StageId)
          ? currentIds
          : [...currentIds, options.stageId as StageId],
      );
    }
    if (options?.openWarnings) {
      setWarningsExpanded(true);
    }
    if (options?.openDiagnostics) {
      setDiagnosticsExpanded(true);
    }
    window.setTimeout(() => {
      document.getElementById(targetId)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);
  };

  const toggleStage = (stageId: StageId) => {
    setExpandedStageIds((currentIds) =>
      currentIds.includes(stageId)
        ? currentIds.filter((id) => id !== stageId)
        : [...currentIds, stageId],
    );
  };

  const clearAttemptedValidationBlockers = () => {
    setAttemptedValidationBlockers([]);
    setAttemptedValidationSummary(undefined);
    setAttemptedValidationContext(null);
  };

  const showAttemptedValidationBlockers = ({
    blockers,
    summary,
    stageId,
    context,
  }: {
    blockers: readonly ICanonicalBootstrapValidationBlocker[];
    summary: string;
    stageId: StageId;
    context?: AttemptedValidationContext;
  }) => {
    setAttemptedValidationBlockers(blockers.slice());
    setAttemptedValidationSummary(summary);
    setAttemptedValidationContext(context ?? null);
    focusElement("validation-blockers-anchor", {
      stageId,
    });
  };

  const focusProposalCard = (proposalId: string) => {
    focusElement(`proposal-card-${proposalId}`, {
      stageId: "review-proposals",
    });
  };

  const focusHierarchyProposalCard = (hierarchyProposalId: string) => {
    focusElement(`hierarchy-proposal-card-${hierarchyProposalId}`, {
      stageId: "review-hierarchy",
    });
  };

  const focusProposalDuplicateSource = (source: ICanonicalDuplicateSource) => {
    if (
      source.sourceType === "PENDING_CANONICAL_PROPOSAL" &&
      source.proposalId
    ) {
      focusProposalCard(source.proposalId);
      return;
    }

    focusElement("proposal-duplicate-groups-anchor", {
      stageId: "review-proposals",
    });
  };

  const focusHierarchyDuplicateSource = (source: ICanonicalDuplicateSource) => {
    if (
      source.sourceType === "PENDING_SYNTHESIZED_HIERARCHY_PARENT" &&
      source.hierarchyProposalId
    ) {
      focusHierarchyProposalCard(source.hierarchyProposalId);
      return;
    }

    focusElement("hierarchy-duplicate-groups-anchor", {
      stageId: "review-hierarchy",
    });
  };

  const fetchAllPendingProposals = async () => {
    const proposals: ICanonicalTagProposal[] = [];
    let cursor: string | undefined;

    do {
      const page =
        await trpcUtils.taxonomy.listPendingCanonicalBootstrapProposals.fetch({
          cursor,
          pageSize: 100,
        });
      proposals.push(...page.items);
      cursor = page.nextCursor;
    } while (cursor);

    return proposals;
  };

  const fetchAllPendingHierarchyProposals = async () => {
    const proposals: ICanonicalHierarchyProposal[] = [];
    let cursor: string | undefined;

    do {
      const page =
        await trpcUtils.taxonomy.listPendingCanonicalHierarchyProposals.fetch({
          cursor,
          pageSize: 100,
        });
      proposals.push(...page.items);
      cursor = page.nextCursor;
    } while (cursor);

    return proposals;
  };

  const refreshWorkflowState = async () => {
    setIsRefreshingWorkflowState(true);
    try {
      clearAttemptedValidationBlockers();
      await Promise.all([
        adminStateQuery.refetch(),
        pendingProposalsQuery.refetch(),
        proposalDuplicateGroupsQuery.refetch(),
        pendingHierarchyProposalsQuery.refetch(),
        hierarchyDuplicateGroupsQuery.refetch(),
        approvedTagsQuery.refetch(),
        workspaceRun?.runId ? warningsQuery.refetch() : Promise.resolve(),
      ]);
    } finally {
      setIsRefreshingWorkflowState(false);
    }
  };

  const startBootstrap = async () => {
    setCleanupCompletionMessage(undefined);
    const parsedPageLimit = Number(pageLimit);
    await startBootstrapMutation.mutateAsync({
      pageLimit:
        bootstrapMode === "LIMITED" &&
        Number.isFinite(parsedPageLimit) &&
        parsedPageLimit > 0
          ? parsedPageLimit
          : undefined,
      proposalModelId,
      captureLlmDebugArtifacts,
    });
  };

  const stopBootstrap = async () => {
    if (!workspaceRun) {
      return;
    }

    await stopBootstrapMutation.mutateAsync({
      runId: workspaceRun.runId,
    });
  };

  const generateCanonicalProposals = async () => {
    setCleanupCompletionMessage(undefined);
    if (!workspaceRun) {
      return;
    }

    await generateCanonicalProposalsMutation.mutateAsync({
      runId: workspaceRun.runId,
    });
  };

  const generateCanonicalHierarchy = async () => {
    setCleanupCompletionMessage(undefined);
    if (!workspaceRun) {
      return;
    }

    await generateCanonicalHierarchyMutation.mutateAsync({
      runId: workspaceRun.runId,
    });
  };

  const publishCanonicalSnapshot = async () => {
    setCleanupCompletionMessage(undefined);
    if (!workspaceRun) {
      return;
    }

    await publishCanonicalSnapshotMutation.mutateAsync({
      runId: workspaceRun.runId,
    });
  };

  const downloadAwaitingApprovalReview = async () => {
    setIsExportingProposalReview(true);
    try {
      const exportData =
        await trpcUtils.taxonomy.getCanonicalBootstrapAwaitingApprovalExport.fetch();
      const exportFiles = buildAwaitingApprovalReviewExportFiles({
        runId: exportData.runId ?? workspaceRun?.runId,
        items: exportData.items,
        drafts: proposalDrafts,
      });
      const isZipExport = exportFiles.length > 1;
      const blob = isZipExport
        ? createMarkdownZipBlob(exportFiles)
        : new Blob([exportFiles[0]?.content ?? ""], {
            type: "text/markdown;charset=utf-8",
          });
      const objectUrl = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = objectUrl;
      downloadLink.download = isZipExport
        ? `canonical-bootstrap-awaiting-approvals-${workspaceRun?.runId ?? "workspace"}.zip`
        : exportFiles[0]?.fileName ??
          `canonical-bootstrap-awaiting-approvals-${workspaceRun?.runId ?? "workspace"}.md`;
      document.body.append(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.URL.revokeObjectURL(objectUrl);
      setReviewActionMessage(
        isZipExport
          ? `Exported ${exportData.items.length} awaiting-approval proposal(s) as a zip of markdown parts.`
          : `Exported ${exportData.items.length} awaiting-approval proposal(s) to markdown.`,
      );
    } finally {
      setIsExportingProposalReview(false);
    }
  };

  const downloadHierarchyAwaitingApprovalReview = async () => {
    setIsExportingHierarchyReview(true);
    try {
      const exportData =
        await trpcUtils.taxonomy.getCanonicalHierarchyAwaitingApprovalExport.fetch();
      const exportFiles = buildHierarchyAwaitingApprovalReviewExportFiles({
        runId: exportData.runId ?? workspaceRun?.runId,
        hierarchyRunId: exportData.hierarchyRunId,
        items: exportData.items,
      });
      const isZipExport = exportFiles.length > 1;
      const blob = isZipExport
        ? createMarkdownZipBlob(exportFiles)
        : new Blob([exportFiles[0]?.content ?? ""], {
            type: "text/markdown;charset=utf-8",
          });
      const objectUrl = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = objectUrl;
      downloadLink.download = isZipExport
        ? `canonical-hierarchy-awaiting-approvals-${workspaceRun?.runId ?? "workspace"}.zip`
        : exportFiles[0]?.fileName ??
          `canonical-hierarchy-awaiting-approvals-${workspaceRun?.runId ?? "workspace"}.md`;
      document.body.append(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.URL.revokeObjectURL(objectUrl);
      setReviewActionMessage(
        isZipExport
          ? `Exported ${exportData.items.length} awaiting-approval hierarchy recommendation(s) as a zip of markdown parts.`
          : `Exported ${exportData.items.length} awaiting-approval hierarchy recommendation(s) to markdown.`,
      );
    } finally {
      setIsExportingHierarchyReview(false);
    }
  };

  const approveRemainingProposals = async () => {
    if (!canApproveRemaining || !workspaceRun) {
      return;
    }

    setIsApprovingRemainingProposalQueue(true);
    try {
      const pendingWorkspaceProposals = await fetchAllPendingProposals();
      const invalidProposalCount = pendingWorkspaceProposals.filter(
        (proposal) => {
          const draft =
            proposalDrafts[proposal.proposalId] ??
            createProposalDraft(proposal);
          return !isCanonicalProposalDraftApprovable(draft);
        },
      ).length;
      if (invalidProposalCount > 0) {
        setReviewActionMessage(
          `${invalidProposalCount} pending proposal(s) are missing required approval fields. Fix them before using Approve Remaining.`,
        );
        return;
      }

      const proposals = pendingWorkspaceProposals.map((proposal) => ({
        proposalId: proposal.proposalId,
        approvedTagDraft: buildApprovedTagDraft({
          proposal,
          draft:
            proposalDrafts[proposal.proposalId] ??
            createProposalDraft(proposal),
        }),
      }));

      const result =
        await approveRemainingCanonicalProposalsMutation.mutateAsync({
          runId: workspaceRun.runId,
          proposals,
        });
      if (result.queuedCount === 0) {
        setReviewActionMessage("No pending proposals remained to approve.");
      }
    } finally {
      setIsApprovingRemainingProposalQueue(false);
    }
  };

  const approveRemainingHierarchyProposals = async () => {
    if (
      workspacePendingHierarchyProposalCount <= 0 ||
      !workspaceRun ||
      (hasActiveHierarchyApprovalJob && !hasStalledHierarchyApprovalJob)
    ) {
      return;
    }

    setIsApprovingRemainingHierarchyQueue(true);
    try {
      const pendingWorkspaceHierarchyProposals =
        await fetchAllPendingHierarchyProposals();
      const result =
        await approveRemainingCanonicalHierarchyProposalsMutation.mutateAsync({
          runId: workspaceRun.runId,
          hierarchyProposalIds: pendingWorkspaceHierarchyProposals.map(
            (proposal) => proposal.hierarchyProposalId,
          ),
        });
      if (result.queuedCount === 0) {
        setReviewActionMessage(
          "No pending hierarchy recommendations remained to approve.",
        );
      }
    } finally {
      setIsApprovingRemainingHierarchyQueue(false);
    }
  };

  const removeProposalDuplicateSourceFromBlockerFlow = (
    source: ICanonicalDuplicateSource,
  ) => {
    if (!isPendingDuplicateSource(source)) {
      return;
    }
    setRemovedProposalDuplicateSourceKeys((currentSourceKeys) =>
      currentSourceKeys.includes(source.sourceKey)
        ? currentSourceKeys
        : [...currentSourceKeys, source.sourceKey],
    );
    setReviewActionMessage(
      `${getDuplicateSourceDisplayLabel(source)} was removed from duplicate blockers and returned to normal proposal review.`,
    );
  };

  const removeHierarchyDuplicateSourceFromBlockerFlow = (
    source: ICanonicalDuplicateSource,
  ) => {
    if (!isPendingDuplicateSource(source)) {
      return;
    }
    setRemovedHierarchyDuplicateSourceKeys((currentSourceKeys) =>
      currentSourceKeys.includes(source.sourceKey)
        ? currentSourceKeys
        : [...currentSourceKeys, source.sourceKey],
    );
    setReviewActionMessage(
      `${getDuplicateSourceDisplayLabel(source)} was removed from duplicate blockers and returned to normal hierarchy review.`,
    );
  };

  const approveProposalDuplicateFix = async (
    group: ICanonicalDuplicateGroup,
  ) => {
    if (!workspaceRun) {
      return;
    }

    setActiveProposalDuplicateGroupId(group.groupId);
    try {
      clearAttemptedValidationBlockers();
      const pendingWorkspaceProposals = await fetchAllPendingProposals();
      const pendingProposalById = new Map(
        pendingWorkspaceProposals.map((proposal) => [
          proposal.proposalId,
          proposal,
        ]),
      );
      let approvedProposalCount = 0;
      let rejectedProposalCount = 0;
      const keepSource = group.sources.find(
        (source) => source.sourceKey === group.recommendation.keepSourceKey,
      );

      if (
        group.recommendation.actionType ===
        "APPROVE_PENDING_AND_REJECT_PENDING_DUPLICATES"
      ) {
        const keepProposal = keepSource?.proposalId
          ? pendingProposalById.get(keepSource.proposalId)
          : undefined;
        if (!keepProposal) {
          throw new Error(
            `Proposal duplicate group "${group.groupId}" could not find the recommended surviving proposal.`,
          );
        }

        const approved = await approveCanonicalProposal({
          proposalId: keepProposal.proposalId,
          approvedTagDraft: buildApprovedTagDraft({
            proposal: keepProposal,
            draft:
              proposalDrafts[keepProposal.proposalId] ??
              createProposalDraft(keepProposal),
          }),
        });
        if (!approved) {
          return;
        }
        approvedProposalCount = 1;
      }

      for (const rejectSourceKey of group.recommendation.rejectSourceKeys) {
        const rejectSource = group.sources.find(
          (source) => source.sourceKey === rejectSourceKey,
        );
        if (!rejectSource?.proposalId) {
          continue;
        }

        await rejectCanonicalProposal({
          proposalId: rejectSource.proposalId,
          notes: `Rejected while applying duplicate recommendation "${group.groupId}".`,
        });
        rejectedProposalCount += 1;
      }

      setReviewActionMessage(
        `Applied 1 proposal duplicate fix group. Approved ${approvedProposalCount} proposal(s) and rejected ${rejectedProposalCount} duplicate proposal(s).`,
      );
    } finally {
      setActiveProposalDuplicateGroupId(null);
    }
  };

  const approveAllProposalDuplicateFixes = async () => {
    if (!workspaceRun || !hasProposalDuplicateGroups) {
      return;
    }

    setActiveProposalDuplicateGroupId("__all__");
    try {
      clearAttemptedValidationBlockers();
      const pendingWorkspaceProposals = await fetchAllPendingProposals();
      const pendingProposalById = new Map(
        pendingWorkspaceProposals.map((proposal) => [
          proposal.proposalId,
          proposal,
        ]),
      );
      let appliedGroupCount = 0;
      let approvedProposalCount = 0;
      let rejectedProposalCount = 0;

      for (const group of proposalDuplicateGroups) {
        const keepSource = group.sources.find(
          (source) => source.sourceKey === group.recommendation.keepSourceKey,
        );
        if (
          group.recommendation.actionType ===
          "APPROVE_PENDING_AND_REJECT_PENDING_DUPLICATES"
        ) {
          const keepProposal = keepSource?.proposalId
            ? pendingProposalById.get(keepSource.proposalId)
            : undefined;
          if (!keepProposal) {
            throw new Error(
              `Proposal duplicate group "${group.groupId}" could not find the recommended surviving proposal.`,
            );
          }

          const approved = await approveCanonicalProposal({
            proposalId: keepProposal.proposalId,
            approvedTagDraft: buildApprovedTagDraft({
              proposal: keepProposal,
              draft:
                proposalDrafts[keepProposal.proposalId] ??
                createProposalDraft(keepProposal),
            }),
          });
          if (!approved) {
            return;
          }
          approvedProposalCount += 1;
        }

        for (const rejectSourceKey of group.recommendation.rejectSourceKeys) {
          const rejectSource = group.sources.find(
            (source) => source.sourceKey === rejectSourceKey,
          );
          if (!rejectSource?.proposalId) {
            continue;
          }

          await rejectCanonicalProposal({
            proposalId: rejectSource.proposalId,
            notes: `Rejected while applying duplicate recommendation "${group.groupId}".`,
          });
          rejectedProposalCount += 1;
        }

        appliedGroupCount += 1;
      }

      setReviewActionMessage(
        `Applied ${appliedGroupCount} proposal duplicate fix group(s). Approved ${approvedProposalCount} proposal(s) and rejected ${rejectedProposalCount} duplicate proposal(s).`,
      );
    } finally {
      setActiveProposalDuplicateGroupId(null);
    }
  };

  const approveHierarchyDuplicateFix = async (
    group: ICanonicalDuplicateGroup,
  ) => {
    if (!workspaceRun) {
      return;
    }

    setActiveHierarchyDuplicateGroupId(group.groupId);
    try {
      clearAttemptedValidationBlockers();
      const pendingWorkspaceHierarchyProposals =
        await fetchAllPendingHierarchyProposals();
      const pendingHierarchyProposalById = new Map(
        pendingWorkspaceHierarchyProposals.map((proposal) => [
          proposal.hierarchyProposalId,
          proposal,
        ]),
      );
      let approvedHierarchyCount = 0;
      let rejectedHierarchyCount = 0;
      const keepSource = group.sources.find(
        (source) => source.sourceKey === group.recommendation.keepSourceKey,
      );

      if (
        group.recommendation.actionType ===
        "APPROVE_PENDING_AND_REJECT_PENDING_DUPLICATES"
      ) {
        const keepProposal = keepSource?.hierarchyProposalId
          ? pendingHierarchyProposalById.get(keepSource.hierarchyProposalId)
          : undefined;
        if (!keepProposal) {
          throw new Error(
            `Hierarchy duplicate group "${group.groupId}" could not find the recommended surviving recommendation.`,
          );
        }

        const approved = await approveCanonicalHierarchyProposal({
          hierarchyProposalId: keepProposal.hierarchyProposalId,
          approvedParentSlugs:
            hierarchyApprovalDrafts[keepProposal.hierarchyProposalId]
              ?.approvedParentSlugs ??
            createHierarchyApprovalDraft(keepProposal).approvedParentSlugs,
          approvedSynthesizedParentParentSlugs:
            hierarchyApprovalDrafts[keepProposal.hierarchyProposalId]
              ?.approvedSynthesizedParentParentSlugs ??
            createHierarchyApprovalDraft(keepProposal)
              .approvedSynthesizedParentParentSlugs,
        });
        if (!approved) {
          return;
        }
        approvedHierarchyCount = 1;
      }

      for (const rejectSourceKey of group.recommendation.rejectSourceKeys) {
        const rejectSource = group.sources.find(
          (source) => source.sourceKey === rejectSourceKey,
        );
        if (!rejectSource?.hierarchyProposalId) {
          continue;
        }

        await rejectCanonicalHierarchyProposal({
          hierarchyProposalId: rejectSource.hierarchyProposalId,
          notes: `Rejected while applying duplicate recommendation "${group.groupId}".`,
        });
        rejectedHierarchyCount += 1;
      }

      setReviewActionMessage(
        `Applied 1 hierarchy duplicate fix group. Approved ${approvedHierarchyCount} recommendation(s) and rejected ${rejectedHierarchyCount} duplicate recommendation(s).`,
      );
    } finally {
      setActiveHierarchyDuplicateGroupId(null);
    }
  };

  const approveAllHierarchyDuplicateFixes = async () => {
    if (!workspaceRun || !hasHierarchyDuplicateGroups) {
      return;
    }

    setActiveHierarchyDuplicateGroupId("__all__");
    try {
      clearAttemptedValidationBlockers();
      const pendingWorkspaceHierarchyProposals =
        await fetchAllPendingHierarchyProposals();
      const pendingHierarchyProposalById = new Map(
        pendingWorkspaceHierarchyProposals.map((proposal) => [
          proposal.hierarchyProposalId,
          proposal,
        ]),
      );
      let appliedGroupCount = 0;
      let approvedHierarchyCount = 0;
      let rejectedHierarchyCount = 0;

      for (const group of hierarchyDuplicateGroups) {
        const keepSource = group.sources.find(
          (source) => source.sourceKey === group.recommendation.keepSourceKey,
        );
        if (
          group.recommendation.actionType ===
          "APPROVE_PENDING_AND_REJECT_PENDING_DUPLICATES"
        ) {
          const keepProposal = keepSource?.hierarchyProposalId
            ? pendingHierarchyProposalById.get(keepSource.hierarchyProposalId)
            : undefined;
          if (!keepProposal) {
            throw new Error(
              `Hierarchy duplicate group "${group.groupId}" could not find the recommended surviving recommendation.`,
            );
          }

          const approved = await approveCanonicalHierarchyProposal({
            hierarchyProposalId: keepProposal.hierarchyProposalId,
            approvedParentSlugs:
              hierarchyApprovalDrafts[keepProposal.hierarchyProposalId]
                ?.approvedParentSlugs ??
              createHierarchyApprovalDraft(keepProposal).approvedParentSlugs,
            approvedSynthesizedParentParentSlugs:
              hierarchyApprovalDrafts[keepProposal.hierarchyProposalId]
                ?.approvedSynthesizedParentParentSlugs ??
              createHierarchyApprovalDraft(keepProposal)
                .approvedSynthesizedParentParentSlugs,
          });
          if (!approved) {
            return;
          }
          approvedHierarchyCount += 1;
        }

        for (const rejectSourceKey of group.recommendation.rejectSourceKeys) {
          const rejectSource = group.sources.find(
            (source) => source.sourceKey === rejectSourceKey,
          );
          if (!rejectSource?.hierarchyProposalId) {
            continue;
          }

          await rejectCanonicalHierarchyProposal({
            hierarchyProposalId: rejectSource.hierarchyProposalId,
            notes: `Rejected while applying duplicate recommendation "${group.groupId}".`,
          });
          rejectedHierarchyCount += 1;
        }

        appliedGroupCount += 1;
      }

      setReviewActionMessage(
        `Applied ${appliedGroupCount} hierarchy duplicate fix group(s). Approved ${approvedHierarchyCount} recommendation(s) and rejected ${rejectedHierarchyCount} duplicate recommendation(s).`,
      );
    } finally {
      setActiveHierarchyDuplicateGroupId(null);
    }
  };

  const failureStageId: StageId = (() => {
    if (!workspaceRun) {
      return "configure";
    }
    if (latestHierarchyRun?.runStatus === "FAILED") {
      return "generate-hierarchy";
    }
    if (getRunMetric(workspaceRun, "proposalQueuedCount") > 0) {
      return "generate-proposals";
    }
    if (getRunMetric(workspaceRun, "detailQueuedCount") > 0) {
      return "detail";
    }
    if (getRunMetric(workspaceRun, "pageScanQueuedCount") > 0) {
      return "scan";
    }
    return "configure";
  })();
  const detailFailureMessage =
    workspaceRun?.runStatus === TaxonomyRunStatus.FAILED &&
    failureStageId === "detail"
      ? workspaceFailureReason ??
        "Build Evidence failed without a recorded error message."
      : undefined;
  const hasCompletedPageScans = hasTaxonomyRunCompletedPageScans(workspaceRun);

  const approveCanonicalProposal = async ({
    proposalId,
    approvedTagDraft,
  }: {
    proposalId: string;
    approvedTagDraft: ReturnType<typeof buildApprovedTagDraft>;
  }) => {
    setActiveProposalApprovalId(proposalId);
    try {
      const result = await approveCanonicalProposalMutation.mutateAsync({
        proposalId,
        approvedTagDraft,
      });
      if (result.outcome === "BLOCKED_VALIDATION") {
        const summary =
          result.validationIssues[0]?.message ??
          "Approval was blocked by canonical snapshot validation.";
        setReviewActionMessage(summary);
        showAttemptedValidationBlockers({
          blockers: result.validationBlockers,
          summary,
          stageId: "review-proposals",
          context: {
            kind: "pending-proposal",
            proposalId,
            approvedTagDraft,
          },
        });
        return false;
      }
      clearAttemptedValidationBlockers();
      return true;
    } finally {
      setActiveProposalApprovalId((currentId) =>
        currentId === proposalId ? null : currentId,
      );
    }
  };

  const rejectCanonicalProposal = async ({
    proposalId,
    notes,
  }: {
    proposalId: string;
    notes?: string;
  }) => {
    setActiveProposalRejectionId(proposalId);
    try {
      clearAttemptedValidationBlockers();
      await rejectCanonicalProposalMutation.mutateAsync({
        proposalId,
        notes,
      });
    } finally {
      setActiveProposalRejectionId((currentId) =>
        currentId === proposalId ? null : currentId,
      );
    }
  };

  const approveCanonicalHierarchyProposal = async ({
    hierarchyProposalId,
    approvedParentSlugs,
    approvedSynthesizedParentParentSlugs,
  }: HierarchyApprovalDraft & {
    hierarchyProposalId: string;
  }) => {
    setActiveHierarchyApprovalId(hierarchyProposalId);
    try {
      const result =
        await approveCanonicalHierarchyProposalMutation.mutateAsync({
          hierarchyProposalId,
          approvedParentSlugs,
          approvedSynthesizedParentParentSlugs,
        });
      if (result.outcome === "BLOCKED_VALIDATION") {
        const summary =
          result.validationIssues[0]?.message ??
          "Hierarchy approval was blocked by canonical snapshot validation.";
        setReviewActionMessage(summary);
        showAttemptedValidationBlockers({
          blockers: result.validationBlockers,
          summary,
          stageId: "review-hierarchy",
          context: {
            kind: "pending-hierarchy",
            hierarchyProposalId,
            hierarchyDraft: {
              approvedParentSlugs: approvedParentSlugs.slice(),
              approvedSynthesizedParentParentSlugs:
                approvedSynthesizedParentParentSlugs.slice(),
            },
          },
        });
        return false;
      }
      clearAttemptedValidationBlockers();
      return true;
    } finally {
      setActiveHierarchyApprovalId((currentId) =>
        currentId === hierarchyProposalId ? null : currentId,
      );
    }
  };

  const rejectCanonicalHierarchyProposal = async ({
    hierarchyProposalId,
    notes,
  }: {
    hierarchyProposalId: string;
    notes?: string;
  }) => {
    setActiveHierarchyRejectionId(hierarchyProposalId);
    try {
      clearAttemptedValidationBlockers();
      await rejectCanonicalHierarchyProposalMutation.mutateAsync({
        hierarchyProposalId,
        notes,
      });
    } finally {
      setActiveHierarchyRejectionId((currentId) =>
        currentId === hierarchyProposalId ? null : currentId,
      );
    }
  };

  const openPendingProposalFix = ({
    proposal,
    draft,
  }: {
    proposal: ICanonicalTagProposal;
    draft: CanonicalProposalDraft;
  }) => {
    const blockers = proposal.approvalValidationBlockers ?? [];
    const summary =
      proposal.approvalValidationIssues?.[0]?.message ??
      "This proposal cannot be approved until its validation blockers are resolved.";

    setReviewActionMessage(summary);
    if (blockers.length === 0) {
      focusProposalCard(proposal.proposalId);
      return;
    }

    showAttemptedValidationBlockers({
      blockers,
      summary,
      stageId: "review-proposals",
      context: {
        kind: "pending-proposal",
        proposalId: proposal.proposalId,
        approvedTagDraft: buildApprovedTagDraft({
          proposal,
          draft,
        }),
      },
    });
  };

  const openPendingHierarchyFix = ({
    proposal,
    draft,
  }: {
    proposal: ICanonicalHierarchyProposal;
    draft: HierarchyApprovalDraft;
  }) => {
    const blockers = proposal.approvalValidationBlockers ?? [];
    const summary =
      proposal.approvalValidationIssues?.[0]?.message ??
      "This recommendation cannot be approved until its validation blockers are resolved.";

    setReviewActionMessage(summary);
    if (blockers.length === 0) {
      focusHierarchyProposalCard(proposal.hierarchyProposalId);
      return;
    }

    showAttemptedValidationBlockers({
      blockers,
      summary,
      stageId: "review-hierarchy",
      context: {
        kind: "pending-hierarchy",
        hierarchyProposalId: proposal.hierarchyProposalId,
        hierarchyDraft: {
          approvedParentSlugs: draft.approvedParentSlugs.slice(),
          approvedSynthesizedParentParentSlugs:
            draft.approvedSynthesizedParentParentSlugs.slice(),
        },
      },
    });
  };

  const keepDuplicateValidationSource = async ({
    blocker,
    source,
  }: {
    blocker: ICanonicalBootstrapValidationBlocker;
    source: ICanonicalBootstrapValidationBlockerSource;
  }) => {
    if (!isDuplicateValidationIssue(blocker)) {
      return;
    }

    const actionKey = `${getValidationBlockerSourceKey(source)}:keep`;
    const attemptedPendingProposal =
      attemptedValidationContext?.kind === "pending-proposal" &&
      source.proposalId === attemptedValidationContext.proposalId
        ? attemptedValidationContext
        : null;
    const attemptedPendingHierarchy =
      attemptedValidationContext?.kind === "pending-hierarchy" &&
      source.hierarchyProposalId ===
        attemptedValidationContext.hierarchyProposalId
        ? attemptedValidationContext
        : null;
    const conflictingSources = blocker.sources.filter(
      (candidate) => !sameValidationBlockerSource(candidate, source),
    );

    setActiveValidationRepairActionKey(actionKey);
    try {
      for (const conflictingSource of conflictingSources) {
        if (
          conflictingSource.sourceType ===
          "APPROVED_SYNTHESIZED_HIERARCHY_PARENT"
        ) {
          if (!conflictingSource.hierarchyProposalId) {
            continue;
          }

          await rejectCanonicalHierarchyProposalMutation.mutateAsync({
            hierarchyProposalId: conflictingSource.hierarchyProposalId,
          });
          continue;
        }

        if (!conflictingSource.proposalId) {
          continue;
        }

        await rejectCanonicalProposalMutation.mutateAsync({
          proposalId: conflictingSource.proposalId,
        });
      }

      if (attemptedPendingProposal) {
        const result = await approveCanonicalProposalMutation.mutateAsync({
          proposalId: attemptedPendingProposal.proposalId,
          approvedTagDraft: attemptedPendingProposal.approvedTagDraft,
        });

        if (result.outcome === "BLOCKED_VALIDATION") {
          const summary =
            result.validationIssues[0]?.message ??
            "Approval was blocked by canonical snapshot validation.";
          setReviewActionMessage(summary);
          showAttemptedValidationBlockers({
            blockers: result.validationBlockers,
            summary,
            stageId: "review-proposals",
            context: attemptedPendingProposal,
          });
          return;
        }
      }

      if (attemptedPendingHierarchy) {
        const result =
          await approveCanonicalHierarchyProposalMutation.mutateAsync({
            hierarchyProposalId: attemptedPendingHierarchy.hierarchyProposalId,
            approvedParentSlugs:
              attemptedPendingHierarchy.hierarchyDraft.approvedParentSlugs,
            approvedSynthesizedParentParentSlugs:
              attemptedPendingHierarchy.hierarchyDraft
                .approvedSynthesizedParentParentSlugs,
          });

        if (result.outcome === "BLOCKED_VALIDATION") {
          const summary =
            result.validationIssues[0]?.message ??
            "Hierarchy approval was blocked by canonical snapshot validation.";
          setReviewActionMessage(summary);
          showAttemptedValidationBlockers({
            blockers: result.validationBlockers,
            summary,
            stageId: "review-hierarchy",
            context: attemptedPendingHierarchy,
          });
          return;
        }
      }

      clearAttemptedValidationBlockers();
      setReviewActionMessage(
        attemptedPendingProposal
          ? `Kept ${source.tag.layer}:${source.tag.slug} by approving this proposal and removing ${conflictingSources.length} conflicting duplicate source(s).`
          : attemptedPendingHierarchy
            ? `Kept ${source.tag.layer}:${source.tag.slug} by approving this recommendation and removing ${conflictingSources.length} conflicting duplicate source(s).`
            : `Kept ${source.tag.layer}:${source.tag.slug} and removed ${conflictingSources.length} conflicting duplicate source(s).`,
      );
    } catch (error) {
      setReviewActionMessage(
        error instanceof Error
          ? error.message
          : "Failed to resolve the duplicate validation blocker.",
      );
    } finally {
      setActiveValidationRepairActionKey((currentKey) =>
        currentKey === actionKey ? null : currentKey,
      );
    }
  };

  const openApprovedTagRepair = (
    source: ICanonicalBootstrapValidationBlockerSource,
  ) => {
    setApprovedTagRepairSource(source);
    setApprovedTagRepairDraft({
      layer: source.tag.layer,
      slug: source.tag.slug,
      name: source.tag.name,
      description: source.tag.description,
      synonyms: source.tag.synonyms.join(", "),
      parentSlugs: source.tag.parentSlugs.slice(),
    });
  };

  const openApprovedTagHierarchyRepair = (
    source: ICanonicalBootstrapValidationBlockerSource,
  ) => {
    setApprovedTagHierarchyRepairSource(source);
    setApprovedTagHierarchyRepairDraft(source.tag.parentSlugs.slice());
  };

  const openSynthesizedParentRepair = (
    source: ICanonicalBootstrapValidationBlockerSource,
  ) => {
    setSynthesizedParentRepairSource(source);
    setSynthesizedParentRepairDraft({
      slug: source.tag.slug,
      name: source.tag.name,
      description: source.tag.description,
      synonyms: source.tag.synonyms.join(", "),
      parentSlugs: source.tag.parentSlugs.slice(),
    });
  };

  const saveApprovedTagRepair = async () => {
    if (
      !approvedTagRepairSource?.proposalId ||
      !approvedTagRepairDraft ||
      approveCanonicalProposalMutation.isLoading
    ) {
      return;
    }

    const result = await approveCanonicalProposalMutation.mutateAsync({
      proposalId: approvedTagRepairSource.proposalId,
      approvedTagDraft: {
        layer: approvedTagRepairDraft.layer,
        slug: approvedTagRepairDraft.slug.trim(),
        name: approvedTagRepairDraft.name.trim(),
        description: approvedTagRepairDraft.description.trim(),
        synonyms: approvedTagRepairDraft.synonyms
          .split(",")
          .map((synonym) => synonym.trim())
          .filter(Boolean),
        exampleOracleIds: [],
      },
    });
    if (result.outcome === "BLOCKED_VALIDATION") {
      const summary =
        result.validationIssues[0]?.message ??
        "Saving the approved tag was blocked by canonical snapshot validation.";
      setReviewActionMessage(summary);
      showAttemptedValidationBlockers({
        blockers: result.validationBlockers,
        summary,
        stageId: "review-proposals",
      });
      return;
    }

    clearAttemptedValidationBlockers();
    setApprovedTagRepairSource(null);
    setApprovedTagRepairDraft(null);
    setReviewActionMessage("Approved canonical tag updated.");
  };

  const saveApprovedTagHierarchyRepair = async () => {
    if (
      !approvedTagHierarchyRepairSource?.proposalId ||
      updateCanonicalTagHierarchyMutation.isLoading
    ) {
      return;
    }

    const result = await updateCanonicalTagHierarchyMutation.mutateAsync({
      proposalId: approvedTagHierarchyRepairSource.proposalId,
      parentSlugs: approvedTagHierarchyRepairDraft,
    });
    if (result.outcome === "BLOCKED_VALIDATION") {
      const summary =
        result.validationIssues[0]?.message ??
        "Updating approved tag parents was blocked by canonical snapshot validation.";
      setReviewActionMessage(summary);
      showAttemptedValidationBlockers({
        blockers: result.validationBlockers,
        summary,
        stageId: "review-proposals",
      });
      return;
    }

    clearAttemptedValidationBlockers();
    setApprovedTagHierarchyRepairSource(null);
    setApprovedTagHierarchyRepairDraft([]);
    setReviewActionMessage("Approved tag parent links updated.");
  };

  const saveSynthesizedParentRepair = async () => {
    if (
      !synthesizedParentRepairSource?.hierarchyProposalId ||
      !synthesizedParentRepairDraft ||
      updateApprovedSynthesizedHierarchyParentMutation.isLoading
    ) {
      return;
    }

    const result =
      await updateApprovedSynthesizedHierarchyParentMutation.mutateAsync({
        hierarchyProposalId: synthesizedParentRepairSource.hierarchyProposalId,
        synthesizedParentDraft: {
          slug: synthesizedParentRepairDraft.slug.trim(),
          name: synthesizedParentRepairDraft.name.trim(),
          description: synthesizedParentRepairDraft.description.trim(),
          synonyms: synthesizedParentRepairDraft.synonyms
            .split(",")
            .map((synonym) => synonym.trim())
            .filter(Boolean),
          parentSlugs: synthesizedParentRepairDraft.parentSlugs,
        },
      });
    if (result.outcome === "BLOCKED_VALIDATION") {
      const summary =
        result.validationIssues[0]?.message ??
        "Saving the synthesized parent was blocked by canonical snapshot validation.";
      setReviewActionMessage(summary);
      showAttemptedValidationBlockers({
        blockers: result.validationBlockers,
        summary,
        stageId: "review-hierarchy",
      });
      return;
    }

    clearAttemptedValidationBlockers();
    setSynthesizedParentRepairSource(null);
    setSynthesizedParentRepairDraft(null);
    setReviewActionMessage("Synthesized parent updated.");
  };

  const workflowContext = useMemo(() => {
    if (!workspaceRun) {
      return {
        stageId: "configure" as StageId,
        stageLabel: "Not Started",
        nextActionTitle: "Start Bootstrap",
        nextActionDescription:
          "Create a fresh canonical bootstrap workspace to begin scanning Tagger source tags.",
      };
    }

    if (workspaceRun.runStatus === TaxonomyRunStatus.PUBLISH_FAILED) {
      return {
        stageId: "publish" as StageId,
        stageLabel: "Ready to Publish",
        nextActionTitle: canPublishSnapshot
          ? "Retry Publish"
          : hasValidationBlockers
            ? "Resolve Validation Blockers"
            : "Review Publish Failure",
        nextActionDescription: hasValidationBlockers
          ? `${validationBlockers.length} validation blocker(s) currently prevent publish. Repair the affected approved tags or synthesized parents, then retry publish.`
          : workspaceFailureReason ??
            "The last publish attempt failed. Review the blocker details and retry publish when the run is valid again.",
        primaryAction: canPublishSnapshot
          ? ({
              label: "Retry Publish",
              onClick: () => void publishCanonicalSnapshot(),
              disabled: !canPublishSnapshot,
              loading: publishCanonicalSnapshotMutation.isLoading,
            } satisfies AsyncWorkflowAction)
          : ({
              label: "Review Validation Blockers",
              onClick: () => focusElement("validation-blockers-anchor"),
            } satisfies AsyncWorkflowAction),
      };
    }

    if (workspaceRun.runStatus === TaxonomyRunStatus.FAILED) {
      return {
        stageId: failureStageId,
        stageLabel: "Failed",
        nextActionTitle: "Review Failure",
        nextActionDescription:
          workspaceFailureReason ??
          "Review the failure details, then clean the workspace before starting again.",
        primaryAction: {
          label: "Review Failure",
          onClick: () => focusElement("recovery-section"),
        } satisfies AsyncWorkflowAction,
      };
    }

    if (workspaceRun.runStatus === TaxonomyRunStatus.COMPLETED) {
      return {
        stageId: "publish" as StageId,
        stageLabel: "Published",
        nextActionTitle: "View Published Snapshot",
        nextActionDescription:
          currentSnapshot?.metadata.sourceRunId === workspaceRun.runId
            ? "The snapshot is published. Inspect the published output or clean the workspace when you are ready for another run."
            : "The workflow is complete. Review diagnostics or clean the workspace to start again.",
        primaryAction: {
          label: "View Published Snapshot",
          onClick: () =>
            focusElement("published-snapshot-anchor", {
              openDiagnostics: true,
            }),
        } satisfies AsyncWorkflowAction,
      };
    }

    if (
      workspaceRun.runStatus === TaxonomyRunStatus.PENDING ||
      workspaceRun.runStatus === TaxonomyRunStatus.SCANNING
    ) {
      return {
        stageId: "scan" as StageId,
        stageLabel: "Collecting Source Tags",
        nextActionTitle: "Async Work Running",
        nextActionDescription:
          "The bootstrap is currently scanning Tagger source pages. No review action is available until scanning and evidence work finish.",
      };
    }

    if (workspaceRun.runStatus === TaxonomyRunStatus.DETAILING) {
      return {
        stageId: hasCompletedPageScans
          ? ("detail" as StageId)
          : ("scan" as StageId),
        stageLabel: hasCompletedPageScans
          ? "Building Evidence"
          : "Collecting Source Tags",
        nextActionTitle: "Async Work Running",
        nextActionDescription: hasCompletedPageScans
          ? "The bootstrap is enriching source tags with ancestry and exemplar evidence. Review remains locked until detail work completes."
          : "The bootstrap is still processing queued scan batches. Evidence enrichment may already be running for completed scan batches, but the scan stage stays active until every queued scan batch finishes.",
      };
    }

    if (workspaceRun.runStatus === TaxonomyRunStatus.PROPOSING) {
      return {
        stageId: "generate-proposals" as StageId,
        stageLabel: "Generating Canonical Proposals",
        nextActionTitle: hasStalledProposalGeneration
          ? "Resume Proposal Generation"
          : "Async Work Running",
        nextActionDescription: hasStalledProposalGeneration
          ? isProposalKickoffPreparing(workspaceRun)
            ? `Proposal generation appears stalled while preparing proposal work. The run last updated at ${formatTimestamp(workspaceRun.updatedAt)} before any proposal chunks were queued. Resume proposal generation to requeue kickoff work.`
            : `Proposal generation appears stalled. The run last updated at ${formatTimestamp(workspaceRun.updatedAt)} after processing ${getRunProposalSummary(workspaceRun)}. Resume proposal generation to requeue missing work.`
          : isProposalKickoffPreparing(workspaceRun)
            ? "Proposal generation is preparing proposal work. Wait for clustering and queue fanout to finish before reviewing proposals."
            : "Proposal generation is active. Wait for the run to return to review mode before approving or rejecting proposals.",
        primaryAction: hasStalledProposalGeneration
          ? ({
              label: "Resume Proposal Generation",
              onClick: () => void generateCanonicalProposals(),
              disabled: !canGenerateProposals,
              loading: generateCanonicalProposalsMutation.isLoading,
            } satisfies AsyncWorkflowAction)
          : undefined,
      };
    }

    if (workspaceRun.runStatus === TaxonomyRunStatus.PUBLISHING) {
      return {
        stageId: "publish" as StageId,
        stageLabel: "Publishing Snapshot",
        nextActionTitle: hasStalledPublishing
          ? "Retry Publish"
          : "Async Work Running",
        nextActionDescription: hasStalledPublishing
          ? `Snapshot publishing appears stalled. The run last updated at ${formatTimestamp(workspaceRun.updatedAt)}. Retry publish to requeue the publish worker for the same run.`
          : "Snapshot publishing is active. Wait for the publish worker to finish or surface a recoverable failure.",
        primaryAction: hasStalledPublishing
          ? ({
              label: "Retry Publish",
              onClick: () => void publishCanonicalSnapshot(),
              disabled: !canPublishSnapshot,
              loading: publishCanonicalSnapshotMutation.isLoading,
            } satisfies AsyncWorkflowAction)
          : undefined,
      };
    }

    if (workspaceRun.runStatus === TaxonomyRunStatus.GENERATING_HIERARCHY) {
      return {
        stageId: "generate-hierarchy" as StageId,
        stageLabel: "Generating Hierarchy",
        nextActionTitle: hasStalledHierarchyGeneration
          ? "Resume Hierarchy Generation"
          : "Async Work Running",
        nextActionDescription: hasStalledHierarchyGeneration
          ? `Hierarchy generation appears stalled. The run last updated at ${formatTimestamp(workspaceRun.updatedAt)} after completing ${latestHierarchyBatchCompletedCount} of ${latestHierarchyBatchQueuedCount} batches. Resume hierarchy generation to requeue any missing hierarchy work for this pass.`
          : hasCurrentHierarchyProgressMetrics
            ? latestHierarchyTargetQueuedCount > 0
              ? `Hierarchy generation is active. Processed ${latestHierarchyTargetCompletedCount} of ${latestHierarchyTargetQueuedCount} approved tag(s) across ${latestHierarchyBatchCompletedCount} of ${latestHierarchyBatchQueuedCount} batches.`
              : "Hierarchy generation is active. No approved tags need processing in this pass; the final no-op batch is still completing."
            : "Hierarchy generation is active. Review will open automatically once recommendations are ready.",
        primaryAction: hasStalledHierarchyGeneration
          ? ({
              label: "Resume Hierarchy Generation",
              onClick: () => void generateCanonicalHierarchy(),
              disabled: !canGenerateHierarchy,
              loading: generateCanonicalHierarchyMutation.isLoading,
            } satisfies AsyncWorkflowAction)
          : undefined,
      };
    }

    if (hasActiveProposalApprovalJob) {
      return {
        stageId: "review-proposals" as StageId,
        stageLabel: "Needs Proposal Review",
        nextActionTitle: hasStalledProposalApprovalJob
          ? "Resume Proposal Approval"
          : "Async Work Running",
        nextActionDescription: hasStalledProposalApprovalJob
          ? `A bulk proposal approval job appears stalled after ${latestProposalApprovalJob?.completedCount ?? 0} of ${latestProposalApprovalJob?.queuedCount ?? 0} item(s) processed. Retry Approve Remaining to resume the pending proposal approvals.`
          : `A bulk proposal approval job is ${latestProposalApprovalJob?.jobStatus.toLowerCase()}. ${latestProposalApprovalJob?.approvedCount ?? 0} proposal(s) approved, ${latestProposalApprovalJob?.skippedInvalidCount ?? 0} skipped as invalid, and ${latestProposalApprovalJob?.completedCount ?? 0} of ${latestProposalApprovalJob?.queuedCount ?? 0} item(s) processed so far.`,
        primaryAction: hasStalledProposalApprovalJob
          ? ({
              label: "Resume Proposal Approval",
              onClick: () => void approveRemainingProposals(),
              disabled: !canApproveRemaining,
              loading: approveRemainingCanonicalProposalsMutation.isLoading,
            } satisfies AsyncWorkflowAction)
          : undefined,
      };
    }

    if (!hasProposalGenerationStarted) {
      return {
        stageId: "generate-proposals" as StageId,
        stageLabel: "Generating Canonical Proposals",
        nextActionTitle: "Generate Proposals",
        nextActionDescription:
          "Scan and detail work are complete. Start canonical proposal generation to create the review queue.",
        primaryAction: {
          label: "Generate Proposals",
          onClick: () => void generateCanonicalProposals(),
          disabled: !canGenerateProposals,
          loading: generateCanonicalProposalsMutation.isLoading,
        } satisfies AsyncWorkflowAction,
      };
    }

    if (hasPendingProposals) {
      if (hasProposalDuplicateGroups) {
        return {
          stageId: "review-proposals" as StageId,
          stageLabel: "Resolve Proposal Duplicates",
          nextActionTitle: "Approve Duplicate Fixes",
          nextActionDescription: `${proposalDuplicateGroups.length} proposal duplicate fix group(s) need review before bulk proposal approval is safe.`,
          primaryAction: {
            label: "Resolve Proposal Duplicates",
            onClick: () =>
              focusElement("proposal-duplicate-groups-anchor", {
                stageId: "review-proposals",
              }),
          } satisfies AsyncWorkflowAction,
        };
      }

      return {
        stageId: "review-proposals" as StageId,
        stageLabel: "Needs Proposal Review",
        nextActionTitle: "Review Proposals",
        nextActionDescription: `${workspacePendingProposalCount} canonical proposal(s) still need approval or rejection before the workflow can continue.`,
        primaryAction: {
          label: "Review Proposals",
          onClick: () =>
            focusElement(STAGE_CARD_ID["review-proposals"], {
              stageId: "review-proposals",
            }),
        } satisfies AsyncWorkflowAction,
      };
    }

    if (hierarchyRuns.length === 0) {
      return {
        stageId: "generate-hierarchy" as StageId,
        stageLabel: "Generating Hierarchy",
        nextActionTitle: "Generate Hierarchy",
        nextActionDescription:
          latestHierarchyRun?.rerunSummary ??
          "All canonical proposals are decided. Generate hierarchy recommendations before publishing so parent links and synthesized parent tags can be reviewed.",
        primaryAction: {
          label: "Generate Hierarchy",
          onClick: () => void generateCanonicalHierarchy(),
          disabled: !canGenerateHierarchy,
          loading: generateCanonicalHierarchyMutation.isLoading,
        } satisfies AsyncWorkflowAction,
      };
    }

    if (hasActiveHierarchyApprovalJob) {
      return {
        stageId: "review-hierarchy" as StageId,
        stageLabel: "Needs Hierarchy Review",
        nextActionTitle: hasStalledHierarchyApprovalJob
          ? "Resume Hierarchy Approval"
          : "Async Work Running",
        nextActionDescription: hasStalledHierarchyApprovalJob
          ? `A bulk hierarchy approval job appears stalled after ${latestHierarchyApprovalJob?.completedCount ?? 0} of ${latestHierarchyApprovalJob?.queuedCount ?? 0} item(s) processed. Retry Approve Remaining to resume the pending hierarchy approvals.`
          : `A bulk hierarchy approval job is ${latestHierarchyApprovalJob?.jobStatus.toLowerCase()}. ${latestHierarchyApprovalJob?.approvedCount ?? 0} recommendation(s) approved, ${latestHierarchyApprovalJob?.skippedInvalidCount ?? 0} skipped as invalid, and ${latestHierarchyApprovalJob?.completedCount ?? 0} of ${latestHierarchyApprovalJob?.queuedCount ?? 0} item(s) processed so far.`,
        primaryAction: hasStalledHierarchyApprovalJob
          ? ({
              label: "Resume Hierarchy Approval",
              onClick: () => void approveRemainingHierarchyProposals(),
              disabled: !canApproveRemainingHierarchy,
              loading:
                approveRemainingCanonicalHierarchyProposalsMutation.isLoading,
            } satisfies AsyncWorkflowAction)
          : undefined,
      };
    }

    if (hasPendingHierarchyProposals) {
      if (hasHierarchyDuplicateGroups) {
        return {
          stageId: "review-hierarchy" as StageId,
          stageLabel: "Resolve Hierarchy Duplicates",
          nextActionTitle: "Approve Duplicate Fixes",
          nextActionDescription: `${hierarchyDuplicateGroups.length} synthesized-parent duplicate fix group(s) need review before bulk hierarchy approval is safe.`,
          primaryAction: {
            label: "Resolve Hierarchy Duplicates",
            onClick: () =>
              focusElement("hierarchy-duplicate-groups-anchor", {
                stageId: "review-hierarchy",
              }),
          } satisfies AsyncWorkflowAction,
        };
      }

      return {
        stageId: "review-hierarchy" as StageId,
        stageLabel: "Needs Hierarchy Review",
        nextActionTitle: "Review Hierarchy",
        nextActionDescription: hierarchyRerunBlockedByPendingReview
          ? `${workspacePendingHierarchyProposalCount} hierarchy recommendation(s) still need approval or rejection. Review this pass first, then Generate Hierarchy Again will unlock.`
          : `${workspacePendingHierarchyProposalCount} hierarchy recommendation(s) still need approval or rejection before publishing.`,
        primaryAction: {
          label: "Review Hierarchy",
          onClick: () =>
            focusElement(STAGE_CARD_ID["review-hierarchy"], {
              stageId: "review-hierarchy",
            }),
        } satisfies AsyncWorkflowAction,
      };
    }

    return {
      stageId: "publish" as StageId,
      stageLabel: "Ready to Publish",
      nextActionTitle: "Publish Snapshot",
      nextActionDescription: canPublishSnapshot
        ? hierarchyRerunRecommended
          ? "Canonical proposal review and hierarchy review are complete. Publish the snapshot now, or optionally run hierarchy generation again to refine parent structure."
          : "Canonical proposal review and hierarchy review are complete. Publish the snapshot when you are ready."
        : hasValidationBlockers
          ? `${validationBlockers.length} validation blocker(s) are preventing publish. Repair the affected approved tags or synthesized parents before retrying.`
          : latestHierarchyRun?.recommendedAction === "REVIEW_WARNINGS"
            ? "The latest hierarchy run recommends reviewing warnings before publishing."
            : "Publish stays locked until hierarchy generation is complete and all hierarchy recommendations are decided.",
      primaryAction: {
        label: "Publish Snapshot",
        onClick: () => void publishCanonicalSnapshot(),
        disabled: !canPublishSnapshot,
        loading: publishCanonicalSnapshotMutation.isLoading,
      } satisfies AsyncWorkflowAction,
    };
  }, [
    bootstrapMode,
    canGenerateHierarchy,
    canGenerateProposals,
    canPublishSnapshot,
    cleanupWorkspaceMutation.isLoading,
    failureStageId,
    generateCanonicalHierarchy,
    generateCanonicalHierarchyMutation.isLoading,
    generateCanonicalProposals,
    generateCanonicalProposalsMutation.isLoading,
    hasValidationBlockers,
    hasActiveHierarchyApprovalJob,
    hasActiveProposalApprovalJob,
    hasHierarchyDuplicateGroups,
    hasPendingHierarchyProposals,
    hasPendingProposals,
    hasProposalDuplicateGroups,
    hasProposalGenerationStarted,
    hasStalledProposalGeneration,
    hierarchyDuplicateGroups.length,
    hierarchyRerunRecommended,
    hierarchyRerunBlockedByPendingReview,
    hierarchyRuns.length,
    latestHierarchyApprovalJob?.completedCount,
    latestHierarchyApprovalJob?.jobStatus,
    latestHierarchyApprovalJob?.queuedCount,
    latestHierarchyBatchCompletedCount,
    latestHierarchyBatchQueuedCount,
    latestHierarchyRun?.recommendedAction,
    latestHierarchyRun?.rerunSummary,
    latestHierarchyTargetCompletedCount,
    latestHierarchyTargetQueuedCount,
    latestProposalApprovalJob?.completedCount,
    latestProposalApprovalJob?.jobStatus,
    latestProposalApprovalJob?.queuedCount,
    hasCurrentHierarchyProgressMetrics,
    pageLimit,
    proposalModelId,
    proposalDuplicateGroups.length,
    publishCanonicalSnapshot,
    publishCanonicalSnapshotMutation.isLoading,
    startBootstrapMutation,
    currentSnapshot?.metadata.sourceRunId,
    validationBlockers.length,
    detailFailureMessage,
    workspaceFailureReason,
    workspacePendingHierarchyProposalCount,
    workspacePendingProposalCount,
    workspaceRequiresCleanup,
    workspaceRun,
  ]);

  const healthColor =
    workspaceRun?.runStatus === TaxonomyRunStatus.FAILED
      ? "error"
      : workspaceRun?.runStatus === TaxonomyRunStatus.PUBLISH_FAILED
        ? "warning"
        : hasValidationBlockers
          ? "warning"
          : hasWorkflowWarnings
            ? "warning"
            : "success";
  const healthLabel =
    workspaceRun?.runStatus === TaxonomyRunStatus.FAILED
      ? "Failed"
      : workspaceRun?.runStatus === TaxonomyRunStatus.PUBLISH_FAILED
        ? "Warnings"
        : hasValidationBlockers
          ? `Warnings (${validationBlockers.length})`
          : hasWorkflowWarnings
            ? `Warnings (${workspaceWarningCount})`
            : "Healthy";

  const secondaryHeaderActions: AsyncWorkflowAction[] = [
    {
      label: "Refresh Workflow State",
      onClick: () => void refreshWorkflowState(),
      disabled: isRefreshingWorkflowState,
      loading: isRefreshingWorkflowState,
      variant: "outlined",
      color: "inherit",
    },
  ];
  if (canStopBootstrap) {
    secondaryHeaderActions.push({
      label: "Stop Run",
      onClick: () => void stopBootstrap(),
      disabled: !canStopBootstrap,
      loading: stopBootstrapMutation.isLoading,
      variant: "outlined",
      color: "error",
    });
  }

  const currentStageIndex = STAGE_SEQUENCE.indexOf(workflowContext.stageId);
  useEffect(() => {
    setExpandedStageIds((currentIds) =>
      currentIds.includes(workflowContext.stageId)
        ? currentIds
        : [...currentIds, workflowContext.stageId],
    );
  }, [workflowContext.stageId]);

  const stageDefinitions: AsyncWorkflowStageDefinition[] = STAGE_SEQUENCE.map(
    (stageId, index) => {
      let summary = "";
      switch (stageId) {
        case "configure":
          summary = workspaceRun
            ? `Workspace ${workspaceRun.runId} is active.`
            : "No workspace exists yet.";
          break;
        case "scan":
          summary = workspaceRun
            ? `Scan batches ${getRunPageScanSummary(workspaceRun)}`
            : "Bootstrap not started.";
          break;
        case "detail":
          summary = workspaceRun
            ? detailFailureMessage
              ? `Failed: ${detailFailureMessage}`
              : `Detail work ${getRunDetailSummary(workspaceRun)}`
            : "Evidence work not started.";
          break;
        case "generate-proposals":
          summary = workspaceRun
            ? workspaceRun.runStatus === TaxonomyRunStatus.PROPOSING &&
              isProposalKickoffPreparing(workspaceRun)
              ? "Preparing proposal work."
              : hasProposalGenerationStarted
                ? `Proposal work ${getRunProposalDisplaySummary(workspaceRun)}`
                : "Ready to start proposal generation."
            : "Proposal generation locked.";
          break;
        case "review-proposals":
          summary = hasActiveProposalApprovalJob
            ? hasStalledProposalApprovalJob
              ? `Bulk approval stalled at ${latestProposalApprovalJob?.completedCount ?? 0}/${latestProposalApprovalJob?.queuedCount ?? 0}`
              : `Bulk approval ${latestProposalApprovalJob?.completedCount ?? 0}/${latestProposalApprovalJob?.queuedCount ?? 0}`
            : hasProposalDuplicateGroups
              ? `${proposalDuplicateGroups.length} duplicate fix group(s) pending.`
              : hasPendingProposals
                ? `${workspacePendingProposalCount} proposal(s) awaiting review.`
                : hasProposalGenerationStarted
                  ? "Proposal review complete."
                  : "Proposal review locked.";
          break;
        case "generate-hierarchy":
          summary =
            hierarchyRuns.length === 0
              ? "Hierarchy generation not started."
              : latestHierarchyRun
                ? latestHierarchyRun.runStatus === "GENERATING" &&
                  hasCurrentHierarchyProgressMetrics
                  ? hasStalledHierarchyGeneration
                    ? `Stalled at ${latestHierarchyBatchCompletedCount}/${latestHierarchyBatchQueuedCount} batches.`
                    : latestHierarchyTargetQueuedCount > 0
                      ? `Processed ${latestHierarchyTargetCompletedCount}/${latestHierarchyTargetQueuedCount} approved tags.`
                      : "No approved tags to process in this pass."
                  : latestHierarchyRun.runStatus === "COMPLETED" &&
                      hasCurrentHierarchyProgressMetrics
                    ? `Generated ${latestHierarchyRecommendationCount} recommendation(s) in run ${latestHierarchyRun.runNumber}.`
                    : `Run ${latestHierarchyRun.runNumber} is ${latestHierarchyRun.runStatus.toLowerCase()}.`
                : "Hierarchy generation pending.";
          break;
        case "review-hierarchy":
          summary = hasActiveHierarchyApprovalJob
            ? hasStalledHierarchyApprovalJob
              ? `Bulk approval stalled at ${latestHierarchyApprovalJob?.completedCount ?? 0}/${latestHierarchyApprovalJob?.queuedCount ?? 0}`
              : `Bulk approval ${latestHierarchyApprovalJob?.completedCount ?? 0}/${latestHierarchyApprovalJob?.queuedCount ?? 0}`
            : hasHierarchyDuplicateGroups
              ? `${hierarchyDuplicateGroups.length} duplicate fix group(s) pending.`
              : hasPendingHierarchyProposals
                ? `${workspacePendingHierarchyProposalCount} recommendation(s) awaiting review.`
                : hierarchyRuns.length > 0
                  ? "Hierarchy review complete."
                  : "Hierarchy review locked.";
          break;
        case "publish":
          summary =
            workspaceRun?.runStatus === TaxonomyRunStatus.PUBLISHING
              ? hasStalledPublishing
                ? "Publish appears stalled."
                : "Publish in progress."
              : workspaceRun?.runStatus === TaxonomyRunStatus.COMPLETED
                ? "Snapshot published."
                : hasValidationBlockers
                  ? `${validationBlockers.length} validation blocker(s) remain.`
                  : canPublishSnapshot
                    ? "Snapshot is publish-ready."
                    : "Publishing is locked.";
          break;
      }

      let state: AsyncWorkflowStageVisualState = "locked";
      if (workspaceRun?.runStatus === TaxonomyRunStatus.FAILED) {
        state =
          index < STAGE_SEQUENCE.indexOf(failureStageId)
            ? "complete"
            : "locked";
        if (stageId === failureStageId) {
          state = "failed";
        }
      } else if (
        workspaceRun?.runStatus === TaxonomyRunStatus.COMPLETED &&
        stageId === "publish"
      ) {
        state = "complete";
      } else if (index < currentStageIndex) {
        state = "complete";
      } else if (stageId === workflowContext.stageId) {
        state = "current";
      } else {
        state = "locked";
      }

      return {
        id: stageId,
        label:
          stageId === "configure"
            ? "Configure & Start"
            : stageId === "scan"
              ? "Scan Source Tags"
              : stageId === "detail"
                ? "Build Evidence"
                : stageId === "generate-proposals"
                  ? "Generate Proposals"
                  : stageId === "review-proposals"
                    ? "Review Proposals"
                    : stageId === "generate-hierarchy"
                      ? "Generate Hierarchy"
                      : stageId === "review-hierarchy"
                        ? "Review Hierarchy"
                        : "Publish Snapshot",
        summary,
        state,
      };
    },
  );

  const completedStageCount = stageDefinitions.filter(
    (stage) => stage.state === "complete",
  ).length;

  const progressCardHelper = workspaceRun
    ? `Current stage: ${workflowContext.stageLabel}. Scan batches ${getRunPageScanSummary(
        workspaceRun,
      )}, detail work ${getRunDetailSummary(workspaceRun)}, proposal work ${getRunProposalDisplaySummary(
        workspaceRun,
      )}.`
    : "No workspace is active yet.";

  const reviewQueueCount =
    workspacePendingProposalCount + workspacePendingHierarchyProposalCount;
  const reviewQueueHelper = workspaceRun
    ? `${workspacePendingProposalCount} proposal(s) and ${workspacePendingHierarchyProposalCount} hierarchy recommendation(s) pending.`
    : "Review queues appear after bootstrap work starts.";

  const snapshotCardValue = currentSnapshot
    ? String(currentSnapshot.tags.length)
    : "0";
  const snapshotCardHelper = currentSnapshot
    ? `Published snapshot ${currentSnapshot.metadata.version} from run ${currentSnapshot.metadata.sourceRunId}.`
    : "No canonical snapshot has been published yet.";

  const publishChecklistItems = [
    {
      label: "Canonical proposal review complete",
      complete: hasProposalGenerationStarted && !hasPendingProposals,
    },
    {
      label: "Hierarchy generation completed",
      complete: hierarchyRuns.length > 0,
    },
    {
      label: "Hierarchy review complete",
      complete: hierarchyRuns.length > 0 && !hasPendingHierarchyProposals,
    },
    {
      label: "No async work still running",
      complete:
        (workspaceRun?.runStatus === TaxonomyRunStatus.AWAITING_REVIEW ||
          workspaceRun?.runStatus === TaxonomyRunStatus.PUBLISH_FAILED ||
          workspaceRun?.runStatus === TaxonomyRunStatus.COMPLETED) &&
        !hasActiveApprovalJob,
    },
    {
      label: "Latest hierarchy run completed",
      complete: latestHierarchyRun?.runStatus === "COMPLETED",
    },
    {
      label: "No canonical snapshot validation blockers remain",
      complete: !hasValidationBlockers,
    },
  ];

  const pageErrorMessage = adminStateQuery.error?.message ?? mutationErrors[0];

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <AppHeader />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: { xs: "flex", lg: "grid" },
          flexDirection: "column",
          gridTemplateColumns: {
            lg: `${ADMIN_SIDEBAR_DESKTOP_WIDTH}px minmax(0, 1fr)`,
          },
          alignItems: "stretch",
          overflow: "hidden",
        }}
      >
        <AdminSidebar activeView="canonical-bootstrap" />
        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
          }}
        >
          <Container maxWidth="xl" sx={{ py: 4 }}>
            <Stack spacing={3} sx={{ minWidth: 0 }}>
              {pageErrorMessage ? (
                <Alert severity="error">{pageErrorMessage}</Alert>
              ) : feedbackBanner ? (
                <Alert severity={feedbackBanner.severity}>
                  {feedbackBanner.message}
                </Alert>
              ) : null}

              <WorkflowHeader
                title="Canonical Bootstrap"
                description="Run the canonical tag bootstrap as a guided workflow: start a workspace, generate canonical proposals, review them, generate hierarchy, review the hierarchy, then publish a snapshot."
                stageLabel={workflowContext.stageLabel}
                healthLabel={healthLabel}
                healthColor={healthColor}
                nextActionTitle={workflowContext.nextActionTitle}
                nextActionDescription={workflowContext.nextActionDescription}
                primaryAction={workflowContext.primaryAction}
                secondaryActions={secondaryHeaderActions}
              />

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                }}
              >
                <SummaryMetricCard
                  label="Progress"
                  value={`${completedStageCount}/${STAGE_SEQUENCE.length}`}
                  helper={progressCardHelper}
                />
                <SummaryMetricCard
                  label="Review Queue"
                  value={String(reviewQueueCount)}
                  helper={reviewQueueHelper}
                />
                <SummaryMetricCard
                  label="Published Tags"
                  value={snapshotCardValue}
                  helper={snapshotCardHelper}
                />
              </Box>

              <WarningSummary
                warningCount={workspaceWarningCount}
                latestWarningAt={workspaceRun?.lastWarningAt}
                topCodes={topWarningCodes}
                expanded={warningsExpanded}
                onToggle={() => setWarningsExpanded((current) => !current)}
              >
                {warningsQuery.error ? (
                  <Alert severity="error">{warningsQuery.error.message}</Alert>
                ) : !workspaceRun ? (
                  <Alert severity="info">
                    Start a canonical bootstrap run to capture warnings here.
                  </Alert>
                ) : !hasWorkflowWarnings && !detailFailureMessage ? (
                  <Alert severity="success">
                    No async workflow warnings have been recorded for this run.
                  </Alert>
                ) : (
                  <Stack spacing={2}>
                    {detailFailureMessage ? (
                      <Alert severity="error">{detailFailureMessage}</Alert>
                    ) : null}
                    {!hasWorkflowWarnings ? (
                      <Alert severity="info">
                        No async workflow warnings have been recorded for this
                        run.
                      </Alert>
                    ) : (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Showing {workflowWarnings.length} warning(s) on page{" "}
                          {warningPageIndex + 1}.
                        </Typography>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          alignItems={{ xs: "stretch", sm: "center" }}
                          justifyContent="space-between"
                        >
                          <TextField
                            select
                            size="small"
                            label="Rows per page"
                            value={warningRowsPerPage}
                            onChange={(event) => {
                              setIsWarningListLoading(true);
                              setWarningRowsPerPage(Number(event.target.value));
                            }}
                            sx={{ minWidth: 160 }}
                          >
                            {[5, 10, 25, 50].map((pageSize) => (
                              <MenuItem key={pageSize} value={pageSize}>
                                {pageSize}
                              </MenuItem>
                            ))}
                          </TextField>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setIsWarningListLoading(true);
                                setWarningPageIndex((currentPage) =>
                                  Math.max(0, currentPage - 1),
                                );
                              }}
                              disabled={
                                warningPageIndex === 0 ||
                                warningsQuery.isFetching
                              }
                            >
                              Previous
                            </Button>
                            <Typography variant="body2" color="text.secondary">
                              Page {warningPageIndex + 1}
                            </Typography>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                const nextCursor = warningPage?.nextCursor;
                                if (!nextCursor) {
                                  return;
                                }

                                setIsWarningListLoading(true);
                                setWarningCursors((currentCursors) => {
                                  const nextCursors = currentCursors.slice(
                                    0,
                                    warningPageIndex + 1,
                                  );
                                  nextCursors.push(nextCursor);
                                  return nextCursors;
                                });
                                setWarningPageIndex(
                                  (currentPage) => currentPage + 1,
                                );
                              }}
                              disabled={
                                !hasNextWarningPage || warningsQuery.isFetching
                              }
                            >
                              Next
                            </Button>
                          </Stack>
                        </Stack>

                        {isWarningContentLoading ? (
                          <TableSectionSkeleton columnCount={7} />
                        ) : (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Severity</TableCell>
                                <TableCell>Stage</TableCell>
                                <TableCell>Code</TableCell>
                                <TableCell>Last Seen</TableCell>
                                <TableCell>Count</TableCell>
                                <TableCell>Entity</TableCell>
                                <TableCell>Message</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {workflowWarnings.map((warning) => (
                                <TableRow key={warning.warningId}>
                                  <TableCell>{warning.severity}</TableCell>
                                  <TableCell>{warning.stage}</TableCell>
                                  <TableCell>{warning.code}</TableCell>
                                  <TableCell>
                                    {formatTimestamp(warning.lastSeenAt)}
                                  </TableCell>
                                  <TableCell>
                                    {warning.occurrenceCount}
                                  </TableCell>
                                  <TableCell>
                                    {warning.entityId
                                      ? `${warning.entityType ?? "ENTITY"} ${warning.entityId}`
                                      : "—"}
                                  </TableCell>
                                  <TableCell>{warning.message}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </>
                    )}
                  </Stack>
                )}
              </WarningSummary>

              {workspaceRun &&
              (hasValidationBlockers || hasAttemptedValidationBlockers) ? (
                <Card
                  id="validation-blockers-anchor"
                  variant="outlined"
                  sx={{ borderRadius: 3, borderColor: "warning.main" }}
                >
                  <CardContent>
                    <Stack spacing={2}>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ fontFamily: displayFontFamily }}
                        >
                          Validation Blockers
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.75 }}
                        >
                          {hasValidationBlockers
                            ? `${validationBlockers.length} blocker(s) currently make the derived canonical snapshot invalid. Repair the affected approved tags or synthesized parents here before publishing.`
                            : attemptedValidationSummary ??
                              (attemptedValidationContext?.kind ===
                              "pending-proposal"
                                ? `${attemptedValidationBlockers.length} blocker(s) would be introduced if you approved this proposal as currently edited. Resolve them here or go back and change the proposal.`
                                : attemptedValidationContext?.kind ===
                                    "pending-hierarchy"
                                  ? `${attemptedValidationBlockers.length} blocker(s) would be introduced if you approved this hierarchy recommendation as currently edited. Resolve them here or go back and change the recommendation.`
                                  : `${attemptedValidationBlockers.length} blocker(s) would be introduced by the last attempted approval or edit. Review the proposed conflicts here before trying again.`)}
                        </Typography>
                      </Box>

                      <Stack spacing={2}>
                        {visibleValidationBlockers.map(
                          (blocker, blockerIndex) => (
                            <Card
                              key={`${blocker.issue.code}-${blockerIndex}`}
                              variant="outlined"
                              sx={{ borderRadius: 3 }}
                            >
                              <CardContent>
                                <Stack spacing={2}>
                                  <Box>
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      flexWrap="wrap"
                                      useFlexGap
                                      alignItems="center"
                                    >
                                      <Chip
                                        size="small"
                                        color="warning"
                                        label={blocker.issue.code}
                                      />
                                      {blocker.issue.affectedCanonicalKeys.map(
                                        (canonicalKey) => (
                                          <Chip
                                            key={canonicalKey}
                                            size="small"
                                            variant="outlined"
                                            label={canonicalKey}
                                          />
                                        ),
                                      )}
                                    </Stack>
                                    <Typography sx={{ mt: 1.25 }}>
                                      {blocker.issue.message}
                                    </Typography>
                                  </Box>

                                  {blocker.sources.length > 0 ? (
                                    <Stack spacing={1.5}>
                                      {blocker.sources.map((source) => {
                                        const repairOptions =
                                          getValidationBlockerRepairOptions({
                                            blocker,
                                            source,
                                          });
                                        const isAttemptedPendingProposalSource =
                                          attemptedValidationContext?.kind ===
                                            "pending-proposal" &&
                                          source.proposalId ===
                                            attemptedValidationContext.proposalId;
                                        const isAttemptedPendingHierarchySource =
                                          attemptedValidationContext?.kind ===
                                            "pending-hierarchy" &&
                                          source.hierarchyProposalId ===
                                            attemptedValidationContext.hierarchyProposalId;
                                        const isAttemptedPendingSource =
                                          isAttemptedPendingProposalSource ||
                                          isAttemptedPendingHierarchySource;
                                        const isDuplicateBlocker =
                                          isDuplicateValidationIssue(blocker);
                                        const keepActionKey = `${getValidationBlockerSourceKey(source)}:keep`;
                                        const isKeepingDuplicateSource =
                                          activeValidationRepairActionKey ===
                                          keepActionKey;
                                        const duplicateKeepLabel =
                                          isAttemptedPendingProposalSource
                                            ? "Keep Proposal"
                                            : isAttemptedPendingHierarchySource
                                              ? "Keep Recommendation"
                                              : source.sourceType ===
                                                  "APPROVED_SYNTHESIZED_HIERARCHY_PARENT"
                                                ? "Keep Synthesized Parent"
                                                : "Keep This Tag";
                                        const sourceGuidance =
                                          isAttemptedPendingProposalSource
                                            ? isDuplicateBlocker
                                              ? "Keep this proposal to approve it and reject the conflicting duplicate source(s), or edit the proposal card and try again."
                                              : "This proposal is still pending. Edit it on the proposal card, then approve it again once the blocker is resolved."
                                            : isAttemptedPendingHierarchySource
                                              ? isDuplicateBlocker
                                                ? "Keep this recommendation to approve it and reject the conflicting duplicate source(s), or reject this recommendation if the existing definition should win."
                                                : "This hierarchy recommendation is still pending. Edit it on the recommendation card, then approve it again once the blocker is resolved."
                                              : repairOptions.guidance;

                                        return (
                                          <Card
                                            key={`${source.sourceType}-${source.proposalId ?? source.hierarchyProposalId ?? source.tag.slug}`}
                                            variant="outlined"
                                            sx={{ borderRadius: 2 }}
                                          >
                                            <CardContent>
                                              <Stack spacing={1.5}>
                                                <Box>
                                                  <Typography variant="subtitle2">
                                                    {source.tag.layer}:
                                                    {source.tag.slug}
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                  >
                                                    {source.tag.name}
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mt: 0.5 }}
                                                  >
                                                    {source.tag.description}
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mt: 1 }}
                                                  >
                                                    {sourceGuidance}
                                                  </Typography>
                                                </Box>

                                                <Stack
                                                  direction={{
                                                    xs: "column",
                                                    sm: "row",
                                                  }}
                                                  spacing={1}
                                                  flexWrap="wrap"
                                                  useFlexGap
                                                >
                                                  {isDuplicateBlocker ? (
                                                    <Button
                                                      variant="contained"
                                                      disabled={
                                                        isValidationRepairMutationLoading &&
                                                        !isKeepingDuplicateSource
                                                      }
                                                      startIcon={
                                                        isKeepingDuplicateSource ? (
                                                          <CircularProgress
                                                            size={16}
                                                            color="inherit"
                                                          />
                                                        ) : undefined
                                                      }
                                                      onClick={() =>
                                                        void keepDuplicateValidationSource(
                                                          {
                                                            blocker,
                                                            source,
                                                          },
                                                        )
                                                      }
                                                    >
                                                      {duplicateKeepLabel}
                                                    </Button>
                                                  ) : null}
                                                  {isAttemptedPendingProposalSource &&
                                                  source.proposalId ? (
                                                    <Button
                                                      variant="outlined"
                                                      disabled={
                                                        isValidationRepairMutationLoading
                                                      }
                                                      onClick={() =>
                                                        focusProposalCard(
                                                          source.proposalId!,
                                                        )
                                                      }
                                                    >
                                                      Edit Proposal
                                                    </Button>
                                                  ) : null}
                                                  {isAttemptedPendingHierarchySource &&
                                                  source.hierarchyProposalId ? (
                                                    <Button
                                                      variant="outlined"
                                                      disabled={
                                                        isValidationRepairMutationLoading
                                                      }
                                                      onClick={() =>
                                                        focusHierarchyProposalCard(
                                                          source.hierarchyProposalId!,
                                                        )
                                                      }
                                                    >
                                                      Edit Recommendation
                                                    </Button>
                                                  ) : null}
                                                  {isAttemptedPendingProposalSource &&
                                                  source.proposalId ? (
                                                    <Button
                                                      color="warning"
                                                      variant="outlined"
                                                      disabled={
                                                        isValidationRepairMutationLoading
                                                      }
                                                      onClick={() =>
                                                        void rejectCanonicalProposal(
                                                          {
                                                            proposalId:
                                                              source.proposalId!,
                                                          },
                                                        )
                                                      }
                                                    >
                                                      Reject Proposal
                                                    </Button>
                                                  ) : null}
                                                  {isAttemptedPendingHierarchySource &&
                                                  source.hierarchyProposalId ? (
                                                    <Button
                                                      color="warning"
                                                      variant="outlined"
                                                      disabled={
                                                        isValidationRepairMutationLoading
                                                      }
                                                      onClick={() =>
                                                        void rejectCanonicalHierarchyProposal(
                                                          {
                                                            hierarchyProposalId:
                                                              source.hierarchyProposalId!,
                                                          },
                                                        )
                                                      }
                                                    >
                                                      Reject Recommendation
                                                    </Button>
                                                  ) : null}
                                                  {!isAttemptedPendingSource &&
                                                  repairOptions.canEditApprovedTag ? (
                                                    <Button
                                                      variant="outlined"
                                                      disabled={
                                                        isValidationRepairMutationLoading
                                                      }
                                                      onClick={() =>
                                                        openApprovedTagRepair(
                                                          source,
                                                        )
                                                      }
                                                    >
                                                      Edit Approved Tag
                                                    </Button>
                                                  ) : null}
                                                  {!isAttemptedPendingSource &&
                                                  repairOptions.canEditParents ? (
                                                    <Button
                                                      variant="outlined"
                                                      disabled={
                                                        isValidationRepairMutationLoading
                                                      }
                                                      onClick={() =>
                                                        openApprovedTagHierarchyRepair(
                                                          source,
                                                        )
                                                      }
                                                    >
                                                      Edit Parents
                                                    </Button>
                                                  ) : null}
                                                  {!isAttemptedPendingSource &&
                                                  repairOptions.canRejectApprovedProposal &&
                                                  source.proposalId ? (
                                                    <Button
                                                      color="warning"
                                                      variant="outlined"
                                                      disabled={
                                                        isValidationRepairMutationLoading
                                                      }
                                                      onClick={() =>
                                                        void rejectCanonicalProposal(
                                                          {
                                                            proposalId:
                                                              source.proposalId!,
                                                          },
                                                        )
                                                      }
                                                    >
                                                      Reject Approved Proposal
                                                    </Button>
                                                  ) : null}
                                                  {!isAttemptedPendingSource &&
                                                  repairOptions.canEditSynthesizedParent ? (
                                                    <Button
                                                      variant="outlined"
                                                      disabled={
                                                        isValidationRepairMutationLoading
                                                      }
                                                      onClick={() =>
                                                        openSynthesizedParentRepair(
                                                          source,
                                                        )
                                                      }
                                                    >
                                                      Edit Synthesized Parent
                                                    </Button>
                                                  ) : null}
                                                  {!isAttemptedPendingSource &&
                                                  repairOptions.canRejectApprovedHierarchy &&
                                                  source.hierarchyProposalId ? (
                                                    <Button
                                                      color="warning"
                                                      variant="outlined"
                                                      disabled={
                                                        isValidationRepairMutationLoading
                                                      }
                                                      onClick={() =>
                                                        void rejectCanonicalHierarchyProposal(
                                                          {
                                                            hierarchyProposalId:
                                                              source.hierarchyProposalId!,
                                                          },
                                                        )
                                                      }
                                                    >
                                                      Reject Approved Hierarchy
                                                    </Button>
                                                  ) : null}
                                                </Stack>
                                              </Stack>
                                            </CardContent>
                                          </Card>
                                        );
                                      })}
                                    </Stack>
                                  ) : (
                                    <Alert severity="info">
                                      No repair source was mapped for this
                                      blocker automatically. Review the approved
                                      tag diagnostics below to fix it manually.
                                    </Alert>
                                  )}
                                </Stack>
                              </CardContent>
                            </Card>
                          ),
                        )}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ) : null}

              <Stack spacing={2.5}>
                <WorkflowStageCard
                  id={STAGE_CARD_ID.configure}
                  title="Configure & Start"
                  description="Choose the bootstrap scope and proposal model, then start a fresh workspace."
                  state={stageDefinitions[0].state}
                  summary={stageDefinitions[0].summary}
                  expanded={expandedStageIds.includes("configure")}
                  onToggle={() => toggleStage("configure")}
                >
                  <Stack spacing={2}>
                    <Alert severity={workspaceRun ? "info" : "success"}>
                      {hasActiveCleanupJob
                        ? `Cleanup job ${latestCleanupJob?.jobId} is ${latestCleanupJob?.jobStatus.toLowerCase()}. Start stays locked until cleanup finishes.`
                        : workspaceRun
                          ? `Workspace ${workspaceRun.runId} already exists. Start stays locked until the workspace is cleaned.`
                          : "No workspace exists. Configure the run and start when ready."}
                    </Alert>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={2}
                      alignItems={{ xs: "stretch", md: "center" }}
                    >
                      <TextField
                        select
                        label="Bootstrap scope"
                        size="small"
                        value={
                          workspaceRun ? displayedBootstrapMode : bootstrapMode
                        }
                        onChange={(event) =>
                          setBootstrapMode(event.target.value as BootstrapMode)
                        }
                        disabled={Boolean(workspaceRun) || hasActiveCleanupJob}
                        helperText="Run the full Tagger dataset or limit the scan for a smoke test."
                        sx={{ minWidth: 240 }}
                      >
                        <MenuItem value="FULL">Full Tagger dataset</MenuItem>
                        <MenuItem value="LIMITED">Limited pages</MenuItem>
                      </TextField>
                      <TextField
                        select
                        label="Proposal model"
                        size="small"
                        value={displayedProposalModelId}
                        onChange={(event) =>
                          setProposalModelId(
                            event.target.value as CanonicalBootstrapModelId,
                          )
                        }
                        disabled={Boolean(workspaceRun) || hasActiveCleanupJob}
                        helperText="Persisted on the run and used later during proposal generation."
                        sx={{ minWidth: 220 }}
                      >
                        {CanonicalBootstrapModelIdValues.map((modelId) => (
                          <MenuItem key={modelId} value={modelId}>
                            {BOOTSTRAP_MODEL_LABELS[modelId]}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        label="Page limit"
                        size="small"
                        type="number"
                        value={displayedPageLimit}
                        onChange={(event) => setPageLimit(event.target.value)}
                        disabled={
                          Boolean(workspaceRun) ||
                          hasActiveCleanupJob ||
                          (workspaceRun
                            ? displayedBootstrapMode === "FULL"
                            : bootstrapMode === "FULL")
                        }
                        helperText={
                          (workspaceRun
                            ? displayedBootstrapMode
                            : bootstrapMode) === "FULL"
                            ? "Ignored for full bootstrap runs."
                            : "Use a small page limit for smoke tests."
                        }
                        sx={{ maxWidth: 220 }}
                        inputProps={{ min: 1, max: 150 }}
                      />
                      <Stack spacing={0.25} sx={{ minWidth: 280 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={displayedCaptureLlmDebugArtifacts}
                              onChange={(event) =>
                                setCaptureLlmDebugArtifacts(
                                  event.target.checked,
                                )
                              }
                              disabled={
                                Boolean(workspaceRun) || hasActiveCleanupJob
                              }
                            />
                          }
                          label="Capture LLM debug artifacts"
                          sx={{ mr: 0 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Store prompt and output audit blobs in S3 and link
                          them from immutable usage events.
                        </Typography>
                      </Stack>
                      <Button
                        variant="contained"
                        onClick={() => void startBootstrap()}
                        disabled={
                          startBootstrapMutation.isLoading ||
                          cleanupWorkspaceMutation.isLoading ||
                          isNewRunBlocked
                        }
                        startIcon={
                          startBootstrapMutation.isLoading ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : undefined
                        }
                      >
                        Start Bootstrap
                      </Button>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveCleanupJob
                        ? "Workspace cleanup is in progress. Starting another bootstrap stays locked until cleanup completes."
                        : workspaceRun
                          ? "Clean the current workspace before starting another bootstrap run."
                          : "Starting a bootstrap creates a fresh workspace and begins the async scan."}
                    </Typography>
                  </Stack>
                </WorkflowStageCard>

                <WorkflowStageCard
                  id={STAGE_CARD_ID.scan}
                  title="Scan Source Tags"
                  description="Read Tagger search pages and write relevant source-tag candidates into the workspace."
                  state={stageDefinitions[1].state}
                  summary={stageDefinitions[1].summary}
                  expanded={expandedStageIds.includes("scan")}
                  onToggle={() => toggleStage("scan")}
                >
                  <Stack spacing={2}>
                    <Alert
                      severity={
                        workspaceRun?.runStatus === TaxonomyRunStatus.PENDING ||
                        !hasCompletedPageScans
                          ? "info"
                          : workspaceRun
                            ? "success"
                            : "info"
                      }
                    >
                      {!workspaceRun
                        ? "Start a bootstrap run to begin scanning source tags."
                        : workspaceRun.runStatus ===
                              TaxonomyRunStatus.PENDING ||
                            !hasCompletedPageScans
                          ? `Scanning is running. ${getRunPageScanSummary(
                              workspaceRun,
                            )} scan batches completed so far.`
                          : `Scanning is complete for this workspace. ${getRunPageScanSummary(
                              workspaceRun,
                            )} scan batches completed.`}
                    </Alert>
                    {workspaceRun ? (
                      <Stack spacing={1}>
                        <LinearProgress
                          variant={
                            getRunMetric(workspaceRun, "pageScanQueuedCount") >
                            0
                              ? "determinate"
                              : "indeterminate"
                          }
                          value={buildProgressValue(
                            getRunMetric(
                              workspaceRun,
                              "pageScanCompletedCount",
                            ),
                            getRunMetric(workspaceRun, "pageScanQueuedCount"),
                          )}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Scan batches {getRunPageScanSummary(workspaceRun)}.
                        </Typography>
                      </Stack>
                    ) : null}
                  </Stack>
                </WorkflowStageCard>

                <WorkflowStageCard
                  id={STAGE_CARD_ID.detail}
                  title="Build Evidence"
                  description="Fetch source-tag detail, exemplar cards, and ancestry data so proposal generation has evidence-rich input."
                  state={stageDefinitions[2].state}
                  summary={stageDefinitions[2].summary}
                  expanded={expandedStageIds.includes("detail")}
                  onToggle={() => toggleStage("detail")}
                >
                  <Stack spacing={2}>
                    <Alert
                      severity={
                        detailFailureMessage
                          ? "error"
                          : workspaceRun?.runStatus ===
                              TaxonomyRunStatus.DETAILING
                            ? "info"
                            : workspaceRun
                              ? "success"
                              : "info"
                      }
                    >
                      {!workspaceRun
                        ? "Start a bootstrap run to begin evidence enrichment."
                        : detailFailureMessage
                          ? `Evidence building failed. ${detailFailureMessage}`
                          : workspaceRun.runStatus ===
                              TaxonomyRunStatus.DETAILING
                            ? `Evidence building is active. ${getRunDetailSummary(
                                workspaceRun,
                              )} detail messages completed so far.`
                            : `Evidence building is complete for this workspace. ${getRunDetailSummary(
                                workspaceRun,
                              )} detail messages completed.`}
                    </Alert>
                    {workspaceRun ? (
                      <Stack spacing={1}>
                        <LinearProgress
                          variant={
                            getRunMetric(workspaceRun, "detailQueuedCount") > 0
                              ? "determinate"
                              : "indeterminate"
                          }
                          value={buildProgressValue(
                            getRunMetric(workspaceRun, "detailCompletedCount"),
                            getRunMetric(workspaceRun, "detailQueuedCount"),
                          )}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Detail work {getRunDetailSummary(workspaceRun)}.
                        </Typography>
                      </Stack>
                    ) : null}
                    {detailWarningCount > 0 ? (
                      <Alert severity="warning">
                        {detailWarningCount} warning(s) from the current warning
                        page are tied to evidence/detail work. Review warnings
                        if exemplar text or source enrichment quality matters
                        before proposal generation.
                      </Alert>
                    ) : null}
                  </Stack>
                </WorkflowStageCard>

                <WorkflowStageCard
                  id={STAGE_CARD_ID["generate-proposals"]}
                  title="Generate Proposals"
                  description="Run canonical tag proposal generation after scan and detail work are complete."
                  state={stageDefinitions[3].state}
                  summary={stageDefinitions[3].summary}
                  expanded={expandedStageIds.includes("generate-proposals")}
                  onToggle={() => toggleStage("generate-proposals")}
                >
                  <Stack spacing={2}>
                    {!workspaceRun ? (
                      <Alert severity="info">
                        Proposal generation unlocks after a bootstrap run
                        exists.
                      </Alert>
                    ) : workspaceRun.runStatus ===
                      TaxonomyRunStatus.PROPOSING ? (
                      <Alert
                        severity={
                          hasStalledProposalGeneration ? "warning" : "info"
                        }
                      >
                        {hasStalledProposalGeneration
                          ? isProposalKickoffPreparing(workspaceRun)
                            ? `Proposal generation appears stalled while preparing proposal work. The run last updated at ${formatTimestamp(
                                workspaceRun.updatedAt,
                              )} before any proposal chunks were queued. Resume proposal generation to requeue kickoff work.`
                            : `Proposal generation appears stalled. The run last updated at ${formatTimestamp(
                                workspaceRun.updatedAt,
                              )} after proposal work ${getRunProposalSummary(
                                workspaceRun,
                              )}; proposals created ${getRunMetric(
                                workspaceRun,
                                "proposalCreatedCount",
                              )}; NO_TAG decisions ${getRunMetric(
                                workspaceRun,
                                "proposalNoTagCount",
                              )}. Resume proposal generation to requeue missing work.`
                          : isProposalKickoffPreparing(workspaceRun)
                            ? "Proposal generation is preparing proposal work. Clusters are being rebuilt and async proposal chunks will queue shortly."
                            : `Proposal generation is running. Proposal work ${getRunProposalSummary(
                                workspaceRun,
                              )}; proposals created ${getRunMetric(
                                workspaceRun,
                                "proposalCreatedCount",
                              )}; NO_TAG decisions ${getRunMetric(
                                workspaceRun,
                                "proposalNoTagCount",
                              )}.`}
                      </Alert>
                    ) : !hasProposalGenerationStarted ? (
                      <Alert severity="info">
                        Scan and detail work are complete. Start proposal
                        generation to create reviewable canonical tags.
                      </Alert>
                    ) : (
                      <Alert severity="success">
                        Proposal generation has completed for this workspace.
                      </Alert>
                    )}
                    {workspaceRun ? (
                      <Stack spacing={1}>
                        <LinearProgress
                          variant={
                            workspaceRun.runStatus ===
                              TaxonomyRunStatus.PROPOSING &&
                            isProposalKickoffPreparing(workspaceRun)
                              ? "indeterminate"
                              : workspaceRun.runStatus ===
                                  TaxonomyRunStatus.PROPOSING
                                ? "determinate"
                                : "determinate"
                          }
                          value={buildProgressValue(
                            getRunMetric(
                              workspaceRun,
                              "proposalCompletedCount",
                            ),
                            getRunMetric(workspaceRun, "proposalQueuedCount"),
                          )}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {isProposalKickoffPreparing(workspaceRun)
                            ? "Preparing proposal work."
                            : `Proposal work ${getRunProposalSummary(
                                workspaceRun,
                              )}. Created ${getRunMetric(
                                workspaceRun,
                                "proposalCreatedCount",
                              )} proposals and ${getRunMetric(
                                workspaceRun,
                                "proposalNoTagCount",
                              )} NO_TAG decisions.`}
                        </Typography>
                      </Stack>
                    ) : null}
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                    >
                      <Button
                        variant="contained"
                        onClick={() => void generateCanonicalProposals()}
                        disabled={!canGenerateProposals}
                        startIcon={
                          generateCanonicalProposalsMutation.isLoading ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : undefined
                        }
                      >
                        {hasStalledProposalGeneration
                          ? "Resume Proposal Generation"
                          : "Start Proposal Generation"}
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        {canGenerateProposals
                          ? hasStalledProposalGeneration
                            ? "The current proposal run appears stalled. This action requeues the missing proposal work for the same run."
                            : "This is the next workflow step once scan and detail work finish."
                          : workspaceRun?.runStatus ===
                              TaxonomyRunStatus.PROPOSING
                            ? hasStalledProposalGeneration
                              ? "The run appears stalled, but resume is currently unavailable while another proposal request is in flight."
                              : "The async proposal worker is already running."
                            : hasProposalGenerationStarted
                              ? "Proposal generation has already completed for this workspace. Review the generated proposals instead of starting it again."
                              : "Proposal generation is only available while the workspace is awaiting review and has not started generating proposals yet."}
                      </Typography>
                    </Stack>
                  </Stack>
                </WorkflowStageCard>

                <WorkflowStageCard
                  id={STAGE_CARD_ID["review-proposals"]}
                  title="Review Proposals"
                  description="Search, export, edit, approve, or reject canonical tag proposals."
                  state={stageDefinitions[4].state}
                  summary={stageDefinitions[4].summary}
                  expanded={expandedStageIds.includes("review-proposals")}
                  onToggle={() => toggleStage("review-proposals")}
                >
                  <Stack spacing={2}>
                    {pendingProposalsQuery.error ? (
                      <Alert severity="error">
                        {pendingProposalsQuery.error.message}
                      </Alert>
                    ) : !workspaceRun ? (
                      <Alert severity="info">
                        Start a bootstrap run before reviewing proposals.
                      </Alert>
                    ) : workspaceRun.runStatus ===
                      TaxonomyRunStatus.PROPOSING ? (
                      <Alert severity="info">
                        Proposal generation is still running. New proposals will
                        appear here as they are created.
                      </Alert>
                    ) : latestProposalApprovalJob?.jobStatus ===
                      TaxonomyApprovalJobStatus.FAILED ? (
                      <Alert severity="error">
                        Bulk proposal approval failed:{" "}
                        {latestProposalApprovalJob.failureReason ??
                          "No failure reason was recorded."}
                      </Alert>
                    ) : latestProposalApprovalJob?.jobStatus ===
                        TaxonomyApprovalJobStatus.COMPLETED &&
                      (latestProposalApprovalJob.skippedInvalidCount ?? 0) >
                        0 &&
                      hasPendingProposals ? (
                      <Alert severity="warning">
                        Bulk proposal approval finished, but{" "}
                        {latestProposalApprovalJob.skippedInvalidCount}{" "}
                        proposal(s) were skipped because approving them would
                        make the canonical snapshot invalid. Approve a proposal
                        individually to inspect the exact blocker details.
                      </Alert>
                    ) : hasActiveProposalApprovalJob ? (
                      <Alert
                        severity={
                          hasStalledProposalApprovalJob ? "warning" : "info"
                        }
                      >
                        {hasStalledProposalApprovalJob
                          ? `Bulk proposal approval appears stalled. ${latestProposalApprovalJob?.completedCount ?? 0} of ${latestProposalApprovalJob?.queuedCount ?? 0} proposal(s) have been processed. Retry Approve Remaining to resume the pending job items.`
                          : `Bulk proposal approval is ${latestProposalApprovalJob?.jobStatus.toLowerCase()}. ${latestProposalApprovalJob?.completedCount ?? 0} of ${latestProposalApprovalJob?.queuedCount ?? 0} proposal(s) have been applied.`}
                      </Alert>
                    ) : !hasProposalGenerationStarted ? (
                      <Alert severity="info">
                        Generate canonical proposals to open this review queue.
                      </Alert>
                    ) : hasPendingProposals ? (
                      <Alert severity="warning">
                        {workspacePendingProposalCount} proposal(s) remain in
                        the canonical review queue.
                      </Alert>
                    ) : (
                      <Alert severity="success">
                        Proposal review is complete. No canonical proposals
                        remain pending.
                      </Alert>
                    )}

                    {hasPendingProposals ? (
                      <Stack spacing={2}>
                        {proposalDuplicateGroupsQuery.error ? (
                          <Alert severity="error">
                            {proposalDuplicateGroupsQuery.error.message}
                          </Alert>
                        ) : isProposalDuplicateAnalysisLoading ? (
                          <Alert severity="info">
                            Analyzing likely duplicate proposal concepts before
                            bulk approval is enabled.
                          </Alert>
                        ) : null}

                        {hasProposalDuplicateGroups ? (
                          <Stack
                            id="proposal-duplicate-groups-anchor"
                            spacing={2}
                          >
                            <Alert severity="warning">
                              {proposalDuplicateGroups.length} likely duplicate
                              proposal group(s) need resolution before bulk
                              proposal approval is safe.
                            </Alert>
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1.5}
                              alignItems={{ xs: "stretch", sm: "center" }}
                            >
                              <Button
                                variant="contained"
                                color="warning"
                                onClick={() =>
                                  void approveAllProposalDuplicateFixes()
                                }
                                disabled={
                                  activeProposalDuplicateGroupId != null ||
                                  approveCanonicalProposalDuplicateFixMutation.isLoading ||
                                  approveAllCanonicalProposalDuplicateFixesMutation.isLoading ||
                                  hasActiveProposalApprovalJob ||
                                  isApprovingRemainingProposalQueue
                                }
                                startIcon={
                                  activeProposalDuplicateGroupId ===
                                  "__all__" ? (
                                    <CircularProgress
                                      size={16}
                                      color="inherit"
                                    />
                                  ) : undefined
                                }
                              >
                                Approve All Recommended Fixes (
                                {proposalDuplicateGroups.length})
                              </Button>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                This keeps the recommended survivor in each
                                duplicate group and rejects the duplicate
                                proposal(s) before normal approval continues.
                                Use Remove on any pending item to send it back
                                to normal proposal review instead.
                              </Typography>
                            </Stack>
                            <Stack spacing={2}>
                              {proposalDuplicateGroups.map((group) => (
                                <DuplicateGroupCard
                                  key={group.groupId}
                                  group={group}
                                  isBusy={
                                    activeProposalDuplicateGroupId ===
                                      group.groupId ||
                                    approveCanonicalProposalDuplicateFixMutation.isLoading ||
                                    approveAllCanonicalProposalDuplicateFixesMutation.isLoading
                                  }
                                  isApplying={
                                    activeProposalDuplicateGroupId ===
                                    group.groupId
                                  }
                                  onApply={() =>
                                    void approveProposalDuplicateFix(group)
                                  }
                                  onRemoveSource={(source) =>
                                    removeProposalDuplicateSourceFromBlockerFlow(
                                      source,
                                    )
                                  }
                                  onFocusSource={focusProposalDuplicateSource}
                                />
                              ))}
                            </Stack>
                          </Stack>
                        ) : null}

                        <Box
                          sx={{
                            display: "grid",
                            gap: 2,
                            gridTemplateColumns: {
                              xs: "1fr",
                              xl: "minmax(0, 360px) auto auto",
                            },
                          }}
                        >
                          <TextField
                            size="small"
                            label="Search awaiting approvals"
                            value={proposalSearch}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              setProposalSearch(nextValue);
                              setIsProposalListLoading(true);
                            }}
                            helperText={
                              isProposalReviewContentLoading
                                ? "Loading proposal queue..."
                                : proposalSearchQuery
                                  ? `Showing ${pendingProposals.length} search result(s) on page ${proposalReviewPageIndex + 1}.`
                                  : `Showing ${pendingProposals.length} proposal(s) on page ${proposalReviewPageIndex + 1}.`
                            }
                          />
                          <Button
                            variant="outlined"
                            onClick={() =>
                              void downloadAwaitingApprovalReview()
                            }
                            disabled={
                              !hasPendingProposals ||
                              isExportingProposalReview ||
                              isApprovingRemainingProposalQueue ||
                              hasActiveProposalApprovalJob
                            }
                            startIcon={
                              isExportingProposalReview ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : undefined
                            }
                          >
                            Export Awaiting Approvals
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => void approveRemainingProposals()}
                            disabled={!canApproveRemaining}
                            startIcon={
                              isApprovingRemainingProposalQueue ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : undefined
                            }
                          >
                            {hasStalledProposalApprovalJob
                              ? `Resume Bulk Approval (${workspacePendingProposalCount})`
                              : `Approve Remaining (${workspacePendingProposalCount})`}
                          </Button>
                        </Box>

                        <Alert
                          severity={
                            invalidPendingProposalCount > 0 ? "warning" : "info"
                          }
                        >
                          Pending in workspace: {workspacePendingProposalCount}.
                          Search results on this page: {pendingProposals.length}
                          .
                          {hasActiveProposalApprovalJob
                            ? hasStalledProposalApprovalJob
                              ? ` Bulk approval appears stalled. Retry Approve Remaining to resume the pending proposal approvals.`
                              : ` Bulk approval is ${latestProposalApprovalJob?.jobStatus.toLowerCase()} and review controls stay locked until it finishes.`
                            : hasProposalDuplicateGroups
                              ? ` Resolve ${proposalDuplicateGroups.length} duplicate fix group(s) before using Approve Remaining. ${currentPageProposalDuplicateCount} item(s) on this page are part of a duplicate recommendation.`
                              : latestProposalApprovalJob?.jobStatus ===
                                    TaxonomyApprovalJobStatus.COMPLETED &&
                                  (latestProposalApprovalJob.skippedInvalidCount ??
                                    0) > 0
                                ? ` ${latestProposalApprovalJob.skippedInvalidCount} proposal(s) were skipped as invalid in the latest bulk approval run.`
                                : blockedPendingProposalCount > 0
                                  ? ` ${blockedPendingProposalCount} proposal(s) currently shown need a Fix step before they can be approved safely.`
                                  : invalidPendingProposalCount > 0
                                    ? ` ${invalidPendingProposalCount} proposal(s) on this page still need edits before Approve Remaining is safe.`
                                    : " Approve Remaining is available for the current workspace queue."}
                        </Alert>

                        <Stack
                          direction={{ xs: "column", lg: "row" }}
                          spacing={2}
                          alignItems={{ xs: "stretch", lg: "center" }}
                          justifyContent="space-between"
                        >
                          <TextField
                            select
                            size="small"
                            label="Rows per page"
                            value={proposalReviewRowsPerPage}
                            onChange={(event) => {
                              setIsProposalListLoading(true);
                              setProposalReviewRowsPerPage(
                                Number(event.target.value),
                              );
                            }}
                            sx={{ minWidth: 140 }}
                          >
                            {[5, 10, 25, 50].map((pageSize) => (
                              <MenuItem key={pageSize} value={pageSize}>
                                {pageSize}
                              </MenuItem>
                            ))}
                          </TextField>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "stretch", sm: "center" }}
                          >
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setIsProposalListLoading(true);
                                setProposalReviewPageIndex((currentPage) =>
                                  Math.max(0, currentPage - 1),
                                );
                              }}
                              disabled={
                                proposalReviewPageIndex === 0 ||
                                pendingProposalsQuery.isFetching
                              }
                            >
                              Previous
                            </Button>
                            <Typography variant="body2" color="text.secondary">
                              Page {proposalReviewPageIndex + 1}
                            </Typography>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                const nextCursor =
                                  pendingProposalPage?.nextCursor;
                                if (!nextCursor) {
                                  return;
                                }

                                setIsProposalListLoading(true);
                                setProposalReviewCursors((currentCursors) => {
                                  const nextCursors = currentCursors.slice(
                                    0,
                                    proposalReviewPageIndex + 1,
                                  );
                                  nextCursors.push(nextCursor);
                                  return nextCursors;
                                });
                                setProposalReviewPageIndex(
                                  (currentPage) => currentPage + 1,
                                );
                              }}
                              disabled={
                                !hasNextPendingProposalPage ||
                                pendingProposalsQuery.isFetching
                              }
                            >
                              Next
                            </Button>
                          </Stack>
                        </Stack>

                        {isProposalReviewContentLoading ? (
                          <ReviewResultSkeletons />
                        ) : pendingProposals.length === 0 ? (
                          <Alert severity="info">
                            No pending proposals match the current search.
                          </Alert>
                        ) : null}

                        {pendingProposals.map((proposal) => {
                          const draft =
                            proposalDrafts[proposal.proposalId] ??
                            createProposalDraft(proposal);
                          const canApprove =
                            isCanonicalProposalDraftApprovable(draft);
                          const proposalDuplicateGroup =
                            proposalDuplicateGroupByProposalId.get(
                              proposal.proposalId,
                            );
                          const proposalApprovalIssues =
                            proposal.approvalValidationIssues ?? [];
                          const proposalApprovalBlockers =
                            proposal.approvalValidationBlockers ?? [];
                          const draftMatchesLoadedProposal =
                            doesProposalDraftMatchProposal({
                              proposal,
                              draft,
                            });
                          const showFixAction =
                            proposalApprovalIssues.length > 0 &&
                            draftMatchesLoadedProposal;

                          return (
                            <Card
                              id={`proposal-card-${proposal.proposalId}`}
                              key={proposal.proposalId}
                              variant="outlined"
                              sx={{ borderRadius: 3 }}
                            >
                              <CardContent>
                                <Stack spacing={2}>
                                  <Box>
                                    <Typography
                                      variant="h6"
                                      sx={{ fontFamily: displayFontFamily }}
                                    >
                                      {proposal.layer ?? "UNKNOWN"}:
                                      {proposal.slug ?? proposal.clusterLabel}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.75 }}
                                    >
                                      Cluster{" "}
                                      <strong>{proposal.clusterLabel}</strong>
                                      {" · "}Source tags{" "}
                                      {proposal.sourceTagKeys
                                        .map((tag) => tag.rawKey)
                                        .join(", ")}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.75 }}
                                    >
                                      {proposal.reasoningSummary}
                                    </Typography>
                                  </Box>

                                  {proposalApprovalIssues.length > 0 ? (
                                    <Alert severity="warning">
                                      <Typography variant="body2">
                                        {showFixAction
                                          ? "As currently loaded from the workspace, this proposal would be blocked by canonical snapshot validation."
                                          : "This proposal was blocked in its original form. If your edits resolve the conflict, approve it again to validate the updated definition."}
                                      </Typography>
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        flexWrap="wrap"
                                        useFlexGap
                                        sx={{ mt: 1 }}
                                      >
                                        {proposalApprovalIssues.map((issue) => (
                                          <Chip
                                            key={`${proposal.proposalId}-${issue.code}-${issue.affectedCanonicalKeys.join("-")}`}
                                            size="small"
                                            variant="outlined"
                                            color="warning"
                                            label={issue.code}
                                          />
                                        ))}
                                      </Stack>
                                      <Stack spacing={0.75} sx={{ mt: 1 }}>
                                        {proposalApprovalIssues.map((issue) => (
                                          <Typography
                                            key={`${proposal.proposalId}-message-${issue.code}-${issue.affectedCanonicalKeys.join("-")}`}
                                            variant="body2"
                                          >
                                            {issue.message}
                                          </Typography>
                                        ))}
                                      </Stack>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 1 }}
                                      >
                                        {showFixAction
                                          ? proposalApprovalBlockers.length > 0
                                            ? "Use Fix to review the blocker details, or edit the proposal fields below to make the definition valid."
                                            : "Edit the proposal fields below, then approve again."
                                          : "Keep editing the proposal fields below, then approve again."}
                                      </Typography>
                                    </Alert>
                                  ) : null}

                                  {proposalDuplicateGroup ? (
                                    <Alert severity="warning">
                                      <Typography variant="body2">
                                        This proposal is part of a likely
                                        duplicate group.
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.75 }}
                                      >
                                        {
                                          proposalDuplicateGroup.recommendation
                                            .summary
                                        }
                                      </Typography>
                                    </Alert>
                                  ) : null}

                                  <Box
                                    sx={{
                                      display: "grid",
                                      gap: 2,
                                      gridTemplateColumns: {
                                        xs: "1fr",
                                        md: "repeat(2, minmax(0, 1fr))",
                                      },
                                    }}
                                  >
                                    <TextField
                                      select
                                      label="Layer"
                                      size="small"
                                      value={draft.layer}
                                      disabled={hasActiveProposalApprovalJob}
                                      onChange={(event) =>
                                        setProposalDraftField(
                                          proposal.proposalId,
                                          "layer",
                                          event.target.value,
                                        )
                                      }
                                    >
                                      {TagLayerValues.map((layer) => (
                                        <MenuItem key={layer} value={layer}>
                                          {layer}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                    <TextField
                                      label="Slug"
                                      size="small"
                                      value={draft.slug}
                                      disabled={hasActiveProposalApprovalJob}
                                      onChange={(event) =>
                                        setProposalDraftField(
                                          proposal.proposalId,
                                          "slug",
                                          event.target.value,
                                        )
                                      }
                                    />
                                    <TextField
                                      label="Name"
                                      size="small"
                                      value={draft.name}
                                      disabled={hasActiveProposalApprovalJob}
                                      onChange={(event) =>
                                        setProposalDraftField(
                                          proposal.proposalId,
                                          "name",
                                          event.target.value,
                                        )
                                      }
                                    />
                                  </Box>

                                  <TextField
                                    label="Description"
                                    size="small"
                                    multiline
                                    minRows={2}
                                    value={draft.description}
                                    disabled={hasActiveProposalApprovalJob}
                                    onChange={(event) =>
                                      setProposalDraftField(
                                        proposal.proposalId,
                                        "description",
                                        event.target.value,
                                      )
                                    }
                                  />
                                  <TextField
                                    label="Synonyms"
                                    size="small"
                                    value={draft.synonyms}
                                    disabled={hasActiveProposalApprovalJob}
                                    onChange={(event) =>
                                      setProposalDraftField(
                                        proposal.proposalId,
                                        "synonyms",
                                        event.target.value,
                                      )
                                    }
                                    helperText="Comma-separated"
                                  />
                                  <TextField
                                    label="Rejection notes"
                                    size="small"
                                    multiline
                                    minRows={2}
                                    value={draft.rejectionNotes}
                                    disabled={hasActiveProposalApprovalJob}
                                    onChange={(event) =>
                                      setProposalDraftField(
                                        proposal.proposalId,
                                        "rejectionNotes",
                                        event.target.value,
                                      )
                                    }
                                  />

                                  <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1.5}
                                  >
                                    <Button
                                      variant="outlined"
                                      disabled={hasActiveProposalApprovalJob}
                                      onClick={() =>
                                        setProposalDrafts((currentDrafts) => ({
                                          ...currentDrafts,
                                          [proposal.proposalId]:
                                            createProposalDraft(proposal),
                                        }))
                                      }
                                    >
                                      Reset to Proposal
                                    </Button>
                                    {showFixAction ? (
                                      <Button
                                        color="warning"
                                        variant="contained"
                                        disabled={
                                          isApprovingRemainingProposalQueue ||
                                          approveCanonicalProposalMutation.isLoading ||
                                          approveRemainingCanonicalProposalsMutation.isLoading ||
                                          hasActiveProposalApprovalJob ||
                                          rejectCanonicalProposalMutation.isLoading ||
                                          isValidationRepairMutationLoading
                                        }
                                        onClick={() =>
                                          openPendingProposalFix({
                                            proposal,
                                            draft,
                                          })
                                        }
                                      >
                                        Fix
                                      </Button>
                                    ) : proposalDuplicateGroup ? (
                                      <>
                                        <Button
                                          color="warning"
                                          variant="contained"
                                          disabled={
                                            isApprovingRemainingProposalQueue ||
                                            hasActiveProposalApprovalJob ||
                                            approveCanonicalProposalDuplicateFixMutation.isLoading ||
                                            approveAllCanonicalProposalDuplicateFixesMutation.isLoading
                                          }
                                          onClick={() =>
                                            focusElement(
                                              "proposal-duplicate-groups-anchor",
                                              {
                                                stageId: "review-proposals",
                                              },
                                            )
                                          }
                                        >
                                          Resolve Duplicate
                                        </Button>
                                        <Button
                                          color="warning"
                                          variant="outlined"
                                          onClick={() =>
                                            void rejectCanonicalProposal({
                                              proposalId: proposal.proposalId,
                                              notes:
                                                draft.rejectionNotes.trim() ||
                                                undefined,
                                            })
                                          }
                                          disabled={
                                            isApprovingRemainingProposalQueue ||
                                            approveCanonicalProposalMutation.isLoading ||
                                            approveRemainingCanonicalProposalsMutation.isLoading ||
                                            hasActiveProposalApprovalJob ||
                                            rejectCanonicalProposalMutation.isLoading ||
                                            approveCanonicalProposalDuplicateFixMutation.isLoading ||
                                            approveAllCanonicalProposalDuplicateFixesMutation.isLoading
                                          }
                                          startIcon={
                                            activeProposalRejectionId ===
                                            proposal.proposalId ? (
                                              <CircularProgress
                                                size={16}
                                                color="inherit"
                                              />
                                            ) : undefined
                                          }
                                        >
                                          Reject Proposal
                                        </Button>
                                      </>
                                    ) : (
                                      <>
                                        <Button
                                          variant="contained"
                                          onClick={() =>
                                            void approveCanonicalProposal({
                                              proposalId: proposal.proposalId,
                                              approvedTagDraft:
                                                buildApprovedTagDraft({
                                                  proposal,
                                                  draft,
                                                }),
                                            })
                                          }
                                          disabled={
                                            !canApprove ||
                                            isApprovingRemainingProposalQueue ||
                                            approveCanonicalProposalMutation.isLoading ||
                                            approveRemainingCanonicalProposalsMutation.isLoading ||
                                            hasActiveProposalApprovalJob ||
                                            rejectCanonicalProposalMutation.isLoading
                                          }
                                          startIcon={
                                            activeProposalApprovalId ===
                                            proposal.proposalId ? (
                                              <CircularProgress
                                                size={16}
                                                color="inherit"
                                              />
                                            ) : undefined
                                          }
                                        >
                                          Approve Proposal
                                        </Button>
                                        <Button
                                          color="warning"
                                          variant="outlined"
                                          onClick={() =>
                                            void rejectCanonicalProposal({
                                              proposalId: proposal.proposalId,
                                              notes:
                                                draft.rejectionNotes.trim() ||
                                                undefined,
                                            })
                                          }
                                          disabled={
                                            isApprovingRemainingProposalQueue ||
                                            approveCanonicalProposalMutation.isLoading ||
                                            approveRemainingCanonicalProposalsMutation.isLoading ||
                                            hasActiveProposalApprovalJob ||
                                            rejectCanonicalProposalMutation.isLoading
                                          }
                                          startIcon={
                                            activeProposalRejectionId ===
                                            proposal.proposalId ? (
                                              <CircularProgress
                                                size={16}
                                                color="inherit"
                                              />
                                            ) : undefined
                                          }
                                        >
                                          Reject Proposal
                                        </Button>
                                      </>
                                    )}
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Stack>
                    ) : null}
                  </Stack>
                </WorkflowStageCard>

                <WorkflowStageCard
                  id={STAGE_CARD_ID["generate-hierarchy"]}
                  title="Generate Hierarchy"
                  description="Create parent-link recommendations and synthesized parent tags after flat canonical tag approval is complete."
                  state={stageDefinitions[5].state}
                  summary={stageDefinitions[5].summary}
                  expanded={expandedStageIds.includes("generate-hierarchy")}
                  onToggle={() => toggleStage("generate-hierarchy")}
                >
                  <Stack spacing={2}>
                    {!workspaceRun ? (
                      <Alert severity="info">
                        Start and review a canonical bootstrap run before
                        generating hierarchy.
                      </Alert>
                    ) : hasPendingProposals ? (
                      <Alert severity="info">
                        Finish canonical proposal review before hierarchy
                        generation is available.
                      </Alert>
                    ) : workspaceRun.runStatus ===
                      TaxonomyRunStatus.GENERATING_HIERARCHY ? (
                      hasCurrentHierarchyProgressMetrics ? (
                        <Stack spacing={2}>
                          <Card
                            variant="outlined"
                            sx={{
                              borderRadius: 3,
                              borderColor: hasStalledHierarchyGeneration
                                ? "warning.main"
                                : "primary.main",
                            }}
                          >
                            <CardContent>
                              <Stack spacing={2}>
                                <Box>
                                  <Typography
                                    variant="h6"
                                    sx={{ fontFamily: displayFontFamily }}
                                  >
                                    Hierarchy run{" "}
                                    {latestHierarchyRun?.runNumber} is
                                    generating
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.75 }}
                                  >
                                    {latestHierarchyTargetQueuedCount > 0
                                      ? `Processed ${latestHierarchyTargetCompletedCount} of ${latestHierarchyTargetQueuedCount} approved tags in this pass.`
                                      : "No approved tags to process in this pass."}
                                  </Typography>
                                </Box>

                                {latestHierarchyTargetQueuedCount > 0 ? (
                                  <Box>
                                    <LinearProgress
                                      variant="determinate"
                                      value={latestHierarchyTargetProgressValue}
                                      sx={{ borderRadius: 999 }}
                                    />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 1 }}
                                    >
                                      Completed{" "}
                                      {latestHierarchyBatchCompletedCount} of{" "}
                                      {latestHierarchyBatchQueuedCount} batches.
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Completed{" "}
                                    {latestHierarchyBatchCompletedCount} of{" "}
                                    {latestHierarchyBatchQueuedCount} batches.
                                  </Typography>
                                )}

                                <Stack
                                  direction="row"
                                  spacing={1}
                                  flexWrap="wrap"
                                  useFlexGap
                                >
                                  <Chip
                                    size="small"
                                    color="primary"
                                    label={`Recommendations generated: ${latestHierarchyRecommendationCount}`}
                                  />
                                  <Chip
                                    size="small"
                                    color="secondary"
                                    label={`Synthesized parents: ${latestHierarchySynthesizedParentCount}`}
                                  />
                                </Stack>
                              </Stack>
                            </CardContent>
                          </Card>

                          {hasStalledHierarchyGeneration ? (
                            <Alert severity="warning">
                              Hierarchy generation appears stalled. Retry
                              Generate Hierarchy to requeue the missing
                              hierarchy batches for this pass.
                            </Alert>
                          ) : null}
                        </Stack>
                      ) : (
                        <Alert
                          severity={
                            hasStalledHierarchyGeneration ? "warning" : "info"
                          }
                        >
                          {hasStalledHierarchyGeneration
                            ? `Hierarchy generation appears stalled for workspace ${workspaceRun.runId}. Retry Generate Hierarchy to requeue the missing hierarchy work for this pass.`
                            : `Hierarchy generation is running for workspace ${workspaceRun.runId}${latestHierarchyRun ? ` (hierarchy run ${latestHierarchyRun.runNumber}).` : "."}`}
                        </Alert>
                      )
                    ) : hierarchyRuns.length === 0 ? (
                      <Alert severity="info">
                        Flat canonical tag review is complete. Generate
                        hierarchy recommendations to synthesize parent links
                        before publishing.
                      </Alert>
                    ) : hasPendingHierarchyProposals ? (
                      <Alert severity="info">
                        Review the current hierarchy recommendations before
                        starting another hierarchy pass.
                        {hierarchyRerunRecommended
                          ? " Generate Hierarchy Again will unlock after every recommendation in this pass is approved or rejected."
                          : ""}
                      </Alert>
                    ) : latestHierarchyRun?.runStatus === "FAILED" ? (
                      <Alert severity="error">
                        The latest hierarchy run failed. Review diagnostics or
                        rerun hierarchy after recovery.
                      </Alert>
                    ) : (
                      <Alert
                        severity={
                          latestHierarchyRun?.recommendedAction ===
                          "PUBLISH_READY"
                            ? "success"
                            : "info"
                        }
                      >
                        Hierarchy run {latestHierarchyRun?.runNumber} is{" "}
                        <strong>{latestHierarchyRun?.runStatus}</strong>.
                        {latestHierarchyRun?.rerunSummary
                          ? ` ${latestHierarchyRun.rerunSummary}`
                          : ""}
                      </Alert>
                    )}

                    {latestHierarchyRun &&
                    latestHierarchyRun.runStatus !== "FAILED" &&
                    hasCurrentHierarchyProgressMetrics ? (
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          label={
                            latestHierarchyTargetQueuedCount > 0
                              ? `Approved tags processed: ${latestHierarchyTargetCompletedCount}/${latestHierarchyTargetQueuedCount}`
                              : "No approved tags in this pass"
                          }
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Batches completed: ${latestHierarchyBatchCompletedCount}/${latestHierarchyBatchQueuedCount}`}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Recommendations generated: ${latestHierarchyRecommendationCount}`}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Synthesized parents: ${latestHierarchySynthesizedParentCount}`}
                        />
                      </Stack>
                    ) : null}

                    {latestHierarchyRun?.rerunReasonCodes.length ? (
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {latestHierarchyRun.rerunReasonCodes.map(
                          (reasonCode) => (
                            <Chip
                              key={reasonCode}
                              size="small"
                              label={reasonCode}
                              variant="outlined"
                            />
                          ),
                        )}
                      </Stack>
                    ) : null}

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                    >
                      <Button
                        variant="contained"
                        onClick={() => void generateCanonicalHierarchy()}
                        disabled={!canGenerateHierarchy}
                        startIcon={
                          generateCanonicalHierarchyMutation.isLoading ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : undefined
                        }
                      >
                        {hasStalledHierarchyGeneration
                          ? "Resume Hierarchy Generation"
                          : hierarchyRerunRecommended
                            ? "Generate Hierarchy Again"
                            : "Generate Hierarchy"}
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        {canGenerateHierarchy
                          ? hasStalledHierarchyGeneration
                            ? "The current hierarchy run appears stalled. This action requeues the missing hierarchy work for the same pass."
                            : "Hierarchy generation is available because canonical proposal review is complete."
                          : hasPendingProposals
                            ? "Hierarchy stays locked until all canonical proposals are approved or rejected."
                            : hasPendingHierarchyProposals
                              ? hierarchyRerunRecommended
                                ? "Review the current hierarchy recommendations first. Generate Hierarchy Again unlocks after they are all approved or rejected."
                                : "Hierarchy generation stays locked until the current hierarchy recommendations are approved or rejected."
                              : workspaceRun?.runStatus ===
                                    TaxonomyRunStatus.GENERATING_HIERARCHY &&
                                  hasStalledHierarchyGeneration
                                ? "The current hierarchy run appears stalled, but resume is currently unavailable while another hierarchy request is in flight."
                                : "Hierarchy generation is unavailable while the workspace is actively running another async step or while publish is in progress."}
                      </Typography>
                    </Stack>
                  </Stack>
                </WorkflowStageCard>

                <WorkflowStageCard
                  id={STAGE_CARD_ID["review-hierarchy"]}
                  title="Review Hierarchy"
                  description="Review generated hierarchy recommendations, including suggested existing parents and synthesized parent tags."
                  state={stageDefinitions[6].state}
                  summary={stageDefinitions[6].summary}
                  expanded={expandedStageIds.includes("review-hierarchy")}
                  onToggle={() => toggleStage("review-hierarchy")}
                >
                  <Stack spacing={2}>
                    {pendingHierarchyProposalsQuery.error ? (
                      <Alert severity="error">
                        {pendingHierarchyProposalsQuery.error.message}
                      </Alert>
                    ) : !workspaceRun ? (
                      <Alert severity="info">
                        Start a bootstrap run before reviewing hierarchy.
                      </Alert>
                    ) : workspaceRun.runStatus ===
                      TaxonomyRunStatus.GENERATING_HIERARCHY ? (
                      <Alert severity="info">
                        Hierarchy generation is still running. Recommendations
                        will appear here when the async worker completes.
                      </Alert>
                    ) : hierarchyRuns.length === 0 ? (
                      <Alert severity="info">
                        Generate hierarchy to open this review queue.
                      </Alert>
                    ) : latestHierarchyApprovalJob?.jobStatus ===
                      TaxonomyApprovalJobStatus.FAILED ? (
                      <Alert severity="error">
                        Bulk hierarchy approval failed:{" "}
                        {latestHierarchyApprovalJob.failureReason ??
                          "No failure reason was recorded."}
                      </Alert>
                    ) : latestHierarchyApprovalJob?.jobStatus ===
                        TaxonomyApprovalJobStatus.COMPLETED &&
                      (latestHierarchyApprovalJob.skippedInvalidCount ?? 0) >
                        0 &&
                      hasPendingHierarchyProposals ? (
                      <Alert severity="warning">
                        Bulk hierarchy approval finished, but{" "}
                        {latestHierarchyApprovalJob.skippedInvalidCount}{" "}
                        recommendation(s) were skipped because approving them
                        would make the canonical snapshot invalid. Use Fix on an
                        affected recommendation to inspect the exact blocker
                        details.
                      </Alert>
                    ) : hasActiveHierarchyApprovalJob ? (
                      <Alert
                        severity={
                          hasStalledHierarchyApprovalJob ? "warning" : "info"
                        }
                      >
                        {hasStalledHierarchyApprovalJob
                          ? `Bulk hierarchy approval appears stalled. ${latestHierarchyApprovalJob?.completedCount ?? 0} of ${latestHierarchyApprovalJob?.queuedCount ?? 0} recommendation(s) have been processed. Retry Approve Remaining to resume the pending job items.`
                          : `Bulk hierarchy approval is ${latestHierarchyApprovalJob?.jobStatus.toLowerCase()}. ${latestHierarchyApprovalJob?.completedCount ?? 0} of ${latestHierarchyApprovalJob?.queuedCount ?? 0} recommendation(s) have been applied.`}
                      </Alert>
                    ) : hasPendingHierarchyProposals ? (
                      <Alert severity="warning">
                        {workspacePendingHierarchyProposalCount} hierarchy
                        recommendation(s) remain in the review queue.
                      </Alert>
                    ) : (
                      <Alert severity="success">
                        Hierarchy review is complete. No recommendations remain
                        pending.
                      </Alert>
                    )}

                    {hasPendingHierarchyProposals ? (
                      <Stack spacing={2}>
                        {hierarchyDuplicateGroupsQuery.error ? (
                          <Alert severity="error">
                            {hierarchyDuplicateGroupsQuery.error.message}
                          </Alert>
                        ) : isHierarchyDuplicateAnalysisLoading ? (
                          <Alert severity="info">
                            Analyzing likely synthesized-parent duplicates
                            before bulk hierarchy approval is enabled.
                          </Alert>
                        ) : null}

                        {hasHierarchyDuplicateGroups ? (
                          <Stack
                            id="hierarchy-duplicate-groups-anchor"
                            spacing={2}
                          >
                            <Alert severity="warning">
                              {hierarchyDuplicateGroups.length} likely
                              synthesized-parent duplicate group(s) need
                              resolution before bulk hierarchy approval is safe.
                            </Alert>
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1.5}
                              alignItems={{ xs: "stretch", sm: "center" }}
                            >
                              <Button
                                variant="contained"
                                color="warning"
                                onClick={() =>
                                  void approveAllHierarchyDuplicateFixes()
                                }
                                disabled={
                                  activeHierarchyDuplicateGroupId != null ||
                                  approveCanonicalHierarchyDuplicateFixMutation.isLoading ||
                                  approveAllCanonicalHierarchyDuplicateFixesMutation.isLoading ||
                                  hasActiveHierarchyApprovalJob ||
                                  isApprovingRemainingHierarchyQueue
                                }
                                startIcon={
                                  activeHierarchyDuplicateGroupId ===
                                  "__all__" ? (
                                    <CircularProgress
                                      size={16}
                                      color="inherit"
                                    />
                                  ) : undefined
                                }
                              >
                                Approve All Recommended Fixes (
                                {hierarchyDuplicateGroups.length})
                              </Button>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                This keeps the recommended survivor in each
                                synthesized-parent duplicate group and rejects
                                the duplicate recommendation(s) before normal
                                hierarchy approval continues. Use Remove on any
                                pending item to send it back to normal hierarchy
                                review instead.
                              </Typography>
                            </Stack>
                            <Stack spacing={2}>
                              {hierarchyDuplicateGroups.map((group) => (
                                <DuplicateGroupCard
                                  key={group.groupId}
                                  group={group}
                                  isBusy={
                                    activeHierarchyDuplicateGroupId ===
                                      group.groupId ||
                                    approveCanonicalHierarchyDuplicateFixMutation.isLoading ||
                                    approveAllCanonicalHierarchyDuplicateFixesMutation.isLoading
                                  }
                                  isApplying={
                                    activeHierarchyDuplicateGroupId ===
                                    group.groupId
                                  }
                                  onApply={() =>
                                    void approveHierarchyDuplicateFix(group)
                                  }
                                  onRemoveSource={(source) =>
                                    removeHierarchyDuplicateSourceFromBlockerFlow(
                                      source,
                                    )
                                  }
                                  onFocusSource={focusHierarchyDuplicateSource}
                                />
                              ))}
                            </Stack>
                          </Stack>
                        ) : null}

                        <Box
                          sx={{
                            display: "grid",
                            gap: 2,
                            gridTemplateColumns: {
                              xs: "1fr",
                              xl: "minmax(0, 360px) auto auto",
                            },
                          }}
                        >
                          <TextField
                            size="small"
                            label="Search hierarchy recommendations"
                            value={hierarchySearch}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              setHierarchySearch(nextValue);
                              setIsHierarchyListLoading(true);
                            }}
                            helperText={
                              isHierarchyReviewContentLoading
                                ? "Loading hierarchy recommendations..."
                                : hierarchySearchQuery
                                  ? `Showing ${pendingHierarchyProposals.length} search result(s) on page ${hierarchyReviewPageIndex + 1}.`
                                  : `Showing ${pendingHierarchyProposals.length} recommendation(s) on page ${hierarchyReviewPageIndex + 1}.`
                            }
                          />
                          <Button
                            variant="outlined"
                            onClick={() =>
                              void downloadHierarchyAwaitingApprovalReview()
                            }
                            disabled={
                              !hasPendingHierarchyProposals ||
                              isExportingHierarchyReview ||
                              isApprovingRemainingHierarchyQueue ||
                              hasActiveHierarchyApprovalJob
                            }
                            startIcon={
                              isExportingHierarchyReview ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : undefined
                            }
                          >
                            Export Awaiting Approvals
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() =>
                              void approveRemainingHierarchyProposals()
                            }
                            disabled={!canApproveRemainingHierarchy}
                            startIcon={
                              isApprovingRemainingHierarchyQueue ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : undefined
                            }
                          >
                            {hasStalledHierarchyApprovalJob
                              ? `Resume Bulk Approval (${workspacePendingHierarchyProposalCount})`
                              : `Approve Remaining (${workspacePendingHierarchyProposalCount})`}
                          </Button>
                        </Box>

                        <Alert severity="info">
                          Pending in workspace:{" "}
                          {workspacePendingHierarchyProposalCount}. Search
                          results on this page:{" "}
                          {pendingHierarchyProposals.length}.
                          {hasActiveHierarchyApprovalJob
                            ? hasStalledHierarchyApprovalJob
                              ? ` Bulk approval appears stalled. Retry Approve Remaining to resume the pending hierarchy approvals.`
                              : ` Bulk approval is ${latestHierarchyApprovalJob?.jobStatus.toLowerCase()} and review controls stay locked until it finishes.`
                            : hasHierarchyDuplicateGroups
                              ? ` Resolve ${hierarchyDuplicateGroups.length} duplicate fix group(s) before using Approve Remaining. ${currentPageHierarchyDuplicateCount} item(s) on this page are part of a duplicate recommendation.`
                              : latestHierarchyApprovalJob?.jobStatus ===
                                    TaxonomyApprovalJobStatus.COMPLETED &&
                                  (latestHierarchyApprovalJob.skippedInvalidCount ??
                                    0) > 0
                                ? ` ${latestHierarchyApprovalJob.skippedInvalidCount} recommendation(s) were skipped as invalid in the latest bulk approval run.`
                                : blockedPendingHierarchyCount > 0
                                  ? ` ${blockedPendingHierarchyCount} recommendation(s) currently shown need a Fix step before they can be approved safely.`
                                  : ""}
                        </Alert>

                        <Stack
                          direction={{ xs: "column", lg: "row" }}
                          spacing={2}
                          alignItems={{ xs: "stretch", lg: "center" }}
                          justifyContent="space-between"
                        >
                          <TextField
                            select
                            size="small"
                            label="Rows per page"
                            value={hierarchyReviewRowsPerPage}
                            onChange={(event) => {
                              setIsHierarchyListLoading(true);
                              setHierarchyReviewRowsPerPage(
                                Number(event.target.value),
                              );
                            }}
                            sx={{ minWidth: 140 }}
                          >
                            {[5, 10, 25, 50].map((pageSize) => (
                              <MenuItem key={pageSize} value={pageSize}>
                                {pageSize}
                              </MenuItem>
                            ))}
                          </TextField>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "stretch", sm: "center" }}
                          >
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setIsHierarchyListLoading(true);
                                setHierarchyReviewPageIndex((currentPage) =>
                                  Math.max(0, currentPage - 1),
                                );
                              }}
                              disabled={
                                hierarchyReviewPageIndex === 0 ||
                                pendingHierarchyProposalsQuery.isFetching
                              }
                            >
                              Previous
                            </Button>
                            <Typography variant="body2" color="text.secondary">
                              Page {hierarchyReviewPageIndex + 1}
                            </Typography>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                const nextCursor =
                                  pendingHierarchyProposalPage?.nextCursor;
                                if (!nextCursor) {
                                  return;
                                }

                                setIsHierarchyListLoading(true);
                                setHierarchyReviewCursors((currentCursors) => {
                                  const nextCursors = currentCursors.slice(
                                    0,
                                    hierarchyReviewPageIndex + 1,
                                  );
                                  nextCursors.push(nextCursor);
                                  return nextCursors;
                                });
                                setHierarchyReviewPageIndex(
                                  (currentPage) => currentPage + 1,
                                );
                              }}
                              disabled={
                                !hasNextPendingHierarchyProposalPage ||
                                pendingHierarchyProposalsQuery.isFetching
                              }
                            >
                              Next
                            </Button>
                          </Stack>
                        </Stack>

                        {isHierarchyReviewContentLoading ? (
                          <ReviewResultSkeletons />
                        ) : pendingHierarchyProposals.length === 0 ? (
                          <Alert severity="info">
                            No hierarchy recommendations match the current
                            search.
                          </Alert>
                        ) : null}

                        {pendingHierarchyProposals.map((proposal) => {
                          const rejectionNotes =
                            hierarchyRejectionNotes[
                              proposal.hierarchyProposalId
                            ] ?? "";
                          const hierarchyDuplicateGroup =
                            hierarchyDuplicateGroupByProposalId.get(
                              proposal.hierarchyProposalId,
                            );
                          const hierarchyDraft =
                            hierarchyApprovalDrafts[
                              proposal.hierarchyProposalId
                            ] ?? createHierarchyApprovalDraft(proposal);
                          const hierarchyApprovalIssues =
                            proposal.approvalValidationIssues ?? [];
                          const hierarchyApprovalBlockers =
                            proposal.approvalValidationBlockers ?? [];
                          const hierarchyDraftMatchesProposal =
                            doesHierarchyApprovalDraftMatchProposal({
                              proposal,
                              draft: hierarchyDraft,
                            });
                          const showHierarchyFixAction =
                            hierarchyApprovalIssues.length > 0 &&
                            hierarchyDraftMatchesProposal;
                          const isHierarchyReviewActionLocked =
                            isApprovingRemainingHierarchyQueue ||
                            hasActiveHierarchyApprovalJob ||
                            approveCanonicalHierarchyProposalMutation.isLoading ||
                            approveRemainingCanonicalHierarchyProposalsMutation.isLoading ||
                            rejectCanonicalHierarchyProposalMutation.isLoading;
                          const recommendedParents =
                            proposal.recommendedParents ?? [];
                          const candidateParents =
                            proposal.candidateParents ?? [];
                          const recommendedParentSummaryBySlug = new Map(
                            recommendedParents.map((parentTag) => [
                              parentTag.slug,
                              parentTag,
                            ]),
                          );
                          const candidateParentSummaryBySlug = new Map(
                            candidateParents.map((parentTag) => [
                              parentTag.slug,
                              parentTag,
                            ]),
                          );
                          const synthesizedParentSummaryBySlug = new Map(
                            (proposal.synthesizedParent?.parentTags ?? []).map(
                              (parentTag) => [parentTag.slug, parentTag],
                            ),
                          );
                          const getParentTooltipTitle = ({
                            layer,
                            slug,
                            summaryBySlug,
                          }: {
                            layer: ICanonicalTag["layer"];
                            slug: string;
                            summaryBySlug: ReadonlyMap<
                              string,
                              {
                                slug: string;
                                name: string;
                                description: string;
                              }
                            >;
                          }) => {
                            const parentSummary = summaryBySlug.get(slug);
                            if (parentSummary) {
                              return `${parentSummary.name}: ${parentSummary.description}`;
                            }

                            return getHierarchyParentDescription({
                              layer,
                              slug,
                            });
                          };

                          return (
                            <Card
                              id={`hierarchy-proposal-card-${proposal.hierarchyProposalId}`}
                              key={proposal.hierarchyProposalId}
                              variant="outlined"
                              sx={{ borderRadius: 3 }}
                            >
                              <CardContent>
                                <Stack spacing={2}>
                                  <Box>
                                    <Typography
                                      variant="h6"
                                      sx={{ fontFamily: displayFontFamily }}
                                    >
                                      {proposal.targetTag.layer}:
                                      {proposal.targetTag.slug}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.75 }}
                                    >
                                      {proposal.targetTag.name}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.75 }}
                                    >
                                      {proposal.targetTag.description}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.75 }}
                                    >
                                      {proposal.reasoningSummary}
                                    </Typography>
                                  </Box>

                                  {hierarchyApprovalIssues.length > 0 ? (
                                    <Alert severity="warning">
                                      <Typography variant="body2">
                                        {showHierarchyFixAction
                                          ? "As currently loaded from the workspace, this recommendation would be blocked by canonical snapshot validation."
                                          : "This recommendation was blocked in its original form. If your edits resolve the conflict, approve it again to validate the updated recommendation."}
                                      </Typography>
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        flexWrap="wrap"
                                        useFlexGap
                                        sx={{ mt: 1 }}
                                      >
                                        {hierarchyApprovalIssues.map(
                                          (issue) => (
                                            <Chip
                                              key={`${proposal.hierarchyProposalId}-${issue.code}-${issue.affectedCanonicalKeys.join("-")}`}
                                              size="small"
                                              variant="outlined"
                                              color="warning"
                                              label={issue.code}
                                            />
                                          ),
                                        )}
                                      </Stack>
                                      <Stack spacing={0.75} sx={{ mt: 1 }}>
                                        {hierarchyApprovalIssues.map(
                                          (issue) => (
                                            <Typography
                                              key={`${proposal.hierarchyProposalId}-message-${issue.code}-${issue.affectedCanonicalKeys.join("-")}`}
                                              variant="body2"
                                            >
                                              {issue.message}
                                            </Typography>
                                          ),
                                        )}
                                      </Stack>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 1 }}
                                      >
                                        {showHierarchyFixAction
                                          ? hierarchyApprovalBlockers.length > 0
                                            ? "Use Fix to review the blocker details, or edit the recommendation below to make it valid."
                                            : "Edit the recommendation below, then approve again."
                                          : "Keep editing the recommendation below, then approve again."}
                                      </Typography>
                                    </Alert>
                                  ) : null}

                                  {hierarchyDuplicateGroup ? (
                                    <Alert severity="warning">
                                      <Typography variant="body2">
                                        This recommendation is part of a likely
                                        synthesized-parent duplicate group.
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.75 }}
                                      >
                                        {
                                          hierarchyDuplicateGroup.recommendation
                                            .summary
                                        }
                                      </Typography>
                                    </Alert>
                                  ) : null}

                                  <Box
                                    sx={{
                                      display: "grid",
                                      gap: 2,
                                      gridTemplateColumns: {
                                        xs: "1fr",
                                        md: "repeat(2, minmax(0, 1fr))",
                                      },
                                    }}
                                  >
                                    <Box>
                                      <Typography variant="subtitle2">
                                        Parents to Apply
                                      </Typography>
                                      {hierarchyDraft.approvedParentSlugs
                                        .length > 0 ? (
                                        <Stack
                                          direction="row"
                                          spacing={1}
                                          flexWrap="wrap"
                                          useFlexGap
                                          sx={{ mt: 0.75 }}
                                        >
                                          {hierarchyDraft.approvedParentSlugs.map(
                                            (parentSlug) => (
                                              <Tooltip
                                                key={`${proposal.hierarchyProposalId}-${parentSlug}`}
                                                title={getParentTooltipTitle({
                                                  layer:
                                                    proposal.targetTag.layer,
                                                  slug: parentSlug,
                                                  summaryBySlug:
                                                    recommendedParentSummaryBySlug,
                                                })}
                                                arrow
                                              >
                                                <Chip
                                                  size="small"
                                                  label={parentSlug}
                                                  onDelete={
                                                    isHierarchyReviewActionLocked
                                                      ? undefined
                                                      : () =>
                                                          setHierarchyApprovalDraft(
                                                            proposal.hierarchyProposalId,
                                                            {
                                                              ...hierarchyDraft,
                                                              approvedParentSlugs:
                                                                hierarchyDraft.approvedParentSlugs.filter(
                                                                  (
                                                                    candidateSlug,
                                                                  ) =>
                                                                    candidateSlug !==
                                                                    parentSlug,
                                                                ),
                                                            },
                                                          )
                                                  }
                                                />
                                              </Tooltip>
                                            ),
                                          )}
                                        </Stack>
                                      ) : (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ mt: 0.75 }}
                                        >
                                          No parent links will be applied to
                                          this tag.
                                        </Typography>
                                      )}
                                    </Box>
                                    <Box>
                                      <Typography variant="subtitle2">
                                        Candidate Existing Parents
                                      </Typography>
                                      {proposal.candidateParentSlugs.length >
                                      0 ? (
                                        <Stack
                                          direction="row"
                                          spacing={1}
                                          flexWrap="wrap"
                                          useFlexGap
                                          sx={{ mt: 0.75 }}
                                        >
                                          {proposal.candidateParentSlugs.map(
                                            (parentSlug) => (
                                              <Tooltip
                                                key={`${proposal.hierarchyProposalId}-candidate-${parentSlug}`}
                                                title={getParentTooltipTitle({
                                                  layer:
                                                    proposal.targetTag.layer,
                                                  slug: parentSlug,
                                                  summaryBySlug:
                                                    candidateParentSummaryBySlug,
                                                })}
                                                arrow
                                              >
                                                <Chip
                                                  size="small"
                                                  variant="outlined"
                                                  label={parentSlug}
                                                />
                                              </Tooltip>
                                            ),
                                          )}
                                        </Stack>
                                      ) : (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          —
                                        </Typography>
                                      )}
                                    </Box>
                                    <Box>
                                      <Typography variant="subtitle2">
                                        Source Raw Tags
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {proposal.sourceTagKeys
                                          .map(
                                            (sourceTagKey) =>
                                              sourceTagKey.rawKey,
                                          )
                                          .join(", ") || "—"}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="subtitle2">
                                        Shared Tagger Ancestors
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {proposal.sharedAncestors
                                          .map((ancestor) => ancestor.rawName)
                                          .join(", ") || "—"}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {proposal.promptContext ? (
                                    <Box>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{ mb: 0.5 }}
                                      >
                                        Mechanics Context
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {proposal.promptContext.mode ===
                                        "derived"
                                          ? `Keywords: ${proposal.promptContext.clusterKeywords.join(", ") || "—"}`
                                          : `Full-text fallback cards: ${
                                              proposal.promptContext.fullTextCards
                                                .map((card) => card.cardLabel)
                                                .join(", ") || "—"
                                            }`}
                                      </Typography>
                                    </Box>
                                  ) : null}

                                  {proposal.synthesizedParent ? (
                                    <Box>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{ mb: 0.5 }}
                                      >
                                        Synthesized Parent Tag
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {proposal.synthesizedParent.layer}:
                                        {proposal.synthesizedParent.slug} |{" "}
                                        {proposal.synthesizedParent.name}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {proposal.synthesizedParent.description}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        Child proposals:{" "}
                                        {proposal.synthesizedParent.childProposalIds.join(
                                          ", ",
                                        )}
                                      </Typography>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{ mt: 1.5 }}
                                      >
                                        Synthesized Parent Parents to Apply
                                      </Typography>
                                      {hierarchyDraft
                                        .approvedSynthesizedParentParentSlugs
                                        .length > 0 ? (
                                        <Stack
                                          direction="row"
                                          spacing={1}
                                          flexWrap="wrap"
                                          useFlexGap
                                          sx={{ mt: 0.75 }}
                                        >
                                          {hierarchyDraft.approvedSynthesizedParentParentSlugs.map(
                                            (parentSlug) => (
                                              <Tooltip
                                                key={`${proposal.hierarchyProposalId}-synthetic-${parentSlug}`}
                                                title={getParentTooltipTitle({
                                                  layer:
                                                    proposal.synthesizedParent!
                                                      .layer,
                                                  slug: parentSlug,
                                                  summaryBySlug:
                                                    synthesizedParentSummaryBySlug,
                                                })}
                                                arrow
                                              >
                                                <Chip
                                                  size="small"
                                                  label={parentSlug}
                                                  onDelete={
                                                    isHierarchyReviewActionLocked
                                                      ? undefined
                                                      : () =>
                                                          setHierarchyApprovalDraft(
                                                            proposal.hierarchyProposalId,
                                                            {
                                                              ...hierarchyDraft,
                                                              approvedSynthesizedParentParentSlugs:
                                                                hierarchyDraft.approvedSynthesizedParentParentSlugs.filter(
                                                                  (
                                                                    candidateSlug,
                                                                  ) =>
                                                                    candidateSlug !==
                                                                    parentSlug,
                                                                ),
                                                            },
                                                          )
                                                  }
                                                />
                                              </Tooltip>
                                            ),
                                          )}
                                        </Stack>
                                      ) : (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ mt: 0.75 }}
                                        >
                                          No parent links will be applied to the
                                          synthesized parent tag.
                                        </Typography>
                                      )}
                                    </Box>
                                  ) : null}

                                  <TextField
                                    label="Rejection notes"
                                    size="small"
                                    multiline
                                    minRows={2}
                                    value={rejectionNotes}
                                    disabled={isHierarchyReviewActionLocked}
                                    onChange={(event) =>
                                      setHierarchyRejectionNote(
                                        proposal.hierarchyProposalId,
                                        event.target.value,
                                      )
                                    }
                                  />

                                  <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1.5}
                                  >
                                    <Button
                                      variant="outlined"
                                      disabled={isHierarchyReviewActionLocked}
                                      onClick={() =>
                                        setHierarchyApprovalDraft(
                                          proposal.hierarchyProposalId,
                                          createHierarchyApprovalDraft(
                                            proposal,
                                          ),
                                        )
                                      }
                                    >
                                      Reset Parents
                                    </Button>
                                    {showHierarchyFixAction ? (
                                      <Button
                                        color="warning"
                                        variant="contained"
                                        disabled={
                                          isHierarchyReviewActionLocked ||
                                          isValidationRepairMutationLoading
                                        }
                                        onClick={() =>
                                          openPendingHierarchyFix({
                                            proposal,
                                            draft: hierarchyDraft,
                                          })
                                        }
                                      >
                                        Fix
                                      </Button>
                                    ) : hierarchyDuplicateGroup ? (
                                      <>
                                        <Button
                                          color="warning"
                                          variant="contained"
                                          disabled={
                                            isHierarchyReviewActionLocked ||
                                            approveCanonicalHierarchyDuplicateFixMutation.isLoading ||
                                            approveAllCanonicalHierarchyDuplicateFixesMutation.isLoading
                                          }
                                          onClick={() =>
                                            focusElement(
                                              "hierarchy-duplicate-groups-anchor",
                                              {
                                                stageId: "review-hierarchy",
                                              },
                                            )
                                          }
                                        >
                                          Resolve Duplicate
                                        </Button>
                                        <Button
                                          color="warning"
                                          variant="outlined"
                                          onClick={() =>
                                            void rejectCanonicalHierarchyProposal(
                                              {
                                                hierarchyProposalId:
                                                  proposal.hierarchyProposalId,
                                                notes:
                                                  rejectionNotes.trim() ||
                                                  undefined,
                                              },
                                            )
                                          }
                                          disabled={
                                            isHierarchyReviewActionLocked ||
                                            approveCanonicalHierarchyDuplicateFixMutation.isLoading ||
                                            approveAllCanonicalHierarchyDuplicateFixesMutation.isLoading
                                          }
                                          startIcon={
                                            activeHierarchyRejectionId ===
                                            proposal.hierarchyProposalId ? (
                                              <CircularProgress
                                                size={16}
                                                color="inherit"
                                              />
                                            ) : undefined
                                          }
                                        >
                                          Reject Recommendation
                                        </Button>
                                      </>
                                    ) : (
                                      <>
                                        <Button
                                          variant="contained"
                                          onClick={() =>
                                            void approveCanonicalHierarchyProposal(
                                              {
                                                hierarchyProposalId:
                                                  proposal.hierarchyProposalId,
                                                ...hierarchyDraft,
                                              },
                                            )
                                          }
                                          disabled={
                                            isHierarchyReviewActionLocked
                                          }
                                          startIcon={
                                            activeHierarchyApprovalId ===
                                            proposal.hierarchyProposalId ? (
                                              <CircularProgress
                                                size={16}
                                                color="inherit"
                                              />
                                            ) : undefined
                                          }
                                        >
                                          Approve Recommendation
                                        </Button>
                                        <Button
                                          color="warning"
                                          variant="outlined"
                                          onClick={() =>
                                            void rejectCanonicalHierarchyProposal(
                                              {
                                                hierarchyProposalId:
                                                  proposal.hierarchyProposalId,
                                                notes:
                                                  rejectionNotes.trim() ||
                                                  undefined,
                                              },
                                            )
                                          }
                                          disabled={
                                            isHierarchyReviewActionLocked
                                          }
                                          startIcon={
                                            activeHierarchyRejectionId ===
                                            proposal.hierarchyProposalId ? (
                                              <CircularProgress
                                                size={16}
                                                color="inherit"
                                              />
                                            ) : undefined
                                          }
                                        >
                                          Reject Recommendation
                                        </Button>
                                      </>
                                    )}
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Stack>
                    ) : null}
                  </Stack>
                </WorkflowStageCard>

                <WorkflowStageCard
                  id={STAGE_CARD_ID.publish}
                  title="Publish Snapshot"
                  description="Validate readiness, then publish the canonical snapshot to S3."
                  state={stageDefinitions[7].state}
                  summary={stageDefinitions[7].summary}
                  expanded={expandedStageIds.includes("publish")}
                  onToggle={() => toggleStage("publish")}
                >
                  <Stack spacing={2}>
                    <Alert
                      severity={
                        workspaceRun?.runStatus === TaxonomyRunStatus.COMPLETED
                          ? "success"
                          : workspaceRun?.runStatus ===
                                TaxonomyRunStatus.PUBLISHING &&
                              hasStalledPublishing
                            ? "warning"
                            : hasValidationBlockers
                              ? "warning"
                              : canPublishSnapshot
                                ? "success"
                                : "info"
                      }
                    >
                      {workspaceRun?.runStatus === TaxonomyRunStatus.COMPLETED
                        ? "This workspace has already published a canonical snapshot."
                        : workspaceRun?.runStatus ===
                            TaxonomyRunStatus.PUBLISHING
                          ? hasStalledPublishing
                            ? "Snapshot publish appears stalled. Retry publish to requeue the publish worker for this run."
                            : "Snapshot publish is currently running."
                          : hasValidationBlockers
                            ? `${validationBlockers.length} validation blocker(s) currently prevent publish. Resolve them in the Validation Blockers section above, then retry publish.`
                            : canPublishSnapshot
                              ? "Publish is unlocked. All required review work is complete and the latest hierarchy run is publish-ready."
                              : "Publish remains locked until every readiness check below is complete."}
                    </Alert>

                    <Stack spacing={1.25}>
                      {publishChecklistItems.map((item) => (
                        <Stack
                          key={item.label}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <Chip
                            size="small"
                            label={item.complete ? "Ready" : "Blocked"}
                            color={item.complete ? "success" : "default"}
                            variant={item.complete ? "filled" : "outlined"}
                          />
                          <Typography
                            variant="body2"
                            color={
                              item.complete ? "text.primary" : "text.secondary"
                            }
                          >
                            {item.label}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    {latestHierarchyRun?.recommendedAction ===
                    "REVIEW_WARNINGS" ? (
                      <Alert severity="warning">
                        The latest hierarchy run recommends reviewing warnings
                        before publish.
                      </Alert>
                    ) : null}

                    {workspaceRun?.runStatus ===
                    TaxonomyRunStatus.PUBLISHING ? (
                      <Alert
                        severity={hasStalledPublishing ? "warning" : "info"}
                      >
                        {hasStalledPublishing
                          ? `Snapshot publish appears stalled. The run last updated at ${formatTimestamp(
                              workspaceRun.updatedAt,
                            )}. Retry publish to requeue the publish worker.`
                          : `Snapshot publish is running for workspace ${workspaceRun.runId}.`}
                      </Alert>
                    ) : null}

                    {workspaceRun?.runStatus ===
                    TaxonomyRunStatus.PUBLISH_FAILED ? (
                      <Alert severity="warning">
                        {workspaceFailureReason ??
                          "The last publish attempt failed. Resolve any blockers, then retry publish."}
                      </Alert>
                    ) : null}

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                    >
                      <Button
                        variant="contained"
                        onClick={() => void publishCanonicalSnapshot()}
                        disabled={!canPublishSnapshot}
                        startIcon={
                          publishCanonicalSnapshotMutation.isLoading ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : undefined
                        }
                      >
                        {hasStalledPublishing
                          ? "Retry Publish"
                          : "Publish Snapshot"}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          focusElement("published-snapshot-anchor", {
                            openDiagnostics: true,
                          })
                        }
                        disabled={!currentSnapshot}
                      >
                        View Published Snapshot
                      </Button>
                    </Stack>
                  </Stack>
                </WorkflowStageCard>
              </Stack>

              <DiagnosticsAccordion
                expanded={diagnosticsExpanded}
                onToggle={() => setDiagnosticsExpanded((current) => !current)}
              >
                <Stack spacing={3}>
                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "repeat(4, minmax(0, 1fr))",
                      },
                    }}
                  >
                    <SummaryMetricCard
                      label="Candidate Rows"
                      value={String(
                        getRunMetric(workspaceRun, "currentCandidateCount"),
                      )}
                      helper="Relevant source-tag candidate rows written into this workspace."
                    />
                    <SummaryMetricCard
                      label="Evidence Rows"
                      value={String(
                        getRunMetric(workspaceRun, "currentEvidenceCount"),
                      )}
                      helper="Detailed evidence rows written into this workspace."
                    />
                    <SummaryMetricCard
                      label="Run ID"
                      value={workspaceRun?.runId ?? "—"}
                      helper="Current workspace run identifier."
                    />
                    <SummaryMetricCard
                      label="Current Snapshot Tags"
                      value={String(currentSnapshot?.tags.length ?? 0)}
                      helper="Tags in the current published canonical snapshot."
                    />
                  </Box>

                  <Card variant="outlined" sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{ fontFamily: displayFontFamily, mb: 2 }}
                      >
                        Applied Hierarchy
                      </Typography>
                      {approvedTagsQuery.error ? (
                        <Alert severity="error">
                          {approvedTagsQuery.error.message}
                        </Alert>
                      ) : approvedTagParentOptionsQuery.error ? (
                        <Alert severity="error">
                          {approvedTagParentOptionsQuery.error.message}
                        </Alert>
                      ) : !workspaceRun ? (
                        <Alert severity="info">
                          Start a bootstrap run to inspect approved tags and
                          parents here.
                        </Alert>
                      ) : (
                        <Stack spacing={2}>
                          <Stack
                            direction={{ xs: "column", lg: "row" }}
                            spacing={2}
                            alignItems={{ xs: "stretch", lg: "center" }}
                            justifyContent="space-between"
                          >
                            <Stack
                              direction={{ xs: "column", lg: "row" }}
                              spacing={2}
                              sx={{ width: "100%" }}
                            >
                              <TextField
                                size="small"
                                label="Search approved tags"
                                value={approvedTagSearch}
                                onChange={(event) => {
                                  setIsApprovedTagListLoading(true);
                                  setApprovedTagSearch(event.target.value);
                                }}
                                helperText={
                                  isApprovedTagContentLoading
                                    ? `Loading approved tags on page ${approvedTagPageIndex + 1}.`
                                    : approvedTagSearchQuery
                                      ? `Showing ${approvedTags.length} approved tag result(s) on page ${approvedTagPageIndex + 1}.`
                                      : `Showing ${approvedTags.length} approved tag(s) on page ${approvedTagPageIndex + 1}.`
                                }
                                sx={{ minWidth: { xs: "100%", lg: 280 } }}
                              />
                              <TextField
                                select
                                size="small"
                                label="Layer"
                                value={appliedHierarchyLayerFilter}
                                onChange={(event) => {
                                  setIsApprovedTagListLoading(true);
                                  setAppliedHierarchyLayerFilter(
                                    event.target.value as
                                      | ICanonicalTag["layer"]
                                      | typeof ALL_SNAPSHOT_LAYERS,
                                  );
                                }}
                                sx={{ minWidth: { xs: "100%", lg: 160 } }}
                              >
                                <MenuItem value={ALL_SNAPSHOT_LAYERS}>
                                  All layers
                                </MenuItem>
                                {TagLayerValues.map((layer) => (
                                  <MenuItem key={layer} value={layer}>
                                    {layer}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                select
                                size="small"
                                label="Parent"
                                value={appliedHierarchyParentFilter}
                                onChange={(event) => {
                                  setIsApprovedTagListLoading(true);
                                  setAppliedHierarchyParentFilter(
                                    event.target.value,
                                  );
                                }}
                                helperText={
                                  selectedAppliedHierarchyParent
                                    ? `Showing all children of ${selectedAppliedHierarchyParent.layer}:${selectedAppliedHierarchyParent.slug}.`
                                    : "Filter to every approved child tag under a selected parent."
                                }
                                sx={{ minWidth: { xs: "100%", lg: 360 } }}
                              >
                                <MenuItem value={ALL_SNAPSHOT_PARENTS}>
                                  All parents
                                </MenuItem>
                                {approvedTagParentOptions.map((option) => (
                                  <MenuItem
                                    key={buildCanonicalTagLookupKey(option)}
                                    value={buildCanonicalTagLookupKey(option)}
                                  >
                                    {`${option.layer} · ${option.name} (${option.childCount})`}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Stack>
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                              alignItems={{ xs: "stretch", sm: "center" }}
                            >
                              <TextField
                                select
                                size="small"
                                label="Rows per page"
                                value={approvedTagRowsPerPage}
                                onChange={(event) => {
                                  setIsApprovedTagListLoading(true);
                                  setApprovedTagRowsPerPage(
                                    Number(event.target.value),
                                  );
                                }}
                                sx={{ minWidth: 140 }}
                              >
                                {[5, 10, 25, 50].map((pageSize) => (
                                  <MenuItem key={pageSize} value={pageSize}>
                                    {pageSize}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <Button
                                variant="outlined"
                                onClick={() => {
                                  setIsApprovedTagListLoading(true);
                                  setApprovedTagPageIndex((currentPage) =>
                                    Math.max(0, currentPage - 1),
                                  );
                                }}
                                disabled={
                                  approvedTagPageIndex === 0 ||
                                  approvedTagsQuery.isFetching
                                }
                              >
                                Previous
                              </Button>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Page {approvedTagPageIndex + 1}
                              </Typography>
                              <Button
                                variant="outlined"
                                onClick={() => {
                                  const nextCursor =
                                    approvedTagPage?.nextCursor;
                                  if (!nextCursor) {
                                    return;
                                  }

                                  setIsApprovedTagListLoading(true);
                                  setApprovedTagCursors((currentCursors) => {
                                    const nextCursors = currentCursors.slice(
                                      0,
                                      approvedTagPageIndex + 1,
                                    );
                                    nextCursors.push(nextCursor);
                                    return nextCursors;
                                  });
                                  setApprovedTagPageIndex(
                                    (currentPage) => currentPage + 1,
                                  );
                                }}
                                disabled={
                                  !hasNextApprovedTagPage ||
                                  approvedTagsQuery.isFetching
                                }
                              >
                                Next
                              </Button>
                            </Stack>
                          </Stack>

                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Showing ${approvedTags.length} approved tag(s)`}
                            />
                            {selectedAppliedHierarchyParent ? (
                              <Tooltip
                                title={
                                  selectedAppliedHierarchyParent.description
                                }
                                placement="top"
                              >
                                <Chip
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  label={`Children of ${selectedAppliedHierarchyParent.layer}:${selectedAppliedHierarchyParent.slug}`}
                                />
                              </Tooltip>
                            ) : null}
                          </Stack>

                          {isApprovedTagContentLoading ? (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Layer</TableCell>
                                  <TableCell>Slug</TableCell>
                                  <TableCell>Name</TableCell>
                                  <TableCell>Parents</TableCell>
                                  <TableCell>Proposal</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Array.from({
                                  length: Math.min(approvedTagRowsPerPage, 5),
                                }).map((_, index) => (
                                  <TableRow
                                    key={`approved-tag-skeleton-${index}`}
                                  >
                                    {Array.from({ length: 5 }).map(
                                      (__unused, columnIndex) => (
                                        <TableCell
                                          key={`approved-tag-skeleton-cell-${index}-${columnIndex}`}
                                        >
                                          <Skeleton
                                            variant="rounded"
                                            height={28}
                                          />
                                        </TableCell>
                                      ),
                                    )}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : approvedTags.length === 0 ? (
                            <Alert severity="info">
                              {approvedTagSearchQuery ||
                              approvedTagLayerFilter ||
                              approvedTagParentFilter
                                ? "No approved tags match the current search and hierarchy filters."
                                : "No approved canonical tags are available in the current workspace yet."}
                            </Alert>
                          ) : (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Layer</TableCell>
                                  <TableCell>Slug</TableCell>
                                  <TableCell>Name</TableCell>
                                  <TableCell>Parents</TableCell>
                                  <TableCell>Proposal</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {approvedTags.map((decision) => (
                                  <TableRow key={decision.proposalId}>
                                    <TableCell>
                                      {decision.approvedTag.layer}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: "monospace" }}>
                                      {decision.approvedTag.slug}
                                    </TableCell>
                                    <TableCell>
                                      {decision.approvedTag.name}
                                    </TableCell>
                                    <TableCell>
                                      {decision.approvedTag.parentSlugs.join(
                                        ", ",
                                      ) || "—"}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: "monospace" }}>
                                      {decision.proposalId}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Stack>
                      )}
                    </CardContent>
                  </Card>

                  <Card variant="outlined" sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{ fontFamily: displayFontFamily, mb: 2 }}
                      >
                        Run State
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Run</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Page limit</TableCell>
                            <TableCell>Proposal model</TableCell>
                            <TableCell>LLM debug</TableCell>
                            <TableCell>Started</TableCell>
                            <TableCell>Updated</TableCell>
                            <TableCell>Completed</TableCell>
                            <TableCell>Metrics</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {runs.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={9}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No canonical bootstrap runs recorded yet.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            runs.map((run) => (
                              <TableRow
                                key={run.runId}
                                selected={run.runId === workspaceRun?.runId}
                              >
                                <TableCell sx={{ fontFamily: "monospace" }}>
                                  {run.runId}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={run.runStatus}
                                    color={
                                      run.runStatus === "FAILED"
                                        ? "error"
                                        : run.runStatus === "COMPLETED"
                                          ? "success"
                                          : "primary"
                                    }
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{run.pageLimit ?? "All"}</TableCell>
                                <TableCell>
                                  {run.proposalModelId
                                    ? BOOTSTRAP_MODEL_LABELS[
                                        run.proposalModelId
                                      ] ?? run.proposalModelId
                                    : "—"}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={
                                      run.captureLlmDebugArtifacts
                                        ? "Enabled"
                                        : "Off"
                                    }
                                    color={
                                      run.captureLlmDebugArtifacts
                                        ? "warning"
                                        : "default"
                                    }
                                    variant={
                                      run.captureLlmDebugArtifacts
                                        ? "filled"
                                        : "outlined"
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  {formatTimestamp(run.startedAt)}
                                </TableCell>
                                <TableCell>
                                  {formatTimestamp(run.updatedAt)}
                                </TableCell>
                                <TableCell>
                                  {formatTimestamp(run.completedAt)}
                                </TableCell>
                                <TableCell>
                                  <Stack spacing={0.5}>
                                    <Typography variant="body2">
                                      coordinatorScheduling:{" "}
                                      {getRunCoordinatorSummary(run)}
                                    </Typography>
                                    <Typography variant="body2">
                                      pageScans: {getRunPageScanSummary(run)}
                                    </Typography>
                                    <Typography variant="body2">
                                      detailWork: {getRunDetailSummary(run)}
                                    </Typography>
                                    <Typography variant="body2">
                                      proposalWork:{" "}
                                      {getRunProposalDisplaySummary(run)}
                                    </Typography>
                                    {getAdditionalRunMetrics(run).map(
                                      ([key, value]) => (
                                        <Typography key={key} variant="body2">
                                          {key}: {value}
                                        </Typography>
                                      ),
                                    )}
                                  </Stack>
                                  {run.failureReason ? (
                                    <Alert severity="error" sx={{ mt: 1 }}>
                                      {run.failureReason}
                                    </Alert>
                                  ) : null}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  <Card
                    id="published-snapshot-anchor"
                    variant="outlined"
                    sx={{ borderRadius: 3 }}
                  >
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{ fontFamily: displayFontFamily, mb: 2 }}
                      >
                        Published Canonical Snapshot
                      </Typography>
                      {currentSnapshot ? (
                        <Stack spacing={2}>
                          <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={2}
                          >
                            <Chip
                              label={`Version: ${currentSnapshot.metadata.version}`}
                              color="primary"
                              variant="outlined"
                            />
                            <Chip
                              label={`Source run: ${currentSnapshot.metadata.sourceRunId}`}
                              variant="outlined"
                            />
                            <Chip
                              label={`${currentSnapshot.tags.length} tags`}
                              variant="outlined"
                            />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            Published{" "}
                            {formatTimestamp(
                              currentSnapshot.metadata.publishedAt,
                            )}{" "}
                            by {currentSnapshot.metadata.publishedBy}
                          </Typography>
                          <Stack
                            direction={{ xs: "column", lg: "row" }}
                            spacing={2}
                            alignItems={{ xs: "stretch", lg: "center" }}
                            justifyContent="space-between"
                          >
                            <TextField
                              select
                              size="small"
                              label="Layer"
                              value={snapshotLayerFilter}
                              onChange={(event) =>
                                setSnapshotLayerFilter(
                                  event.target.value as
                                    | ICanonicalTag["layer"]
                                    | typeof ALL_SNAPSHOT_LAYERS,
                                )
                              }
                              sx={{ minWidth: { xs: "100%", lg: 180 } }}
                            >
                              <MenuItem value={ALL_SNAPSHOT_LAYERS}>
                                All layers
                              </MenuItem>
                              {TagLayerValues.map((layer) => (
                                <MenuItem key={layer} value={layer}>
                                  {layer}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              select
                              size="small"
                              label="Parent"
                              value={snapshotParentFilter}
                              onChange={(event) =>
                                setSnapshotParentFilter(event.target.value)
                              }
                              sx={{ minWidth: { xs: "100%", lg: 420 } }}
                              helperText={
                                selectedSnapshotParent
                                  ? `Showing every child tag that references ${selectedSnapshotParent.layer}:${selectedSnapshotParent.slug}.`
                                  : "Filter the snapshot to tags that inherit from a specific parent."
                              }
                            >
                              <MenuItem value={ALL_SNAPSHOT_PARENTS}>
                                All parents
                              </MenuItem>
                              {snapshotParentOptions.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {`${option.layer} · ${option.name} (${option.childCount})`}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Chip
                              variant="outlined"
                              label={`Showing ${filteredSnapshotTags.length} tag(s)`}
                            />
                            {selectedSnapshotParent ? (
                              <Tooltip
                                title={selectedSnapshotParent.description}
                                placement="top"
                              >
                                <Chip
                                  variant="outlined"
                                  color="secondary"
                                  label={`Children of ${selectedSnapshotParent.layer}:${selectedSnapshotParent.slug}`}
                                />
                              </Tooltip>
                            ) : null}
                          </Stack>
                          {filteredSnapshotTags.length === 0 ? (
                            <Alert severity="info">
                              No published snapshot tags match the current layer
                              and parent filters.
                            </Alert>
                          ) : (
                            <Box
                              sx={{
                                maxHeight: 520,
                                overflow: "auto",
                                border: (theme) =>
                                  `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                              }}
                            >
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Layer</TableCell>
                                    <TableCell>Slug</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Parents</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {filteredSnapshotTags.map((tag) => (
                                    <TableRow key={`${tag.layer}:${tag.slug}`}>
                                      <TableCell>{tag.layer}</TableCell>
                                      <TableCell
                                        sx={{ fontFamily: "monospace" }}
                                      >
                                        {tag.slug}
                                      </TableCell>
                                      <TableCell>{tag.name}</TableCell>
                                      <TableCell>
                                        {tag.parentSlugs.join(", ") || "—"}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          )}
                        </Stack>
                      ) : (
                        <Alert severity="info">
                          No canonical snapshot is currently published in S3.
                        </Alert>
                      )}
                    </CardContent>
                  </Card>

                  <Card variant="outlined" sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{ fontFamily: displayFontFamily, mb: 2 }}
                      >
                        S3 Outputs
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Version</TableCell>
                            <TableCell>Last modified</TableCell>
                            <TableCell>Size</TableCell>
                            <TableCell>S3 key</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(adminState?.versionedOutputs ?? []).length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No canonical snapshot objects have been
                                  written yet.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            adminState?.versionedOutputs.map((output) => (
                              <TableRow key={output.key}>
                                <TableCell>{output.version}</TableCell>
                                <TableCell>
                                  {formatTimestamp(output.lastModified)}
                                </TableCell>
                                <TableCell>
                                  {output.sizeBytes?.toLocaleString() ?? "—"}
                                </TableCell>
                                <TableCell sx={{ fontFamily: "monospace" }}>
                                  {output.key}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </Stack>
              </DiagnosticsAccordion>

              {workspaceRun || latestCleanupJob ? (
                <Box id="recovery-section">
                  <RecoverySection
                    title="Recovery & Workspace Management"
                    description="Destructive and recovery actions live here so the main workflow stays focused on forward progress."
                    actions={
                      workspaceRun
                        ? [
                            {
                              label: "Stop Bootstrap",
                              onClick: () => void stopBootstrap(),
                              disabled: !canStopBootstrap,
                              loading: stopBootstrapMutation.isLoading,
                              variant: "outlined",
                              color: "warning",
                            },
                            {
                              label:
                                workspaceRun.runStatus ===
                                TaxonomyRunStatus.FAILED
                                  ? "Cleanup Failed Workspace"
                                  : "Cleanup Workspace",
                              onClick: () => setIsCleanupDialogOpen(true),
                              disabled: !canCleanupWorkspace,
                              variant: "outlined",
                              color:
                                workspaceRun.runStatus ===
                                TaxonomyRunStatus.FAILED
                                  ? "error"
                                  : "warning",
                            },
                          ]
                        : []
                    }
                  >
                    <Stack spacing={1.25}>
                      {workspaceRun ? (
                        <Typography variant="body2" color="text.secondary">
                          Current workspace run{" "}
                          <strong>{workspaceRun.runId}</strong>
                          {" · "}
                          {workspaceRun.runStatus}
                        </Typography>
                      ) : null}
                      {latestCleanupJob ? (
                        <Alert
                          severity={
                            latestCleanupJob.jobStatus ===
                            TaxonomyApprovalJobStatus.FAILED
                              ? "error"
                              : latestCleanupJob.jobStatus ===
                                  TaxonomyApprovalJobStatus.COMPLETED
                                ? "success"
                                : "info"
                          }
                        >
                          Cleanup job <strong>{latestCleanupJob.jobId}</strong>
                          {" · "}
                          {latestCleanupJob.jobStatus}
                          {" · "}
                          {latestCleanupJob.jobStatus ===
                          TaxonomyApprovalJobStatus.COMPLETED
                            ? formatCleanupJobSummary(latestCleanupJob)
                            : latestCleanupJob.failureReason ??
                              `Targets ${latestCleanupJob.targetRunIds.length} run(s).`}
                        </Alert>
                      ) : null}
                      {workspaceFailureReason ? (
                        <Alert severity="error">{workspaceFailureReason}</Alert>
                      ) : (
                        <Alert severity="info">
                          Cleanup clears unpublished Dynamo workspace state
                          only. Published S3 snapshots remain available in
                          diagnostics.
                        </Alert>
                      )}
                    </Stack>
                  </RecoverySection>
                </Box>
              ) : null}
            </Stack>
          </Container>
        </Box>
      </Box>
      <Dialog
        open={Boolean(approvedTagRepairSource && approvedTagRepairDraft)}
        onClose={() => {
          if (!approveCanonicalProposalMutation.isLoading) {
            setApprovedTagRepairSource(null);
            setApprovedTagRepairDraft(null);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontFamily: displayFontFamily }}>
          Edit Approved Canonical Tag
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              label="Layer"
              size="small"
              value={approvedTagRepairDraft?.layer ?? "FUNCTIONAL"}
              onChange={(event) =>
                setApprovedTagRepairDraft((currentDraft) =>
                  currentDraft
                    ? {
                        ...currentDraft,
                        layer: event.target.value as ICanonicalTag["layer"],
                      }
                    : currentDraft,
                )
              }
            >
              {TagLayerValues.map((layer) => (
                <MenuItem key={layer} value={layer}>
                  {layer}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Slug"
              size="small"
              value={approvedTagRepairDraft?.slug ?? ""}
              onChange={(event) =>
                setApprovedTagRepairDraft((currentDraft) =>
                  currentDraft
                    ? {
                        ...currentDraft,
                        slug: event.target.value,
                      }
                    : currentDraft,
                )
              }
            />
            <TextField
              label="Name"
              size="small"
              value={approvedTagRepairDraft?.name ?? ""}
              onChange={(event) =>
                setApprovedTagRepairDraft((currentDraft) =>
                  currentDraft
                    ? {
                        ...currentDraft,
                        name: event.target.value,
                      }
                    : currentDraft,
                )
              }
            />
            <TextField
              label="Description"
              size="small"
              multiline
              minRows={3}
              value={approvedTagRepairDraft?.description ?? ""}
              onChange={(event) =>
                setApprovedTagRepairDraft((currentDraft) =>
                  currentDraft
                    ? {
                        ...currentDraft,
                        description: event.target.value,
                      }
                    : currentDraft,
                )
              }
            />
            <TextField
              label="Synonyms"
              size="small"
              value={approvedTagRepairDraft?.synonyms ?? ""}
              helperText="Comma-separated"
              onChange={(event) =>
                setApprovedTagRepairDraft((currentDraft) =>
                  currentDraft
                    ? {
                        ...currentDraft,
                        synonyms: event.target.value,
                      }
                    : currentDraft,
                )
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setApprovedTagRepairSource(null);
              setApprovedTagRepairDraft(null);
            }}
            disabled={approveCanonicalProposalMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void saveApprovedTagRepair()}
            disabled={!approvedTagRepairDraft}
            startIcon={
              approveCanonicalProposalMutation.isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Save Approved Tag
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(approvedTagHierarchyRepairSource)}
        onClose={() => {
          if (!updateCanonicalTagHierarchyMutation.isLoading) {
            setApprovedTagHierarchyRepairSource(null);
            setApprovedTagHierarchyRepairDraft([]);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontFamily: displayFontFamily }}>
          Edit Approved Parent Links
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Parent slugs must stay in the same layer and should be
              comma-separated. Empty means this tag will have no parents.
            </Typography>
            <TextField
              label="Parent slugs"
              size="small"
              value={approvedTagHierarchyRepairDraft.join(", ")}
              helperText="Comma-separated"
              onChange={(event) =>
                setApprovedTagHierarchyRepairDraft(
                  event.target.value
                    .split(",")
                    .map((slug) => slug.trim())
                    .filter(Boolean),
                )
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setApprovedTagHierarchyRepairSource(null);
              setApprovedTagHierarchyRepairDraft([]);
            }}
            disabled={updateCanonicalTagHierarchyMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void saveApprovedTagHierarchyRepair()}
            startIcon={
              updateCanonicalTagHierarchyMutation.isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Save Parent Links
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(
          synthesizedParentRepairSource && synthesizedParentRepairDraft,
        )}
        onClose={() => {
          if (!updateApprovedSynthesizedHierarchyParentMutation.isLoading) {
            setSynthesizedParentRepairSource(null);
            setSynthesizedParentRepairDraft(null);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontFamily: displayFontFamily }}>
          Edit Synthesized Parent
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Slug"
              size="small"
              value={synthesizedParentRepairDraft?.slug ?? ""}
              onChange={(event) =>
                setSynthesizedParentRepairDraft((currentDraft) =>
                  currentDraft
                    ? {
                        ...currentDraft,
                        slug: event.target.value,
                      }
                    : currentDraft,
                )
              }
            />
            <TextField
              label="Name"
              size="small"
              value={synthesizedParentRepairDraft?.name ?? ""}
              onChange={(event) =>
                setSynthesizedParentRepairDraft((currentDraft) =>
                  currentDraft
                    ? {
                        ...currentDraft,
                        name: event.target.value,
                      }
                    : currentDraft,
                )
              }
            />
            <TextField
              label="Description"
              size="small"
              multiline
              minRows={3}
              value={synthesizedParentRepairDraft?.description ?? ""}
              onChange={(event) =>
                setSynthesizedParentRepairDraft((currentDraft) =>
                  currentDraft
                    ? {
                        ...currentDraft,
                        description: event.target.value,
                      }
                    : currentDraft,
                )
              }
            />
            <TextField
              label="Synonyms"
              size="small"
              value={synthesizedParentRepairDraft?.synonyms ?? ""}
              helperText="Comma-separated"
              onChange={(event) =>
                setSynthesizedParentRepairDraft((currentDraft) =>
                  currentDraft
                    ? {
                        ...currentDraft,
                        synonyms: event.target.value,
                      }
                    : currentDraft,
                )
              }
            />
            <TextField
              label="Parent slugs"
              size="small"
              value={synthesizedParentRepairDraft?.parentSlugs.join(", ") ?? ""}
              helperText="Comma-separated"
              onChange={(event) =>
                setSynthesizedParentRepairDraft((currentDraft) =>
                  currentDraft
                    ? {
                        ...currentDraft,
                        parentSlugs: event.target.value
                          .split(",")
                          .map((slug) => slug.trim())
                          .filter(Boolean),
                      }
                    : currentDraft,
                )
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setSynthesizedParentRepairSource(null);
              setSynthesizedParentRepairDraft(null);
            }}
            disabled={
              updateApprovedSynthesizedHierarchyParentMutation.isLoading
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void saveSynthesizedParentRepair()}
            disabled={!synthesizedParentRepairDraft}
            startIcon={
              updateApprovedSynthesizedHierarchyParentMutation.isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Save Synthesized Parent
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isCleanupDialogOpen}
        onClose={() => {
          if (!cleanupWorkspaceMutation.isLoading) {
            setIsCleanupDialogOpen(false);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontFamily: displayFontFamily }}>
          {workspaceRun?.runStatus === TaxonomyRunStatus.FAILED
            ? "Cleanup Failed Workspace?"
            : "Cleanup Workspace?"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              This will delete the unpublished canonical bootstrap workspace
              data in Dynamo for the current run, including proposal, hierarchy,
              and warning state. Published S3 snapshots will remain available.
            </Typography>
            {workspaceRun ? (
              <Typography variant="body2" color="text.secondary">
                Current workspace run <strong>{workspaceRun.runId}</strong>
                {" · "}
                {workspaceRun.runStatus}
              </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              Cleanup runs asynchronously in the background after you confirm.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setIsCleanupDialogOpen(false)}
            disabled={cleanupWorkspaceMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            color={
              workspaceRun?.runStatus === TaxonomyRunStatus.FAILED
                ? "error"
                : "warning"
            }
            variant="contained"
            onClick={async () => {
              setCleanupCompletionMessage(undefined);
              const cleanupJob =
                await cleanupWorkspaceMutation.mutateAsync(undefined);
              setActiveCleanupJobId(cleanupJob.jobId);
              setIsCleanupDialogOpen(false);
            }}
            disabled={!canCleanupWorkspace}
            startIcon={
              cleanupWorkspaceMutation.isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Queue Workspace Cleanup
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CanonicalBootstrapAdminPage;
